import { createPortraitStage } from './viewport/portrait-stage.js';
import { PORTRAIT_ADJUSTMENTS } from './viewport/portrait-adjustments.js';
import { createPackageDoor } from './shell/package-door.js';
import './shell/body-panel.css';
import { createBodyPanel, type BodySection } from './shell/body-panel.js';
import { applyCommand, stripDigitsFromRig, StripDigitsRefusal } from '@harryhausen/kernel';
import { createElbowPlacementTool } from './viewport/elbow-placement-tool.js';
import { tailDetectArrays } from './viewport/tail-arrays.js';
/**
 * Animator v3 app entry — boot probes plus the seed Document: prove the
 * renderer backend and the layer seams loudly (ADR-002 §9, ADR-005 §9), then
 * run the checked-in Document through the ONE driver and present the result
 * (ADR-006 §6: the first-light demo is not a code path anymore; it is data —
 * the first row of the corpus manifest, executed by the same function the
 * agent and the corpus call headless).
 * Depends on: adapter-three (engine guard, toThreeClip), kernel (public API),
 * viewport, session driver, import pipeline.
 */
import {
  ArmSpaceRefusal,
  QUAT_IDENTITY,
  armSpaceOffsets,
  armSpacePlan,
  headSpacePlan,
  authoredClipData,
  bakeHingeTracks,
  quatConjugateBy,
  quatMultiply,
  hingeBendAngle,
  evaluateHingeCorrectives,
  bbsaOntologyData,
  chooseProfile,
  createHistory,
  current,
  detectRigFamily,
  hasPlayableKeys,
  loadOntology,
  parseDocument,
  parsePriorsBundle,
  playbackEquals,
  v2PriorsData,
  detectUnarmatured,
  detectTail,
  nearestSegmentDistances,
  meshHeight,
  wandStroke,
  faceForwardGlb,
  tailFanRefusal,
  TailFanRefusal,
  chainCentringYaw,
  nativeBustBones,
  SOFT_TISSUE_REGIONS,
  placeTail,
  fingerprintMismatch,
  fingerprintMesh,
  beginTargetLineage,
  addSourceToLineage,
  type MeshFingerprint,
  type Zone,
  parseRosterSheet,
  reconcileSheet,
  serializeRosterSheet,
  type RosterSheet,
  type AnimatorDocument,
  type WorldAxisRotation,
  type AuthoredClip,
  type SpecimenRef,
  tailMoodSchema,
  type JiggleRegionId,
} from '@harryhausen/kernel';
import {
  MultipleSkeletonsError,
  assertValidatedEngine,
  buildSkinnedGlb,
  exportAnimationOnly,
  exportGlb,
  loadFBX,
  fbxShape,
  fbxCharacterToGlb,
  fbxCharacterReportLine,
  convertedFbxCharacter,
  loadGLB,
  glbShape,
  minimalBipedNodes,
  clearSceneArmSpace,
  writeSceneArmSpace,
  toThreeClip,
  bindLibraryClip,
  loadLibraryClip,
  isHipBoneName,
  type HipScaleDecision,
  withWorldAxisPreRotation,
  subjectArrays,
  type SubjectArrays,
} from '@harryhausen/adapter-three';
import mixamoUrl from '../../packages/adapter-three/fixtures/specimens/mixamo-walking.fbx?url';
import androidUrl from '../assets/tripo-android.glb?url';
import boneAssetUrl from '../assets/octahedralBoneZup.glb?url';
import seedRaw from './session/seed-document.json?raw';
import { boot } from './boot.js';
import { importFbx, importGlb, type ImportDeps } from './import/import-pipeline.js';
import { commitHeadSeatingInPlace, decideHeadSeating, runDocument, tailShapeOptions, type DocumentIO, type RunOptions } from './session/run-document.js';
import { createPickWalker, type PickWalker, type WalkDirection } from './session/pick-walker.js';
import { DERIVED_BONES, mappedRigBones } from './session/bone-vocabulary.js';
import { createPoseProjector } from './session/pose-projection.js';
import { createStore } from './session/store.js';
import { initialShell, type ShellMode } from './session/shell-state.js';
import { mountUndoChrome } from './shell/undo-chrome.js';
import { mountExportChrome } from './shell/export-chrome.js';
import { DOCUMENT_SAVE, exportFilename, pickSaveTarget, writeSaveTarget } from './export/save-glb.js';
import { mountFileMenu } from './shell/file-menu.js';
import { createDocumentIo, isDocumentFile, looksLikeDocument, uploadName } from './session/document-io.js';
import { createFileHandles, fileFromHandle, grantHandle } from './session/file-handles.js';
import { describeButton } from './shell/tooltip.js';
import { createPanelRegistry } from './shell/panel-registry.js';
import { createTimelineStrip } from './shell/timeline-strip.js';
import { createHandsPanel } from './shell/hands-panel.js';
import { createCharacterPanel, type ArmSpaceUiState, type HeadSpaceUiState } from './shell/character-panel.js';
import { createZonesPanel, type ZonesUiState } from './shell/zones-panel.js';
import { createPackagePanel } from './shell/package-panel.js';
import { packagePanelView } from './shell/package-panel-state.js';
import { WORLD_BASE, bakeFolderFromFiles, libraryForRig, readLibrary, type BakeFolder, type LibraryForRig, type LibraryRead } from './package/library-read.js';
import { createProofStage } from './viewport/proof-stage.js';
import { readWorldUser } from './intake/world-user.js';
import { HANDOFF_CHANNEL, createWorldChannel } from './intake/world-channel.js';
import { avatarName } from './intake/intake-view.js';
import { fetchBytesStrict, resolveClipBytes } from './package/library-clip.js';
import { createLibraryPlayer } from './package/library-player.js';
import { HERO_SIZE, heroTakeFromPixels, heroTakeLine, heroThumbnailDataUri, nextTakeName } from './package/hero-png.js';
import { alphaForFacing, pickHeadBone, type HeadshotLens } from './package/headshot-frame.js';
import { fitHeadFrame, fitTailFrame } from './intake/first-frame.js';
import { lensPlacement } from './viewport/lens.js';
import { PACKAGE_SET, defaultChoices as defaultPackageChoices, withHeroTake, withHeroFavourite, withIdentity } from './shell/package-panel-state.js';
import { FAMILY_CANONS, FamilyCanonRefusal, PackageRefusal, canonicalizeForFamily, entityPathFor, tailUnriggedFinding, type RigCheckReport } from '@harryhausen/kernel';
import { WORLD_UPLOAD_PATH, createIntakeSession, type IntakeBuilt, type IntakeExportBody } from './intake/intake-session.js';
import { indexedDbBackend, mintWriterId } from './intake/handoff-store.js';
import { checkGlbForWorld } from './intake/check-glb.js';
import { createIntakeFrame } from './shell/intake-frame.js';
import './shell/intake-frame.css';
import { intakeView } from './intake/intake-view.js';
import { createIntakePanel } from './shell/intake-panel.js';
import { ontologyOfRig, packageBoneNames } from './package/family-names.js';
import { packageAvatar } from './package/package-avatar.js';
import { packageLine, pickPackageDestination, planPackageFolder, writePackage, type PackagePlan } from './package/package-folder.js';
import { batchLine, runBatch, type BatchDeps, type BatchReport } from './package/batch-package.js';
import { batchPickerAvailable, pickBatchSource, type BatchSource } from './package/batch-source-picker.js';
import { sha256Hex } from './package/package-avatar.js';
import { pickBakeFolder } from './package/bake-folder-picker.js';
import {
  createMaterialsPanel,
  type LinearRgb,
  type MaterialChannel,
  type MaterialColorChannel,
  type MaterialsUiState,
} from './shell/materials-panel.js';
import { applyMaterialFactors, materialFactorTargets } from './viewport/material-factors.js';
import { KHR_MATERIALS_VARIANTS, applyMoodVariants, deriveGlbTextures, readMoodVariants, type MoodSet } from '@harryhausen/texture-master';
import { createHandMarkers } from './viewport/hand-markers.js';
import { createWeightOverlay } from './viewport/weight-overlay.js';
import { createWireframeOverlay } from './viewport/wireframe-overlay.js';
import { createZoneTint } from './viewport/zone-tint.js';
import { createTailWag } from './viewport/tail-wag.js';
import { createTailSpring, nativeTailChain } from './viewport/tail-spring.js';
import { createTailSpringPanel } from './shell/tail-spring-panel.js';
import { createHairPanel } from './shell/hair-panel.js';
import { createHairController } from './hair/hair-controller.js';
import { createHairTool, HAIR_HUE, type HairToolProbe } from './viewport/hair-tool.js';
import { createTailRootHandle } from './viewport/tail-root-handle.js';
import { hairSubject, readHair, type HairSubject } from './viewport/hair-subject.js';
import { createSoftTissuePanel, SOFT_TISSUE_MASSES, type SoftTissueCardView, type SoftTissuePanelView } from './shell/soft-tissue-panel.js';
import { bodyProgress, type BodyProgress, type HandLiveReading } from './shell/body-progress.js';
import { dispositionOf, initialHandUi } from './shell/hands-panel-state.js';
import type { ElbowPinStatus } from './viewport/elbow-placement-tool.js';
import { createSoftTissueSprings, type SoftTissueSpringProbe } from './jiggle/soft-tissue-spring.js';
import { createSurfaceJiggle, type SurfaceJiggleProbe, type SurfaceJiggleTuning } from './jiggle/surface-jiggle.js';
import { measureSoftTissue } from './jiggle/soft-tissue-run.js';
import { springCensus, tailCensus, type SpringCensusMesh, type TailCensusMesh } from './viewport/tail-census.js';
import { createZoneFlyout, type ZoneFlyoutState } from './shell/zone-flyout.js';
import { SHELL_MODES } from './session/shell-state.js';
import { handCandidates, type HandCandidate } from './hands/hand-candidates.js';
import { measureHand } from './hands/hand-evidence.js';
import { consensusScan, type TipConsensus } from './hands/tip-consensus.js';
import { savedHandState } from './shell/hand-edit-state.js';
import { declareHand, forgetReplacedDigitEdits, withdrawHand } from './hands/declare-hand.js';
import { mountWorkspaceShell } from './shell/workspace-shell.js';
import {
  WorkspaceParseError,
  closePanel,
  defaultWorkspaces,
  activateTab,
  setZoneCollapsed,
  openPanel,
  parseWorkspaces,
  type ZoneName,
} from './shell/workspace.js';
import type { PanelId } from './shell/panel-ids.js';
import { applyCreaseWeights, type CreaseDrive } from './hinge/apply-crease-weights.js';
import { createBoneVisual, extractBoneGeometry, type BoneDrawState, type BoneVisual } from './viewport/bone-visual.js';
import { createViewport, type LensEvidence, type PresentInfo, type ViewportProbe } from './viewport/viewport.js';
import type { PoseToolConfig } from './viewport/pose-tool.js';
import { statusMark } from './shell/status-mark.js';

export interface ImportProbe {
  bones: number;
  clips: number;
  coverageComplete: boolean;
  synthesizedTips: number;
}

/** What the seed Document's run put on stage — the smoke test's evidence. */
export interface DocumentProbe {
  documentName: string;
  clipName: string;
  /** Every take the FBX source carried, classified — the selection evidence. */
  sourceTakes: ReadonlyArray<{ readonly name: string; readonly classification: string }>;
  /** Scene nodes grown for synthesized rig bones (ADR-010 §12) — loud record. */
  materializedBones: readonly string[];
  clipDurationSec: number;
  retargetedRotationTracks: number;
  droppedTracks: readonly string[];
  /** Tracks of the playable clip that resolved to scene nodes. */
  boundTracks: number;
  unresolvedTracks: readonly string[];
}

declare global {
  interface Window {
    __ANIMATOR_BOOT__?: {
      backend: 'webgpu' | 'webgl2';
      threeRevision: number;
      kernelAlive: boolean;
      importProbe: ImportProbe;
    };
    /** Set once the seed Document ran and its subject is on stage. */
    __ANIMATOR_DOCUMENT__?: DocumentProbe;
    /** Live per-frame counters — the smoke test's proof the loop is running. */
    __ANIMATOR_VIEWPORT__?: ViewportProbe;
    /**
     * Measure the honest lens (UX review §3.5) as it stands — a FUNCTION, not
     * a snapshot, because the evidence is read from the live renderer/scene.
     */
    __ANIMATOR_LENS__?: () => LensEvidence;
    /**
     * The shell store, exposed as the native register in miniature (ADR-006
     * §13.0): the same store the chrome drives, reachable programmatically. The
     * e2e proves the whole loop — dispatch a pose.set, watch it project, undo it
     * — headless, through exactly the surface a person clicks.
     */
    __ANIMATOR_SHELL__?: ShellProbe;
    /** The guided intake (INTAKE_GUIDED_FLOW): the proof stage's playback — the e2e's evidence that World's walk plays. */
    __ANIMATOR_INTAKE__?: { proof: () => import('./viewport/proof-stage.js').ProofStageProbe | null; tail: () => { available: boolean; refusal: string | null; on: boolean } };
    /** The live Document as JSON — the field's pins, wrist, knuckles, for a headless probe. */
    __ANIMATOR_DOCUMENT_JSON__?: () => string;
    /** ADR-039 T1: the Hair card's reading and the drawn skull; `adjustBy` is the drag's end without a pointer. */
    __ANIMATOR_HAIR__?: {
      card(): ReturnType<ReturnType<typeof createHairController>['probe']>;
      tool(): HairToolProbe;
      adjustBy(du: number, dv: number, dw: number, scale?: number): void;
    };
  }
}

/** A read snapshot of the store — what the e2e asserts against. */
export interface ShellSnapshot {
  mode: string;
  canUndo: boolean;
  canRedo: boolean;
  isDirty: boolean;
  lastAction: string | null;
  posedBones: readonly string[];
  selection: readonly string[];
  /** The authored clip on stage, by name — null = the default presentation. */
  activeClip: string | null;
  /** A library clip previewed on the subject, by World's clipId — null = none (ADR-033 phase 4). */
  libraryClip: string | null;
  /** A rig bone guaranteed present in the scene — the e2e poses THIS one. */
  sampleBone: string;
  /** ADR-031 T2b-min: the session-only preview wag — on, and what it last applied. */
  tailWag: { on: boolean; frames: number; lastRootDeg: number; bones: number };
  /** ADR-038: the tail spring — on, where its chain came from, frames applied and solver steps run. */
  tailSpring: import('./viewport/tail-spring.js').TailSpringProbe;
  /** ADR-038 J2: the soft-tissue springs, one per leaf — on, where it came from, solver steps run. */
  softTissue: readonly SoftTissueSpringProbe[];
  /** ADR-038 §14 J4: per grown mass, its surface modes and their live reading. */
  surfaceJiggle: readonly SurfaceJiggleProbe[];
}

/** The store, driveable from the e2e: read the snapshot, dispatch, undo, redo. */
export interface ShellProbe {
  snapshot(): ShellSnapshot;
  dispatch(command: unknown): void;
  undo(): void;
  redo(): void;
  setMode(mode: string): void;
  /** ADR-038: switch the tail spring on / off (the Body card's checkbox); false when there is no chain. */
  setTailSpring(on: boolean): boolean;
  /** ADR-038 R5: commit a tail mood (and optionally muscle) through the Document; false when there is no spring. */
  setTailMood(mood: string, muscle?: number): boolean;
  /** ADR-038 J2: the grafted soft-tissue leaves — region, rig bone, root and apex (bind-world). */
  softTissueLeaves(): readonly { region: string; bone: string; head: { x: number; y: number; z: number }; tail: { x: number; y: number; z: number } }[];
  /** J4 prototype dials (ratio, life, cap, gain) — the field read's knobs until the Jiggle control lands. */
  tuneSurfaceJiggle(tuning: Partial<SurfaceJiggleTuning>): SurfaceJiggleTuning;
  /** Put the camera at `eye` looking at `target` (screenshots). */
  viewFrom(eye: { x: number; y: number; z: number }, target: { x: number; y: number; z: number }): void;
  /** Stage an authored clip by name (null = default) — the §13.4 selection. */
  selectClip(name: string | null): void;
  /**
   * Preview a LIBRARY clip on the subject from its bytes (ADR-033 phase 4):
   * loads the animation-only GLB, binds its tracks by the family's names
   * (`_graft` fallback), plays it looping, and reports the hit rate. Null
   * stops the preview and returns the stage to the staged clip.
   */
  previewLibraryClip(clipId: string | null, bytes?: ArrayBuffer): Promise<{ bound: number; total: number; unresolved: readonly string[] } | null>;
  /** Take a hero (ADR-033 phase 6) and return its name; the bytes are readable through `heroTake`. */
  takeHero(): Promise<string | null>;
  /** Take a HEADSHOT (ADR-035 phase 1): World's frame from the declared height and the head bone; the live camera is not moved. */
  takeHeadshot(): Promise<string | null>;
  heroTake(name: string): Uint8Array | null;
  /** The package as it would be written (ADR-033 phase 7), without a picker: the files' paths, roles and sizes, the record. */
  packageToPlan(): Promise<{ id: string; files: { path: string; role: string; size: number }[]; missingTakes: readonly string[]; record: unknown; bodySha256: string }>;
  /** ADR-043 M0 probe: the moods the export door's body declares (the Package door's producer, no clips), or null. */
  exportMoods(): Promise<{ variants: string[]; bases: { material: string; base: string[]; moods: { variants: string[]; mime: string; bytes: number }[] }[] } | null>;
  /** The batch runner (ADR-035 §3.3) over given bodies and a sheet, with an in-memory writer — the smoke's door. */
  runBatch(bodies: { name: string; bytes: number[] }[], sheet: unknown): Promise<{ report: BatchReport; sheet: unknown; packages: { id: string; files: { path: string; role: string; size: number }[]; hero: number[] }[] }>;
  /**
   * The live Document. The strip renders a projection of this; a test that
   * measures the DOM alone cannot tell "drew nothing" from "there was nothing
   * to draw", so the authored truth is readable too.
   */
  document(): AnimatorDocument;
  /** A scene bone's LIVE local rotation — the e2e's the-mixer-moved-it proof. */
  boneRotation(bone: string): { x: number; y: number; z: number; w: number } | null;
  /** ADR-015 evidence: which panels the engine has MOUNTED right now. */
  mountedPanels(): readonly string[];
  /** Drive the workspace through the same transitions a gesture would. */
  closePanel(id: string): void;
  openPanel(id: string, zone: string): void;
  /** The pose tool's live evidence, or null while withdrawn (playback). */
  poseTool(): { handleCount: number; segmentCount: number; selected: string | null; dragging: boolean } | null;
  pinTool(): { pinned: number; missed: number; markers: number } | null;
  /** Screen position of a bone's handle — lets the e2e aim a REAL mouse click. */
  handleXY(bone: string): { x: number; y: number } | null;
  /** Each drawn skeleton mesh's draw state (queue, depth, order), or null while withdrawn. */
  boneDrawStates(): Record<string, BoneDrawState> | null;
  /** ADR-031 T2: what the tail bones weight and actually move, per skinned mesh, against the tail zone. */
  tailCensus(): readonly TailCensusMesh[];
  /** ADR-038: every mesh on the WHOLE stage measured against the spring's own bone objects (null = no running spring). */
  springCensus(): readonly SpringCensusMesh[] | null;
  handMarkers(): {
    dots: number;
    ghosts: number;
    litPoints: number;
    handExtent: number;
    pinFromHandRatio: number | null;
  };
  /** ADR-027 stage 1 — what the weight overlay is showing right now. */
  weightOverlay(): {
    mode: 'off' | 'bone' | 'shared';
    bone: string | null;
    opacity: number;
    ghosts: number;
    influenced: number;
  };
  /** ADR-031 T0 — is the wireframe up, how faint, over how many meshes. */
  wireframe(): { on: boolean; opacity: number; ghosts: number };
  /** ADR-031 T1 — what the Document declares, what is tinted, which flyout is open. */
  zones(): { declared: number; tinted: number; paintedFaces: number; open: string | null };
  /** Where a pin or ghost sits on SCREEN, so a test can aim a real click at it. */
  pinXY(handBone: string, id: string): { x: number; y: number } | null;
}

/**
 * The app's specimen resolver: `specimen:` uris map to bundled fixture URLs.
 * A Document naming an unregistered specimen fails here, loudly (§8) — user
 * uploads join this registry when the import UI lands.
 */
// The seed subject is the golden android (Allen, 2026-07-25): current-gen
// Tripo — 41 joints with the 18 mesh-deform supplements (the "41-bone
// update"), committed TEXTURED so the viewport judges real materials. The
// old goblin fixture stays for the 12 test suites pinned to it, but it is
// pre-supplement (no Twist chains) and noodles at bent elbows — a rig-era
// fact, not a pipeline bug (PRODUCT_TARGET §mechanisms).
const SPECIMEN_URLS: Readonly<Record<string, string>> = {
  'specimen:tripo-android.glb': androidUrl,
  'specimen:mixamo-walking.fbx': mixamoUrl,
};

/**
 * Bytes a person dropped on the window, under `upload:<filename>` uris. The
 * registry SPECIMEN_URLS' comment predicted this ("user uploads join this
 * registry when the import UI lands"); they join beside it rather than in it,
 * because a bundled asset is fetched by URL and an upload is already in memory.
 * A Document that names an upload is only resolvable in the session that
 * received it — which is honest: nothing here persists a file yet.
 */
const uploads = new Map<string, ArrayBuffer>();

/**
 * What a held buffer REPLACED, so a load that does not commit can put it
 * back (Codex P2, #140 round 7): a file dropped under the same name as the
 * live subject's overwrites the subject's bytes, and deleting on refusal
 * would strand the subject on stage with nothing behind it. Keyed by the
 * buffer itself (identity), so only the load that held it can release it.
 */
const priorOf = new WeakMap<ArrayBuffer, ArrayBuffer | undefined>();
/** ONE door into the map for bytes a load holds — the drop door and the Document door alike. */
const holdUpload = (uri: string, bytes: ArrayBuffer): void => {
  if (uploads.get(uri) === bytes) return; // already held — never its own predecessor
  priorOf.set(bytes, uploads.get(uri));
  uploads.set(uri, bytes);
};
/**
 * Release bytes a load held, restoring whatever they replaced — if they are
 * STILL the ones held. If a newer load has replaced them meanwhile, they are
 * UNLINKED from that load's predecessor chain instead (Codex P1, #140 round
 * 9): three drops of one name A → B → C, B superseded then C refused, must
 * leave A behind, not the stale B.
 */
const releaseUpload = (uri: string, bytes: ArrayBuffer): void => {
  const current = uploads.get(uri);
  if (current !== bytes) {
    for (let cur = current; cur !== undefined; cur = priorOf.get(cur)) {
      if (priorOf.get(cur) === bytes) {
        priorOf.set(cur, priorOf.get(bytes));
        break;
      }
    }
    return;
  }
  const prior = priorOf.get(bytes);
  if (prior !== undefined) uploads.set(uri, prior);
  else uploads.delete(uri);
};

const appIO: DocumentIO = {
  async loadSpecimen(ref: SpecimenRef): Promise<ArrayBuffer> {
    const uploaded = uploads.get(ref.uri);
    if (uploaded !== undefined) return uploaded;
    const url = SPECIMEN_URLS[ref.uri];
    if (url === undefined) {
      throw new Error(`no specimen registered for uri "${ref.uri}" — the Document names bytes this app cannot resolve`);
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error(`specimen fetch failed: ${ref.uri} → HTTP ${res.status}`);
    return res.arrayBuffer();
  },
};

/**
 * Import-seam probe (ADR-008 §9): an engineered GLB — built in memory, no
 * payload — travels the WHOLE pipeline in the real browser: loader →
 * conversion → auto-mapping → tip synthesis (two toe tips deliberately
 * missing) → validation. Proves the seam runs where the app runs.
 */
async function probeImportSeam(deps: ImportDeps): Promise<ImportProbe> {
  // Parent references are positional — removing nodes requires a remap.
  const all = minimalBipedNodes((id) => `probe:${id}`);
  const kept = all.filter((n) => !n.name.includes('Toe_End'));
  const nameAt = new Map(all.map((n, i) => [i, n.name]));
  const newIndex = new Map(kept.map((n, i) => [n.name, i]));
  const nodes = kept.map((n) => ({
    ...n,
    parent: n.parent === null ? null : (newIndex.get(nameAt.get(n.parent) as string) as number),
  }));
  const glb = buildSkinnedGlb({
    generator: 'boot-probe',
    nodes,
    animations: [
      {
        name: 'probe-sway',
        channels: [
          { node: 1, path: 'rotation', times: [0, 0.5, 1], values: [0, 0, 0, 1, 0.2588, 0, 0, 0.9659, 0, 0, 0, 1] },
        ],
      },
    ],
  });
  const result = await importGlb(glb, deps, { label: 'boot-probe' });
  return {
    bones: result.rig.bones.length,
    clips: result.clips.length,
    coverageComplete: result.missingAfterSynthesis.length === 0,
    synthesizedTips: result.synthesizedTips.length,
  };
}

const statusEl = document.getElementById('boot-status');

// The status line lives over the bottom bar and is HIDDEN until the ` hotkey
// toggles it (Allen, 2026-07-28: the pill sat over the subject; the info is
// for occasional confirmation, not ambient reading). One exception, stated
// loudly: an ERROR forces it visible — this line is the app's fail-loud
// surface (refusals, boot failures), and a hidden refusal is a silent one.
const revealStatus = (): void => statusEl?.classList.remove('status-hidden');
window.addEventListener('keydown', (event) => {
  if (event.key !== '`' || event.altKey || event.ctrlKey || event.metaKey) return;
  statusEl?.classList.toggle('status-hidden');
});

function reportFatal(err: unknown): void {
  // Fail-loud contract: boot failures are displayed, not swallowed (ADR-001 §7).
  const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  if (statusEl) {
    revealStatus();
    statusEl.textContent = `BOOT FAILED — ${message}`;
    statusEl.style.color = '#ff8888';
  }
  console.error('[animator-v3] boot failed:', err);
}

/**
 * A launch FROM WORLD (`?handoff=1`): the boot seed must never be seen (Allen, 2026-10-03: a flash of the
 * default avatar, her hair editor on, "looks like a mistake"). From the first line, the stage is hidden and
 * the workspace is out of sight — the guided frame takes over from here, and clears this when it shows their
 * model, or steps aside.
 */
const HANDOFF_LAUNCH = new URLSearchParams(location.search).get('handoff') === '1';
if (HANDOFF_LAUNCH) {
  document.body.dataset['intake'] = 'guided';
  document.body.dataset['intakeStage'] = 'absent';
}

async function main(): Promise<void> {
  const threeRevision = assertValidatedEngine();

  // Cross-seam probe: the kernel's public API is alive in the app context.
  const remapped = quatConjugateBy(QUAT_IDENTITY, QUAT_IDENTITY);
  const kernelAlive = remapped.w === 1;

  const canvas = document.getElementById('renderCanvas');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('renderCanvas missing — index.html contract broken');
  }

  const { backend, renderer } = await boot(canvas);
  const deps: ImportDeps = {
    ontology: loadOntology(bbsaOntologyData()),
    priors: parsePriorsBundle(v2PriorsData()),
  };
  // ADR-038 §14 (J4): the surface jiggle is ON (field-passed on Rita and Pavel, 2026-09-28).
  // `?j4=off` is the explicit, logged kill switch (a deliberate off must never look like broken).
  const runOptions: RunOptions = { surfaceJiggle: new URLSearchParams(location.search).get('j4') !== 'off' };
  if (runOptions.surfaceJiggle !== true) console.info('[animator-v3] J4 surface jiggle OFF (?j4=off)');
  // The field read's dials (multipliers/overrides on the Jiggle control): ?gain=1.5&cap=0.12&ratio=2&life=1.5
  const j4Dials = (): Partial<SurfaceJiggleTuning> => {
    const q = new URLSearchParams(location.search);
    const out: { -readonly [K in keyof SurfaceJiggleTuning]?: number } = {};
    for (const key of ['gain', 'cap', 'ratio', 'life'] as const) {
      const raw = q.get(key);
      const value = raw === null ? Number.NaN : Number(raw);
      if (Number.isFinite(value) && value > 0) out[key] = value;
    }
    return out;
  };
  const importProbe = await probeImportSeam(deps);

  window.__ANIMATOR_BOOT__ = { backend, threeRevision, kernelAlive, importProbe };
  const bootLine =
    `three r${threeRevision} · ${backend} · kernel ${kernelAlive ? 'alive' : 'DEAD'} · ` +
    `import ${importProbe.coverageComplete ? 'ok' : 'INCOMPLETE'} ` +
    `(${importProbe.bones} bones, ${importProbe.clips} clip, ${importProbe.synthesizedTips} tips synthesized)`;
  if (statusEl) statusEl.textContent = bootLine;
  console.info(`[animator-v3] boot ok — ${bootLine}`);
  // What the status line says WITHOUT the workspace notice. The notice can
  // arrive at any time (a storage write failing mid-session), so the line is
  // recomposed rather than concatenated once — see renderStatus below.
  let statusBase = bootLine;

  // The seed Document: parsed at boot (the gate is the entry), run by the one
  // driver, presented. The viewport is pointed AT the result — the run itself
  // is renderer-free (ADR-006 §2.2).
  const seed = parseDocument(JSON.parse(seedRaw));
  const viewport = createViewport(renderer, canvas);
  window.__ANIMATOR_VIEWPORT__ = viewport.probe;
  window.__ANIMATOR_LENS__ = () => viewport.lensEvidence();
  const run = await runDocument(seed, appIO, deps, runOptions);

  const [clipRun] = run.clips;
  if (!clipRun) throw new Error(`seed Document "${seed.name}" selects no clip — nothing to present`);
  const playable = toThreeClip(clipRun.result.clip);

  // THE LAW OF MIXER BIRTH (Codex P2, #61 r5 + r6): three records each bound
  // property's ORIGINAL state the moment an action plays, and stopAllAction
  // RESTORES that recording. So whatever the bones read when a mixer is born
  // becomes, permanently, that mixer's idea of rest — and a pose captured
  // there is stamped back by a LATER stop, onto a bone the user may have
  // cleared long before. There is no correcting it after the fact: the
  // projector no longer tracks that bone, so nothing believes it is posed.
  //
  // The cure is a single door. EVERY present goes through here, boot included
  // — clear the projection so the scene holds imported rests, then present.
  // The pose layer re-covers immediately: at boot via the first reproject
  // below, and on every change via the reproject subscriber, which is
  // registered AFTER the resolver deliberately so it lands on the same notify.
  // A bare viewport.present() outside this door is the bug re-entering.
  //
  // A SUBJECT SWAP (dropping a new model) is this same door, entered a second
  // time. Everything below that describes the subject — the run, its pose
  // targets, its projector — moves together as one value, because a projector
  // built over the previous scene pointed at the new one is the same disease
  // wearing an import for a costume. Hence `subject`: rebound as a unit, and
  // read through, never captured, by presentOnRests and the reproject
  // subscriber.
  /** One hinge's live-drive wiring (ADR-028 E1) — nodes cached at mount, read through `subject`. */
  interface LeafDrive {
    /** Evaluator output name → this leaf. */
    readonly name: string;
    /** Bind-local rest position — the evaluator's delta ADDS to this. */
    readonly bind: { x: number; y: number; z: number };
    /** Character scale: the segment length the normalized delta multiplies by (§6's amplitude law). */
    readonly scale: number;
    readonly node: { position: { set(x: number, y: number, z: number): unknown } };
  }
  /**
   * ADR-028 E3's live door. A crease is a scalar on a target the MESH carries,
   * so unlike a leaf there is no node to move — only an influence to set. The
   * same target may live on several meshes at once (a garment skinned with
   * copied body weights carries the body's crease), so one declared morph
   * drives a LIST.
   *
   * Structural types, never a three import: main stays on the engine-free side
   * of the layer gate (ADR-002 §6), the same way LeafDrive names only the
   * `position.set` it calls.
   */
  type MorphDrive = CreaseDrive;
  interface HingeDrive {
    readonly profile: import('@harryhausen/kernel').HingeProfile;
    readonly driverBindLocal: { x: number; y: number; z: number; w: number };
    readonly helperBindLocal: { x: number; y: number; z: number; w: number };
    readonly driverNode: { quaternion: { x: number; y: number; z: number; w: number } };
    readonly helperNode: { quaternion: { set(x: number, y: number, z: number, w: number): unknown } };
    readonly leafDrives: readonly LeafDrive[];
    /** Index-aligned with `profile.morphs`, which is the order the evaluator returns. */
    readonly morphDrives: readonly MorphDrive[];
  }
  interface Subject {
    readonly run: Awaited<ReturnType<typeof runDocument>>;
    readonly poseTargets: ReadonlySet<string>;
    readonly projector: ReturnType<typeof createPoseProjector>;
    readonly hingeDrives: readonly HingeDrive[];
  }
  const subjectChanged = new Set<() => void>();
  const mountSubject = (r: Awaited<ReturnType<typeof runDocument>>): Subject => {
    // `_corr` helpers are DERIVED bones — the hinge driver rewrites them from
    // the driver's rotation every frame, so a hand-authored pose on one would
    // be silently overwritten: the exact contradiction the pose gate exists
    // to refuse atomically (§8). The rule comes from bone-vocabulary rather
    // than a second inline copy of the pattern, which is how the drawn and
    // the posable vocabularies would drift apart.
    //
    // Note this gate is DELIBERATELY wider than the DRAWN vocabulary: hidden
    // plumbing stays posable through the agent register, so only the derived
    // correctives are excluded here.
    const targets = new Set(r.rig.bones.map((bone) => bone.name).filter((n) => !DERIVED_BONES.test(n)));
    const proj = createPoseProjector(r.scene, r.poseRests);
    const drives: HingeDrive[] = r.hinges.map((h) => {
      const driverBone = r.rig.bones.find((b) => b.name === h.profile.driver);
      const helperBone = r.rig.bones.find((b) => b.name === h.helper);
      const driverNode = r.scene.getObjectByName(h.profile.driver);
      const helperNode = r.scene.getObjectByName(h.helper);
      if (!driverBone || !helperBone || !driverNode || !helperNode) {
        throw new Error(
          `hinge "${h.helper}": driver/helper missing from ${!driverBone || !helperBone ? 'rig' : 'scene'} — ` +
            `the graft and the mount disagree (§8, fail loud)`,
        );
      }
      const leafDrives: LeafDrive[] = h.leaves.map((leaf) => {
        const node = r.scene.getObjectByName(leaf.name);
        if (!node) {
          throw new Error(`leaf "${leaf.name}": missing from scene — the graft and the mount disagree (§8, fail loud)`);
        }
        return { name: leaf.name, bind: leaf.bindLocalPosition, scale: leaf.scaleWorld, node };
      });
      // A declared crease with no target drives NOTHING, and that is correct
      // rather than fatal. A crease is declared for a FAMILY; whether a given
      // character has flesh in that band is a property of the character, and
      // the Document run already said so loudly at derivation time
      // (crease-morph-empty, which names the hinge and the band count).
      //
      // This was a throw, and it took the app down: measured on the committed
      // goblin, whose LEFT elbow band holds zero vertices while the right
      // holds twenty. Refusing at mount turned "this character has no left
      // elbow crease" into "this character cannot be opened", and the loud
      // failure landed where nothing knows WHY — the derivation does.
      //
      // The entry is still created, so the drives stay index-aligned with
      // profile.morphs and the alignment check keeps its meaning.
      const morphDrives: MorphDrive[] = h.profile.morphs.map((m) => {
        const targets: { influences: number[]; index: number }[] = [];
        r.scene.traverse((node) => {
          const mesh = node as { morphTargetInfluences?: number[]; morphTargetDictionary?: Record<string, number> };
          const index = mesh.morphTargetDictionary?.[m.name];
          if (index === undefined || !Array.isArray(mesh.morphTargetInfluences)) return;
          targets.push({ influences: mesh.morphTargetInfluences, index });
        });
        return { name: m.name, targets };
      });
      return {
        profile: h.profile,
        driverBindLocal: driverBone.localBind.rotation,
        helperBindLocal: helperBone.localBind.rotation,
        driverNode,
        helperNode,
        leafDrives,
        morphDrives,
      };
    });
    // Capture EVERY pose target's rest FIRST, in one synchronous block with the
    // birth below — the mixer samples on the next RAF tick, which cannot fire
    // mid-block, so these are true rests. Without this, a pose.set dispatched
    // during playback would lazily snapshot a mid-animation frame as "rest"
    // (Codex P2, #54 round 3). Loaded-pose bones keep their seeded imported rest.
    proj.captureRests([...targets]);
    // Panels that read the SUBJECT (not the store) re-render once it is on stage:
    // a remount notifies nothing in the store (a grown tail's spring card never
    // mounted — measured on :5192, 2026-09-23).
    queueMicrotask(() => subjectChanged.forEach((listener) => listener()));
    return { run: r, poseTargets: targets, projector: proj, hingeDrives: drives };
  };
  let subject = mountSubject(run);
  const poseTargets = subject.poseTargets;
  // ---- ADR-028 E1's live door: the helper follows the driver, every frame,
  //      through EVERY presentation path — clip playback (the mixer writes the
  //      driver inside the render loop), pose projection, scrubbing. Reads
  //      through `subject`, never captures it (the #61 lesson). Registered as
  //      the viewport's frame driver below; called synchronously wherever the
  //      scene's TRS is parsed outside the loop (the export door).
  const driveHinges = (): void => {
    for (const d of subject.hingeDrives) {
      const q = d.driverNode.quaternion;
      const reading = hingeBendAngle(d.profile, d.driverBindLocal, { x: q.x, y: q.y, z: q.z, w: q.w });
      const out = evaluateHingeCorrectives(d.profile, reading.thetaDeg);
      for (const pose of out.helpers) {
        if (pose.rotation !== undefined) {
          const local = quatMultiply(d.helperBindLocal, pose.rotation);
          d.helperNode.quaternion.set(local.x, local.y, local.z, local.w);
        }
        if (pose.translation !== undefined) {
          // The evaluator's delta is NORMALIZED (§6): scale by the character's
          // measured segment length, add to the leaf's bind-local rest.
          const leaf = d.leafDrives.find((l) => l.name === pose.name);
          if (leaf === undefined) continue;
          leaf.node.position.set(
            leaf.bind.x + pose.translation.x * leaf.scale,
            leaf.bind.y + pose.translation.y * leaf.scale,
            leaf.bind.z + pose.translation.z * leaf.scale,
          );
        }
      }
      // E3: the crease. Applied through the shared unit so the door and its
      // tests run the SAME code — E1/E2's application lives in this closure
      // and no test exercises it.
      applyCreaseWeights(d.morphDrives, out.morphWeights);
    }
  };
  viewport.setFrameDriver(driveHinges);
  // The clip type comes from the adapter, not a three import — main stays on
  // the engine-free side of the layer gate (ADR-002 §6).
  const presentOnRests = (clip: ReturnType<typeof toThreeClip> | null): PresentInfo => {
    subject.projector.project([]);
    return viewport.present(subject.run.scene, clip);
  };
  const presented = presentOnRests(playable);

  const mixamoSource = run.sources.get('mixamo-walking');
  const sourceTakes =
    mixamoSource !== undefined && 'takes' in mixamoSource
      ? mixamoSource.takes.map(({ name, classification }) => ({ name, classification }))
      : [];

  window.__ANIMATOR_DOCUMENT__ = {
    documentName: seed.name,
    clipName: playable.name,
    sourceTakes,
    materializedBones: run.materializedBones,
    clipDurationSec: playable.duration,
    retargetedRotationTracks: clipRun.result.report.retargetedRotationTracks,
    droppedTracks: clipRun.result.report.droppedTracks,
    boundTracks: presented.boundTracks,
    unresolvedTracks: presented.unresolvedTracks,
  };

  // The shell store (ADR-007 §4): the one stateful seam holds the History this
  // loader begins (createHistory — the load door) and the shell's app state.
  // The chrome resolves from it; a listener re-projects the pose layer onto the
  // live scene on every change, so undo/redo/dispatch move the bones the cursor
  // now points at — without re-importing (§5, §11). The projector is the ONLY
  // place the scene is written from the Document after the initial run.
  // poseTargets makes a pose on an absent bone an ATOMIC refusal (§8) instead
  // of a committed edit that trips the projector after the fact; poseRests
  // seeds the projector with the imported rest of any pose loaded WITH the
  // Document, so clearing it restores rest, not the loaded pose (Codex, #54).
  // Both poseTargets and the projector are built ABOVE, with the presentation
  // door — the projector must exist BEFORE the first mixer is born, or that
  // birth cannot be laundered (#61 r6).

  // The workspace system (ADR-015): the registry is the closed vocabulary;
  // panels register HERE with injected mounts (the registry file imports no
  // panel). The timeline strip is the first registered consumer (§5).
  // ─── WHO IS HOLDING THE MIXER STILL (Codex P1, #94) ───────────────────────
  // Three doors want the mixer stopped and they were each calling
  // `setPaused` with their own idea of when to start it again. The bug that
  // named this: arming the pin gesture in POSE MODE paused nothing (already
  // paused), and disarming resumed playback anyway — while the pose projector
  // still owned the bones. Worse than a flicker: three re-captures every
  // binding's "original state" when an action goes stopped→playing, so a
  // resume there records the PROJECTED pose as the action's rest and a later
  // stop stamps it back on (the #61 r7 mixer-birth disease, through a new
  // door).
  //
  // So the reasons are named and OR-ed, and there is one place that decides.
  // A reason that clears does not start playback — it only stops claiming.
  let pinHold = false;
  let elbowHold = false;
  // An open ZONE bar holds too (Allen's field read, 2026-09-22: "the flags move
  // if the character moves"): the bar follows the flesh, and walking flesh
  // carries the question out from under the pointer.
  let zoneHold = false;
  // ADR-039: a skull drag holds too (the same reason: the head carries the handle out from under the pointer).
  let hairHold = false;
  let elbowProjection: Promise<void> = Promise.resolve();
  let elbowProjectionError: Error | null = null;
  const elbowButtons = new Set<HTMLButtonElement>();
  const elbowStatuses = new Set<HTMLElement>();
  // The Character panel's rule (its header): explanations live in the TOOLTIP
  // tiers, never as an ambient paragraph. So the standing how-to rides the `?`
  // beside the button and the live region starts EMPTY. Since the Body-tab
  // simplification (XD 2026-09-29 §3B) it speaks ONE thing only — a rebuild that
  // FAILED, short, because a refusal must reach the eye; everything else it used
  // to say (saved, rebuilding, the reconstructed-weight count, a placement
  // refusal's detail) goes to the status line through the Body voice.
  const ELBOW_HOWTO =
    'Move a dot or ring on the character, then choose +Pin to commit that elbow. ' +
    'Reset restores the position the file was imported with.';
  let elbowStatus = '';
  /** The card's one line: '' (nothing) or the short failure. */
  const setElbowStatus = (message: string) => {
    elbowStatus = message;
    for (const label of elbowStatuses) {
      label.textContent = message;
      label.hidden = message === '';
    }
  };
  const awaitElbowProjection = async () => {
    let pending: Promise<void>;
    do { pending = elbowProjection; await pending; } while (pending !== elbowProjection);
    if (elbowProjectionError) throw elbowProjectionError;
  };
  let exportHold = false;
  /** Pose mode owns the bones (ADR-007 §5.3); pinning and export hold too. */
  // `guidedHold`: the guided frame shows their avatar AT REST (design §3.3) — an embedded clip may be the broken thing.
  const mixerPaused = (): boolean => store.getShell().mode === 'pose' || pinHold || elbowHold || zoneHold || hairHold || exportHold || guidedHold;
  const applyMixerPause = (): void => viewport.setPaused(mixerPaused());

  // Branch markers ride the hand BONE, so they follow the clip the subject is
  // wearing rather than hanging where its rest pose used to be.
  const handMarkers = createHandMarkers(() => subject.run.scene);

  // ADR-027 stage 1 — who owns the flesh, seen (Allen, 2026-08-20: "the
  // weights assigned to the bones are simply not at all correct" — a claim
  // nobody could CHECK by looking until now). Ghost SkinnedMeshes on the
  // subject's own skeleton, so the heat rides the playing clip for free.
  const weightOverlay = createWeightOverlay(() => subject.run.scene);
  // ADR-031 T0: the mesh's structure, seen — the first instrument of the zones arc.
  const wireframeOverlay = createWireframeOverlay(() => subject.run.scene);

  const editingHands = new WeakMap<object, Set<string>>();
  /** Hands a person chose to REPLACE (2026-09-29): their native digits go before the graft. */
  const replacingHands = new WeakMap<object, Set<string>>();
  const editableCandidates = () => handCandidates(subject.run.rig, deps.ontology).map(c => editingHands.get(subject.run)?.has(c.handBone) ? { ...c, existingChildren: [] } : c);
  /**
   * A hand whose rig carries mapped finger chains can be REPLACED (2026-09-29). Before this, a
   * body's own fingers — a reopened export's graft, a Tripo preset's — left the hands panel
   * unmounted: Allen's "hand joints aren't being offered if they've been resolved".
   */
  const replaceable = (handBone: string): boolean => {
    try {
      return stripDigitsFromRig(subject.run.rig, handBone).chains.length > 0;
    } catch {
      return false;
    }
  };
  const handsOffered = () =>
    handCandidates(subject.run.rig, deps.ontology).some(
      (c) => c.existingChildren.length === 0 || savedHandState(store.getDocument(), c.handBone) !== null || replaceable(c.handBone),
    );
  // THE BODY HEADER'S READINGS (XD note 2026-09-27 §3.1, PR 2): each read from
  // what a panel already reads — nothing new in the Document. The hands panel
  // reports its live pinning per run (it lives in the panel, not the store), and
  // the elbow tool its pins; either change redraws the headers only.
  const handLive = new WeakMap<object, Map<string, HandLiveReading | null>>();
  const bodyProgressListeners = new Set<() => void>();
  let elbowPinStatus: (() => readonly ElbowPinStatus[]) | null = null;
  /** ONE RECEIPT (XD PR 4): a card's status mark, from the same progress the Body headers draw. */
  const bodyMark = (subject: string): HTMLElement | null => {
    const p = readBodyProgress();
    for (const id of ['joints', 'parts', 'hair', 'soft'] as const) {
      const o = p[id].objects.find((x) => x.subject === subject);
      if (o === undefined) continue;
      const m = statusMark(o.state, { subject: o.subject, ...(o.hue === undefined ? {} : { hue: o.hue }) });
      if (o.faint === true) m.dataset['faint'] = 'true';
      return m;
    }
    return null;
  };
  const readBodyProgress = (): BodyProgress => {
    const run = subject.run;
    const doc = store.getDocument();
    const live = handLive.get(run);
    const zones = zonesUiState();
    const spring = tailSpring.view(run);
    const soft = softTissueView();
    return bodyProgress({
      handsOffered: handsOffered(),
      hands: handCandidates(run.rig, deps.ontology).map(c => ({
        side: c.side,
        graftable: dispositionOf(c).kind === 'graftable',
        savedPins: savedHandState(doc, c.handBone) !== null,
        live: live?.get(c.handBone) ?? null,
      })),
      elbows: subject.run.rig.mapping.some(m => m.ontologyId === 'LeftForeArm' || m.ontologyId === 'RightForeArm') && elbowPinStatus !== null
        ? { pins: elbowPinStatus() } : null,
      zones: zones.kind === 'ready'
        ? {
            zones: zones.zones.map(z => {
              return { name: z.name, intent: z.intent, refused: tailRefused.has(z.id) };
            }),
            offers: zones.candidates.map(c => ({ shape: c.shape })),
          }
        : null,
      tailSpring: {
        available: spring.available,
        refusal: spring.refusal,
        on: spring.on,
        settingsFrom: spring.settingsFrom,
        collidersAdjusted: spring.colliders.some(k => k.adjusted),
      },
      softOffered: ['LeftUpLeg', 'RightUpLeg', 'LeftArm', 'RightArm'].every(id => run.rig.mapping.some(m => m.ontologyId === id)),
      hair: hairCardState(),
      ...(intake.state() === null ? {} : { intake: intakeView(intake.state()!).cards.map(c => ({ subject: c.title, state: c.state })) }),
      soft: SOFT_TISSUE_MASSES.map(m => ({
        label: m.label,
        cards: soft.cards.filter(c => m.regions.includes(c.region)).map(c => ({ state: c.state, tier: c.tier })),
      })),
    });
  };
  // ─── ADR-040: intake V1 — World's same-origin hand-off and the guided repair ──
  // Every collaborator is a lazy closure (several are declared further down this
  // body); nothing here runs before boot() at the end of main().
  const intake = createIntakeSession({
    backend: indexedDbBackend(() => globalThis.indexedDB),
    clock: { now: () => Date.now(), sha256: sha256Hex },
    check: (bytes, label) => checkGlbForWorld(bytes, deps, label),
    // The arriving card (A) is the one prompt: nothing loads until Open it (INTAKE_GUIDED_FLOW §3.4 A).
    ask: (offer) => intakeFrame.ask(offer),
    missed: () => intakeFrame.missing(),
    // The ONE load door, as a drop walks it (ADR-032), under the sequence the Load CLICK reserved.
    load: (file, seq) => importDropped(file, 'character', seq),
    reserve: () => ++dropSeq,
    isCurrent: (seq) => seq === dropSeq,
    subject: () => {
      const t = store.getDocument().target;
      const held = uploads.get(t.specimen.uri) ?? null;
      // World checks a GLB: an FBX body hands over the GLB it was converted to.
      const bytes = held !== null && t.specimen.format === 'fbx' ? (convertedFbxCharacter(held)?.glb ?? null) : held;
      return { uri: t.specimen.uri, label: t.label, bytes };
    },
    // In the guided frame the CARD is the eye (panels carry decisions, not details): its notes stay on
    // the console and the hidden line; the full editor keeps the old reveal.
    note: (line, err) => {
      if (intakeFrame.guided()) {
        noteStatus(line);
        return;
      }
      noteStatus(line, err);
      revealStatus();
    },
    url: {
      asked: () => new URLSearchParams(location.search).get('handoff') === '1',
      strip: () => {
        const u = new URL(location.href);
        u.searchParams.delete('handoff');
        history.replaceState(history.state, '', u.toString());
      },
    },
    harrySha: __HARRY_SHA__,
    writerId: mintWriterId(),
    exportBody: (report) => intakeExportBody(report),
    // The guided flow (INTAKE_GUIDED_FLOW §3.4 C–E): the build on the click, the questions, the tail, the walk.
    build: (report) => buildForWorld(report),
    questions: (report) => {
      const unrigged = report.findings.some((f) => f.code === 'tail-unrigged');
      return {
        fingers: report.findings.some((f) => f.code === 'no-fingers') && handsOffered(),
        tail: unrigged || (report.findings.some((f) => f.code === 'tail-rigid') && tailSpring.view(subject.run).available),
        tailUnrigged: unrigged,
      };
    },
    // A tail the MESH has and the skeleton lacks: the zone detector's tail offer, on the subject on stage.
    meshFindings: () => {
      const offer = tailOfferNow();
      return offer === null ? [] : [tailUnriggedFinding(`zone detector: a tail at the hips, ${offer.cluster.faceIndices.length} faces, owned by ${offer.ownedBy ?? 'the body'}`)];
    },
    growTail: () => growOfferedTail(),
    leaveTail: () => leaveGrownTail(),
    // The name World shows (≤ 64, display only): the person's own name when they gave one, else the tidied file name.
    displayName: () => {
      const own = store.getDocument().package?.name?.trim();
      if (own !== undefined && own !== '') return own;
      const s = intake.state();
      return avatarName(s !== null && s.origin.kind === 'world' ? s.origin.name : store.getDocument().target.label);
    },
    announce: (message) => worldChannel.announce(message),
    setTailSway: (on) => tailSpring.setOn(subject.run, on, 'human:local'),
    showWalk: (glb) => showWorldWalk(glb),
    hideWalk: () => proofStage?.hide(),
    // Save a copy (F's door, and the way through F1/F3): WHERE at the click, then the bytes Check showed.
    saveCopy: async (glb, filename) => {
      const target = await pickSaveTarget(filename);
      if (target.kind === 'cancelled') return false;
      await writeSaveTarget(target, glb, filename);
      return true;
    },
    // The manual path returns too (Allen): the person World signed in on this origin, decoded, never trusted.
    worldUser: () =>
      readWorldUser({
        session: () => window.sessionStorage,
        local: () => window.localStorage,
      }),
    // The frame is the reveal while it guides; the Body tab is the full editor's.
    reveal: () => {
      if (!intakeFrame.guided()) openSurface('body');
    },
  });
  // ─── The guided frame (INTAKE_GUIDED_FLOW_2026-10-03, PR 1) ──
  // ONE predicate, `guided()`, keeps every detector overlay off the stage whatever the register (the
  // hair-highlight leak was Expert's open Hair section drawing an UNCONFIRMED offer). The first frame
  // is World's own headshot of their avatar, at rest.
  let guidedHold = false;
  /**
   * What the held portrait was fitted to: the scene AND the geometry of the canvas and the card (Codex
   * P2, #241) — a taller card, a resized window or a rotated tablet refits it; only an identical frame skips.
   */
  let headshotFor: string | null = null;
  let headshotScene: object | null = null;
  let sceneSerial = 0;
  const sceneKey = (): string => {
    const scene = subject.run.scene as object;
    if (headshotScene !== scene) {
      headshotScene = scene;
      sceneSerial += 1;
    }
    return String(sceneSerial);
  };
  const holdHeadshot = (): void => {
    const canvasBox = document.getElementById('renderCanvas')?.getBoundingClientRect();
    const cardBox = document.querySelector('.intake-step-card')?.getBoundingClientRect();
    const key = `${sceneKey()}|${Math.round(canvasBox?.width ?? 0)}x${Math.round(canvasBox?.height ?? 0)}|${Math.round(cardBox?.top ?? 0)}`;
    if (headshotFor === key) return;
    headshotFor = key;
    // Fitted to the MEASURED head (first-frame.ts says why this departs from World's exact frame), and
    // placed so the subject clears the step card. No measurable head: the full-body framing the load set.
    const head = viewport.measureHead(pickHeadBone);
    const canvasRect = document.getElementById('renderCanvas')?.getBoundingClientRect();
    const cardRect = document.querySelector('.intake-step-card')?.getBoundingClientRect();
    const lens =
      head === null || canvasRect === undefined || canvasRect.height <= 0
        ? null
        : fitHeadFrame(head, alphaForFacing(subject.run.rig.facing), {
            width: canvasRect.width,
            height: canvasRect.height,
            cardTop: cardRect === undefined ? canvasRect.height : cardRect.top - canvasRect.top,
          });
    viewport.holdLens(lens);
    console.info('[animator-v3] guided first frame:', lens === null ? 'full body (no measurable head)' : `fitted to ${head?.headBone} (${head?.vertices} vertices)`, { head, lens });
  };
  /** The guided screen asks the person to work AT THE HAND (C1b): its tools, markers and flyout come back. */
  let addingFingers = false;
  /** C2b: the person is giving the tail bones in its own editor — its bones, tint, bar and wag come back. */
  let editingTail = false;
  /** A launch from World holds the stage and every detector overlay until the frame takes over (B). */
  let bootHold = HANDOFF_LAUNCH;
  const endBootHold = (): void => {
    if (!bootHold) return;
    bootHold = false;
    if (!intakeFrame.guided()) {
      delete document.body.dataset['intake'];
      delete document.body.dataset['intakeStage'];
    }
    drawHair();
    refreshZones();
  };
  /**
   * The camera at the tail with the orbit FREE (the editor's look, not a held portrait): the chain and the
   * hips from a back three-quarter — the tail question's framing, placed once, then the person's to move.
   */
  const focusTail = (): void => {
    const canvasRect = document.getElementById('renderCanvas')?.getBoundingClientRect();
    if (canvasRect === undefined || canvasRect.height <= 0) return;
    const scene = subject.run.scene;
    scene.updateMatrixWorld(true);
    const chain = subject.run.tailGrafts[0]?.bones ?? nativeTailChain(scene) ?? [];
    const hips = subject.run.rig.mapping.find((m) => m.ontologyId === 'Hips')?.rigBone;
    const points = [...(hips === undefined ? [] : [hips]), ...chain]
      .map((n) => scene.getObjectByName(n))
      .filter((n): n is NonNullable<typeof n> => n !== undefined)
      .map((n) => ({ x: n.matrixWorld.elements[12]!, y: n.matrixWorld.elements[13]!, z: n.matrixWorld.elements[14]! }));
    const behind = alphaForFacing(subject.run.rig.facing) + Math.PI - (35 * Math.PI) / 180;
    // The viewport's own 50° lens: the placement is handed over, not held.
    const lens = fitTailFrame(points, behind, { width: canvasRect.width, height: canvasRect.height, cardTop: canvasRect.height }, (50 * Math.PI) / 180);
    if (lens === null) return;
    // Pulled back from the portrait's tight fit: room for the whole wag, the hips and the tail's bar.
    const at = lensPlacement({ ...lens, radius: lens.radius * 1.7 }, { x: 0, y: 0, z: 0 });
    viewport.viewFrom(at.camera, at.target);
  };
  /** The stage looks at one hand from above (the fingers question): the hand bone's world position, the Hand Studio's focus. */
  const focusHands = (): void => {
    const hand = subject.run.rig.mapping.find((m) => m.ontologyId === 'LeftHand') ?? subject.run.rig.mapping.find((m) => m.ontologyId === 'RightHand');
    const node = hand === undefined ? undefined : subject.run.scene.getObjectByName(hand.rigBone);
    if (node === undefined) return;
    node.updateWorldMatrix(true, false);
    const e = node.matrixWorld.elements;
    const at = { x: e[12]!, y: e[13]!, z: e[14]! };
    const measure = viewport.measureSubject(pickHeadBone);
    viewport.focusOn({ x: at.x, y: at.y, z: at.z }, Math.max(measure.heightUnits * 0.07, 1e-3));
  };
  /**
   * The tail question looks at THE TAIL (design §3.7 `ask-tail`; Allen, 2026-10-03): the chain's joints
   * and the hips, from a back three-quarter, filling the space above the card with room for the sway.
   * No chain to find: the whole body from behind.
   */
  const holdTailView = (): void => {
    const canvasRect = document.getElementById('renderCanvas')?.getBoundingClientRect();
    const cardRect = document.querySelector('.intake-step-card')?.getBoundingClientRect();
    if (canvasRect === undefined || canvasRect.height <= 0) return;
    const view = { width: canvasRect.width, height: canvasRect.height, cardTop: cardRect === undefined ? canvasRect.height : cardRect.top - canvasRect.top };
    const behind = alphaForFacing(subject.run.rig.facing) + Math.PI - (35 * Math.PI) / 180;
    const scene = subject.run.scene;
    scene.updateMatrixWorld(true);
    const at = (name: string): { x: number; y: number; z: number } | null => {
      const node = scene.getObjectByName(name);
      if (node === undefined) return null;
      const e = node.matrixWorld.elements;
      return { x: e[12]!, y: e[13]!, z: e[14]! };
    };
    const chain = nativeTailChain(scene) ?? subject.run.tailGrafts[0]?.bones ?? null;
    const hips = subject.run.rig.mapping.find((m) => m.ontologyId === 'Hips')?.rigBone;
    const points = [...(hips === undefined ? [] : [hips]), ...(chain ?? [])].map(at).filter((p): p is { x: number; y: number; z: number } => p !== null);
    let lens = chain === null ? null : fitTailFrame(points, behind, view);
    // An UNRIGGED tail is framed on the detector's zone (its flesh) and the hips — shown before the person answers.
    if (lens === null) {
      const offer = tailOfferNow();
      const now = zoneArraysNow();
      if (offer !== null && now !== null) {
        const P = now.arrays.positions;
        const I = now.arrays.indices;
        const flesh: { x: number; y: number; z: number }[] = [];
        const faces = offer.cluster.faceIndices;
        const step = Math.max(1, Math.floor(faces.length / 400));
        for (let k = 0; k < faces.length; k += step) {
          const v = I[faces[k]! * 3]!;
          flesh.push({ x: P[v * 3]!, y: P[v * 3 + 1]!, z: P[v * 3 + 2]! });
        }
        const hip = hips === undefined ? null : at(hips);
        lens = fitTailFrame([...(hip === null ? [] : [hip]), ...flesh], behind, view, undefined, true);
      }
    }
    if (lens === null) {
      const m = viewport.measureSubject(pickHeadBone);
      const half = m.heightUnits * 0.3;
      lens = fitHeadFrame({ min: { x: m.centerX - half, y: m.floorY, z: m.centerZ - half }, max: { x: m.centerX + half, y: m.floorY + m.heightUnits, z: m.centerZ + half } }, behind, view, (30 * Math.PI) / 180, 0);
    }
    if (lens !== null) viewport.holdLens(lens);
  };
  /**
   * Back to World — ONE door for every way out (Allen 2026-10-04: the editor's side-panel link loaded a
   * fresh World page in Harry's tab while the guided Done handed back to the World tab that opened Harry).
   */
  const returnToWorld = (
    say: { readonly worldHasIt: (sha256: string) => void; readonly stayed: () => void } = {
      worldHasIt: (sha256) => intakeFrame.worldHasIt(sha256),
      stayed: () => intakeFrame.stayed(),
    },
  ): void => {
    // Design §3.6 + the world-2d channel: an opener → focus and close (a refused close says where World
    // is, on the card). No opener → ask the channel: a World tab that has the file answers within 3 s,
    // and the card says so (no new tab). No answer, or no channel → go to World in this tab.
    const opener = window.opener as Window | null;
    if (opener === null || opener.closed) {
      const sent = intake.lastSent();
      // 3 s: World acks late when its tab is busy (World #2280 rerun) — a slower answer still keeps this tab.
      void (sent === null ? Promise.resolve(false) : worldChannel.waitAck(sent.sha256, 3000)).then((has) => {
        if (has && sent !== null) say.worldHasIt(sent.sha256);
        else window.location.assign(new URL(WORLD_UPLOAD_PATH, window.location.origin).toString());
      });
      return;
    }
    try {
      opener.focus();
    } catch {
      /* another origin's window: focusing is best effort */
    }
    window.close();
    window.setTimeout(() => say.stayed(), 300);
  };
  const intakeFrame = createIntakeFrame(document.body, {
    session: intake,
    stageName: () => store.getDocument().target.label,
    changed: (on, screen) => {
      const stage = screen?.stage ?? null;
      const wasAdding = addingFingers;
      const wasTail = editingTail;
      addingFingers = on && screen?.id === 'doing-fingers';
      editingTail = on && screen?.id === 'doing-tail';
      // The frame speaks: their model is on stage, or the frame stepped aside — the boot hold is over.
      if (!on || stage !== 'absent') bootHold = false;
      // The proof stage is the walk's ONLY while a proof screen shows it: never over the editor, never
      // taking a click (the "companion" Allen saw walking over the Full editor, 2026-10-03).
      proofStage?.setVisible(on && stage === 'proof');
      if (on) document.body.dataset['intake'] = 'guided';
      else delete document.body.dataset['intake'];
      if (on && stage === 'absent') document.body.dataset['intakeStage'] = 'absent';
      else delete document.body.dataset['intakeStage'];
      // Every overlay is off while it guides — except adding fingers and giving a tail bones: the tools' work.
      viewport.setOverlaysSuppressed(on && !addingFingers && !editingTail);
      // At rest throughout — adding fingers too: pinning reads the hand where it rests, not mid-stride.
      guidedHold = on;
      applyMixerPause();
      if (on) statusEl?.classList.add('status-hidden');
      // Leaving the frame gives an Expert back the evidence line the register shows (Codex P2, #241).
      else if (store.getLayout().register === 'expert') revealStatus();
      if (on && stage === 'headshot') holdHeadshot();
      else {
        headshotFor = null;
        viewport.holdLens(null);
        if (on && stage === 'hands') focusHands();
        if (on && stage === 'body') holdTailView();
        if (on && stage === 'tail' && !wasTail) focusTail();
      }
      if (addingFingers) openSurface('body', 'joints');
      if (editingTail && !wasTail) {
        openSurface('body', 'parts');
        const zoneId = subject.run.tailGrafts[0]?.zoneId;
        // After the camera has rendered at the tail: the bar is placed beside flesh it can see.
        if (zoneId !== undefined && zoneId !== null) requestAnimationFrame(() => requestAnimationFrame(() => { if (editingTail) openZone(zoneId); }));
      }
      if (!editingTail && wasTail) closeZoneFlyout();
      if (addingFingers !== wasAdding || editingTail !== wasTail) intakeFocusListeners.forEach((l) => l());
      worldCheckLabel();
      // The overlays the predicate gates are redrawn on the turn, in both directions.
      drawHair();
      refreshZones();
    },
    worldTab: () => {
      try {
        const opener = window.opener as Window | null;
        return opener === null || opener.closed ? 'gone' : 'open';
      } catch {
        return 'gone';
      }
    },
    returnToWorld: () => returnToWorld(),
  });
  const intakeFocusListeners = new Set<() => void>();
  /** The world-2d return channel (optional: no BroadcastChannel, or a throw, is a channel that never answers). */
  const worldChannel = createWorldChannel(() => (typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(HANDOFF_CHANNEL)));
  // The portrait refits when the canvas or the card changes size (Codex P2, #241).
  {
    const refit = (): void => {
      if (!intakeFrame.guided()) return;
      const stage = intakeFrame.probe()?.stage;
      if (stage === 'headshot') holdHeadshot();
      // The tail's frame depends on the same canvas and card (Codex P2, #242).
      else if (stage === 'body') holdTailView();
    };
    const observer = new ResizeObserver(refit);
    const canvasEl = document.getElementById('renderCanvas');
    if (canvasEl !== null) observer.observe(canvasEl);
    const cardEl = document.querySelector('.intake-step-card');
    if (cardEl !== null) observer.observe(cardEl);
  }
  /** The guided predicate (design §3.1): read by every overlay that could reach the first impression. */
  const guided = (): boolean => intakeFrame.guided() || bootHold;
  let worldCheckLabel = (): void => undefined;
  subjectChanged.add(() => {
    if (!intakeFrame.guided()) return;
    // A drop on the nothing-waiting screen IS the action: the dropped file is checked, guided.
    if (intakeFrame.waitingForDrop()) {
      intakeFrame.resume();
      void intake.optIn();
      return;
    }
    if (intakeFrame.probe()?.stage === 'headshot') holdHeadshot();
  });
  subjectChanged.add(() => intake.subjectChanged());
  intake.subscribe(() => bodyProgressListeners.forEach((listener) => listener()));
  const registry = createPanelRegistry([{
      id: 'timeline',
      title: 'Timeline',
      modes: ['playback', 'pose', 'edit'],
      registers: ['expert', 'creator'],
      paletteVerbs: ['open timeline', 'close timeline'],
      // The imported clip is asked for at click time, not captured here: once
      // the import surface lands the subject is replaceable, and a captured
      // clip would go stale the moment it is.
      mount: createTimelineStrip(viewport.probe, () => clipRun.result.clip),
    },{id:'body', title:'Body', modes:[...SHELL_MODES], registers:['expert','creator'], paletteVerbs:['open body','close body'], mount:createBodyPanel({
hands:createHandsPanel({
mark: bodyMark,
        say: (line, reveal) => noteBody(line, reveal),
        // Asked at click time: the subject is replaceable, and a captured rig
        // would go stale the moment it is (#61's mixer-birth lesson).
        candidates: editableCandidates,
        savedState: handBone => savedHandState(store.getDocument(), handBone),
        onReading: (handBone, reading) => {
          let perRun = handLive.get(subject.run);
          if (perRun === undefined) { perRun = new Map(); handLive.set(subject.run, perRun); }
          if (JSON.stringify(perRun.get(handBone) ?? null) === JSON.stringify(reading)) return;
          perRun.set(handBone, reading);
          bodyProgressListeners.forEach(listener => listener());
        },
        measure: (candidate, resolution) => measureHand(subject.run.scene, candidate, resolution),
        // The sweep is real work (8 voxelizations per hand), so it is memoized
        // PER RUN: the WeakMap key is the run object itself, which a graft or a
        // new drop replaces — stale scans die with the subject that produced
        // them, and the panel's promise-identity check sees a fresh scan.
        scan: ((): ((candidate: HandCandidate) => Promise<TipConsensus>) => {
          const memo = new WeakMap<object, Map<string, Promise<TipConsensus>>>();
          return (candidate) => {
            const run = subject.run;
            let perHand = memo.get(run);
            if (perHand === undefined) {
              perHand = new Map();
              memo.set(run, perHand);
            }
            let scan = perHand.get(candidate.handBone);
            if (scan === undefined) {
              scan = consensusScan(run.scene, candidate);
              perHand.set(candidate.handBone, scan);
            }
            return scan;
          };
        })(),
        confirmerId: () => 'human:local',
        showPins: (handBoneName, view) => handMarkers.show(handBoneName, view),
        // The pin gesture is the viewport's to own (it has the camera and the
        // canvas, and it is the seam that keeps two click-tools from meaning
        // the same click — ADR-006 §13.2). The panel says WHEN; this says WHERE.
        armPinning: (handlers) =>
          viewport.setPinTool(
            handlers === null
              ? null
              : {
                  // The DISCS are the pick surface (ADR-022 T3b, Allen's first
                  // field report: "my instinct was to click the discs in 3D").
                  markers: () => handMarkers.pickables(),
                  onMarker: (handBone, markerId) => handlers.onMarker(handBone, markerId),
                  onHover: (handBone, markerId) => handlers.onHover(handBone, markerId),
                  onPin: (hit) => handlers.onPin(hit),
                  onMiss: () => handlers.onMiss(),
                  onRefusal: (message) => handlers.onRefusal(message),
                },
          ),
        // Pinning holds still: a fingertip is a two-millimetre target and a
        // walking model has moved it by the time the click lands. PAUSE, not a
        // mode change — a mode change re-mounts the panels for that mode, which
        // disposes the Hand Studio mid-gesture (measured through the e2e: the
        // pin tool went away between arming and the click, and clicking did
        // nothing at all).
        holdStill: (hold) => {
          pinHold = hold;
          applyMixerPause();
        },
        clearMarkers: () => handMarkers.clear(),
        markerScreenXY: (handBone, id) =>
          handMarkers.screenXY(handBone, id, { projectPoint: (p) => viewport.projectPoint(p) }),
        // Editing is a draft over the current hand; applying re-declares only
        // that hand through the normal rebuild. Other Document edits survive.
        canEdit: handBone => savedHandState(store.getDocument(), handBone) !== null,
        editHand: handBone => {
          if (savedHandState(store.getDocument(), handBone) === null) return Promise.resolve(false);
          let selected = editingHands.get(subject.run);
          if (!selected) { selected = new Set(); editingHands.set(subject.run, selected); }
          selected.add(handBone);
          return Promise.resolve(true);
        },
        replaceHand: (handBone) => {
          // DROP, THEN PIN FRESH (Allen, 2026-09-29, on Daria): no pins are seeded from the old
          // fingers — on a body whose own finger weights are wrong (her wing membrane rides them)
          // they would put the "tips" where the bad weights end. The hand opens as a fingerless hand
          // does: the detector's tips, the wrist, the knuckles; the Graft drops the old armature.
          try {
            if (stripDigitsFromRig(subject.run.rig, handBone).chains.length === 0) return Promise.resolve(null);
          } catch (err) {
            noteBody(`${handBone}: ${err instanceof Error ? err.message : String(err)}`, true);
            return Promise.resolve(null);
          }
          for (const map of [editingHands, replacingHands]) {
            let set = map.get(subject.run);
            if (!set) { set = new Set(); map.set(subject.run, set); }
            set.add(handBone);
          }
          noteBody(`${handBone}: pin the fingertips as for a new hand — the old fingers are dropped when you Graft`);
          return Promise.resolve(initialHandUi());
        },
        focusHand: (evidence) => {
          // The guided frame owns the camera (its first frame is World's headshot): a hidden Hand Studio
          // that scans on arrival must not swing the stage to a hand nobody asked about.
          if (guided() && !addingFingers) return;
          // Frame the whole HAND: a hand is a few centimetres of a whole body,
          // and a person cannot point at a fingertip that is forty pixels wide.
          const xs = evidence.branches.flatMap((b) => [b.baseWorld, b.tipWorld]);
          const center = {
            x: xs.reduce((t, p) => t + p.x, 0) / xs.length,
            y: xs.reduce((t, p) => t + p.y, 0) / xs.length,
            z: xs.reduce((t, p) => t + p.z, 0) / xs.length,
          };
          const radius = Math.max(
            ...xs.map((p) => Math.hypot(p.x - center.x, p.y - center.y, p.z - center.z)),
            evidence.region.voxelSize * 4,
          );
          viewport.focusOn(center, radius);
        },
        declare: (candidate, args) => {
          // Read NOW, at the Graft click: the queued graft below may run after another rerun.
          const replacing = replacingHands.get(subject.run)?.has(candidate.handBone) === true;
          // Composed here, GATED by the loader — declareHand does not parse
          // (only main.ts begins a lineage), and swapTo takes it through the
          // same door a dropped file walks. A graft is not an undoable edit:
          // it adds bones that later edits address, so undoing it would strand
          // them (ADR-007 §4, the reason `target` has no command verbs).
          //
          // Grafts SERIALIZE — each declaration composes over the previous
          // graft's completed document. Composing eagerly from the pre-graft
          // document meant two rapid clicks (left hand, then right) had the
          // second `swapTo` discard the first as stale: a silent lost graft
          // (Codex P1, #77).
          graftChain = graftChain.then(async () => {
            dropSeq += 1;
            const seq = dropSeq;
            noteStatus(`grafting ${candidate.handBone}…`);
            try {
              const declared =
                args.pins.length === 0 ? withdrawHand(store.getDocument(), candidate.handBone) : declareHand(store.getDocument(), candidate, {
                  resolution: args.resolution,
                  ...(replacing ? { replaceNativeDigits: true } : {}),
                  digitAssignment: { pins: args.pins, confirmedBy: args.confirmedBy },
                  wristRecenter: args.recenterWrist
                    ? { value: 'seed-centroid', confirmedBy: args.confirmedBy }
                    : null,
                  ...(args.wrist === undefined
                    ? {}
                    : { wristStation: { point: args.wrist, confirmedBy: args.confirmedBy } }),
                  thumbOpposition: { degrees: args.thumbOpposition, confirmedBy: args.confirmedBy },
                });
              // THE OLD FINGERS' EDITS GO WITH THEM (Codex P2, #226): a pose or key naming a native digit
              // of THIS hand would refuse the very run that strips it. Only on the FIRST replace — once the
              // Document says 'replace', the live rig's digits under this hand are the graft's, and a
              // re-pin keeps what was authored on them. The strip's own rule names the bones.
              const firstReplace =
                replacing && args.pins.length > 0 && store.getDocument().target.hands.find((h) => h.handBone === candidate.handBone)?.nativeDigits !== 'replace';
              let digitBones: readonly string[] = [];
              if (firstReplace) {
                try {
                  digitBones = stripDigitsFromRig(subject.run.rig, candidate.handBone).removed;
                } catch (err) {
                  // Nothing to strip is the run's refusal to make, loudly, by name — not this door's.
                  if (!(err instanceof StripDigitsRefusal)) throw err;
                }
              }
              const forgotten = forgetReplacedDigitEdits(declared, digitBones);
              const dropped =
                forgotten.poses.length + forgotten.tracks.length === 0
                  ? null
                  : `replacing the fingers dropped what named the old ones — ` +
                    [
                      forgotten.poses.length > 0 ? `${forgotten.poses.length} pose edit(s) (${forgotten.poses.join(', ')})` : '',
                      forgotten.tracks.length > 0 ? `${forgotten.tracks.length} key track(s) (${forgotten.tracks.join(', ')})` : '',
                    ]
                      .filter(Boolean)
                      .join(' and ');
              // A PARTIAL REPLACE (2026-09-29, the ActorCore kobold): a native-fingered body's clips were
              // selected with the full-hands profile; replacing a hand with fewer than five digits leaves
              // that profile incomplete and the retarget refuses the run. Such clips drop to the body
              // profile — finger motion waits for all five — and the status line says so.
              const fullHand = new Set(args.pins.map((p) => p.digit)).size >= 5;
              const next =
                replacing && !fullHand
                  ? { ...forgotten.doc, clipSelections: forgotten.doc.clipSelections.map((c) => (c.profile === 'minimal+hands' ? { ...c, profile: 'minimal' } : c)) }
                  : forgotten.doc;
              const partial = next !== forgotten.doc ? `${new Set(args.pins.map((p) => p.digit)).size} of 5 fingers pinned — the clips play without finger motion until all five are` : null;
              const committed = await swapTo(next, seq);
              // One body line carries both (noteBody holds one), said only once the Document is the
              // live one — a refused or overtaken run dropped nothing. A loss is revealed, never hidden.
              if (committed && (dropped !== null || partial !== null)) noteBody(`${candidate.handBone}: ${[dropped, partial].filter(Boolean).join('; ')}`, dropped !== null);
              const grafted = subject.run.handGrafts.reduce((t, g) => t + g.graftedBoneCount, 0);
              noteStatus(args.pins.length === 0 || grafted > 0 ? null : `${candidate.handBone}: the run completed but grafted no bones`);
            } catch (err) {
              // WITHOUT THIS the button was a dead switch: a refusal from
              // placeDigits (a declared profile the volume contradicts, say)
              // rejected a floating promise, the console got it, and the person
              // who pressed "graft" saw nothing move and nothing said. A refusal
              // is the §8 contract speaking and belongs on the surface, exactly
              // as the drop path already treats it. The catch lives INSIDE the
              // chained task, so a refusal never breaks the chain for the next
              // declaration.
              // `err` rides along so noteStatus treats this as the refusal it
              // is — revealing the hidden-by-default status line (and owning
              // the one console output path). Without it the notice landed on
              // an invisible surface (Codex, #77).
              noteStatus(
                `${candidate.handBone} could not be grafted — ${err instanceof Error ? err.message : String(err)}`,
                err,
              );
            }
          });
        },
      }),
joints:createCharacterPanel({
        mountElbows: (host) => {
          const section = document.createElement('section');
          section.className = 'elbow-card hand-card';
          const title = document.createElement('h3');
          title.textContent = 'Elbows';
          const drawElbowMark = () => {
            title.querySelector('.status')?.remove();
            const m = bodyMark('Elbows');
            if (m !== null) title.append(m);
          };
          drawElbowMark();
          bodyProgressListeners.add(drawElbowMark);
          const head = document.createElement('div');
          head.className = 'elbow-head';
          const button = document.createElement('button');
          // `.elbow-action` is the placement tool's own class (it owns the
          // pressed/suspect/disabled states and sets the ⌖ label on refresh).
          // Adding it at CREATION means the button is styled before the tool
          // has ever mounted, instead of sitting unstyled in the panel.
          button.type = 'button'; button.className = 'elbow-action pin-arm';
          button.textContent = 'Pin elbows'; button.disabled = true;
          head.append(button, describeButton('Elbow pinning tutorial', () => ELBOW_HOWTO));
          const status = document.createElement('p');
          status.className = 'elbow-status'; status.setAttribute('role', 'status');
          status.textContent = elbowStatus; status.hidden = elbowStatus === '';
          const resets = document.createElement('div');
          resets.className = 'elbow-resets';
          section.append(title, head, status, resets);
          const resetButtons: Array<{ side: 'Left' | 'Right'; button: HTMLButtonElement }> = [];
          for (const side of ['Left', 'Right'] as const) {
            const reset = document.createElement('button');
            reset.type = 'button'; reset.className = 'elbow-reset';
            reset.textContent = `Reset ${side.toLowerCase()}`;
            reset.onclick = () => {
              const bone = subject.run.rig.mapping.find(m => m.ontologyId === `${side}ForeArm`)?.rigBone;
              if (!bone) return;
              void commitElbow({ schemaVersion: '1.0.0', verb: 'elbow.reset', params: { bone } }).catch(error => noteBody(`elbows: ${refusalLine(error)}`, true));
            };
            resets.append(reset); resetButtons.push({ side, button: reset });
          }
          const renderReset = () => {
            for (const { side, button: reset } of resetButtons) {
              const bone = subject.run.rig.mapping.find(m => m.ontologyId === `${side}ForeArm`)?.rigBone;
              reset.hidden = !store.getDocument().target.recenters?.some(r => r.bone === bone && r.rebuildHinge);
            }
            // An empty row still claims its gap in the column — hide the row,
            // not just the buttons in it.
            resets.hidden = resetButtons.every(({ button: reset }) => reset.hidden);
          };
          const unsubscribe = store.subscribe(renderReset); renderReset();
          host.append(section); elbowButtons.add(button); elbowStatuses.add(status);
          return () => { unsubscribe(); bodyProgressListeners.delete(drawElbowMark); elbowButtons.delete(button); elbowStatuses.delete(status); section.remove(); };
        },
        // Lazy on purpose: the state functions and `store` are declared later
        // in this function body, and the arrows only run at render/commit time
        // — the same read-through-never-capture posture as every other panel dep.
        armSpace: () => armSpaceUiState(),
        setArmSpace: (degrees) => {
          armSpacePreview = null;
          store.dispatch({ schemaVersion: '1.0.0', verb: 'armSpace.set', params: { degrees } });
        },
        previewArmSpace: (degrees) => {
          armSpacePreview = degrees;
          applyCharacterFilter();
        },
        headSpace: () => headSpaceUiState(),
        setHeadSpace: (degrees) => {
          headSpacePreview = null;
          store.dispatch({ schemaVersion: '1.0.0', verb: 'headSpace.set', params: { degrees } });
        },
        previewHeadSpace: (degrees) => {
          headSpacePreview = degrees;
          applyCharacterFilter();
        },
      }),
parts:createZonesPanel({
mark: bodyMark,
        say: (line, reveal) => noteBody(line, reveal),
        // Lazy like every other panel dep: these closures are declared later in
        // this function body and only run at render/click time.
        zones: () => zonesUiState(),
        pin: (key) => pinCandidate(key),
        dismiss: (key) => dismissCandidate(key),
        newZoneByWand: () => armNewZone(),
        openZone: (id) => openZone(id),
        deleteZone: (id) => {
          try {
            store.dispatch({ schemaVersion: '1.0.0', verb: 'zone.delete', params: { id } });
          } catch (err) {
            // T2: a zone carrying a chain refuses removal until the chain is withdrawn.
            noteZones(`zone: ${refusalLine(err)}`, true);
            return;
          }
          if (openZoneId === id) closeZoneFlyout();
          noteZones(null);
        },
      }),
showHands:handsOffered,
showJoints:()=>subject.run.rig.mapping.some(m=>m.ontologyId==='LeftForeArm'||m.ontologyId==='RightForeArm'),
// ready alone, not "the detector found something": the ➕ new-zone card is the escape hatch for
// exactly the body where it finds NOTHING (Allen, 2026-09-26: Penny — no offer, so no section, so no ➕).
showParts:()=>zonesUiState().kind==='ready',
// ADR-038: the tail's spring — offered once a chain exists (grown, or declared by the file).
spring:createTailSpringPanel({
mark: bodyMark,
        say: (line, reveal) => noteBody(line, reveal),
        view: () => tailSpring.view(subject.run),
        setOn: (on) => tailSpring.setOn(subject.run, on, 'human:local'),
        preview: (choice) => tailSpring.preview(subject.run, choice),
        commit: (choice) => tailSpring.commit(subject.run, choice, 'human:local'),
        reset: () => tailSpring.reset(subject.run),
        subjectChanged: (listener) => { subjectChanged.add(listener); return () => subjectChanged.delete(listener); },
      }),
showSpring:()=>{const v=tailSpring.view(subject.run);return v.available||v.refusal!==null;},
// ADR-038 J2: the soft-tissue cards — offered on any body with hips and shoulders mapped.
softTissue:createSoftTissuePanel({
mark: bodyMark,
        say: (line, reveal) => noteBody(line, reveal),
        view: () => softTissueView(),
        declare: (region, zoneId) => {
          try {
            store.dispatch({ schemaVersion: '1.0.0', verb: 'softTissue.declare', params: { region, ...(zoneId !== null ? { zoneId } : {}), confirmedBy: 'human:local' } });
          } catch (err) {
            noteBody(`soft tissue: ${refusalLine(err)}`, true);
          }
        },
        withdraw: (region) => {
          try {
            store.dispatch({ schemaVersion: '1.0.0', verb: 'softTissue.withdraw', params: { region } });
          } catch (err) {
            noteBody(`soft tissue: ${refusalLine(err)}`, true);
          }
        },
        setOn: (region, on) => softTissue.setOn(subject.run, region, on),
        setControls: (region, controls) => softTissue.setControls(subject.run, region, controls, 'human:local'),
        surfaceJiggle: (region) => subject.run.surfaceJiggle.some((r) => r.region === region && r.modes.length > 0),
        reset: () => softTissue.reset(subject.run),
        showWeights: (bone) => showBoneWeights(bone),
        subjectChanged: (listener) => { subjectChanged.add(listener); return () => subjectChanged.delete(listener); },
      }),
showSoftTissue:()=>['LeftUpLeg','RightUpLeg','LeftArm','RightArm'].every(id=>subject.run.rig.mapping.some(m=>m.ontologyId===id)),
// ADR-039 T1: the Hair card — offered when the detector finds flesh over a skull (or refuses to place one, said so).
hair:createHairPanel({
mark: bodyMark,
        say: (line, reveal) => noteBody(line, reveal),
        view: () => hair.view(),
        confirm: (trusted) => {
          // A human act, so a trusted click only (the confirmedBy rule, document.ts confirmationSchema).
          if (!trusted) return;
          try {
            hairTool.flush();
            hair.confirm('human:local');
          } catch (err) {
            noteBody(`hair: ${refusalLine(err)}`, true);
          }
        },
        notHair: () => {
          try {
            hair.notHair();
          } catch (err) {
            noteBody(`hair: ${refusalLine(err)}`, true);
          }
          hairChanged();
        },
        offerAgain: () => {
          hair.offerAgain();
          hairChanged();
        },
        resetSkull: () => {
          hair.resetSkull();
          hairChanged();
        },
        startAdjust: () => {
          hair.startAdjust();
          hairChanged();
        },
        setGravity: (gravity) => {
          try {
            hair.setGravity(gravity);
          } catch (err) {
            noteBody(`hair: ${refusalLine(err)}`, true);
          }
        },
        visible: (on) => {
          hairVisible = on;
          if (!on) disarmHairWand();
          drawHair();
        },
        wand: (mode: 'add' | 'remove' | null, trusted: boolean) => {
          if (!trusted) return;
          armHairWand(mode);
        },
        wandMode: () => hairWandMode,
        motion: () => (softTissue.views(subject.run).some((v) => v.region === 'Hair') ? hair.motion() : null),
        previewMotion: (controls) => softTissue.setControls(subject.run, 'Hair', controls, 'human:local'),
        setMotion: (motion) => {
          try {
            hair.setMotion(motion);
          } catch (err) {
            noteBody(`hair: ${refusalLine(err)}`, true);
          }
        },
        subjectChanged: (listener) => { subjectChanged.add(listener); return () => subjectChanged.delete(listener); },
        changed: (listener) => { hairListeners.add(listener); return () => hairListeners.delete(listener); },
      }),
showHair:()=>hairCardState()!==null,
// ADR-040: the World check leads the Body tab while an intake session is open.
intake:createIntakePanel({
  session: intake,
  // The editor's way out is the guided one, its words on the status line (the card is not on screen).
  returnToWorld: () =>
    returnToWorld({
      worldHasIt: () => { noteStatus('Your World tab has it. Switch to it.'); revealStatus(); },
      stayed: () => { noteStatus('Your browser kept you here. Click your World tab at the top of the window.'); revealStatus(); },
    }),
  jump: (to) => openSurface('body', to),
  playWalk: () => {
    store.previewLibraryClip(null);
    store.setMode('playback');
    applyMixerPause();
    noteStatus(subject.run.clips.length > 0
      ? `walk preview: ${subject.run.clips[0]!.result.clip.name}, retargeted by Harry — World plays its own walk once the export's fixes are in`
      : 'walk preview: this character wears no clip — drop an animation onto it to preview one');
  },
}),
showIntake:()=>intake.active(),
// INTAKE_GUIDED_FLOW §3.4 C1b: while the person adds fingers, Body shows ONLY Joints (the Hand Studio).
focus:()=>addingFingers?'joints':editingTail?'parts':null,
focusChanged:(listener)=>{intakeFocusListeners.add(listener);return ()=>intakeFocusListeners.delete(listener);},
intakeChanged:(listener)=>intake.subscribe(listener),
subjectChanged:(listener)=>{subjectChanged.add(listener);return ()=>subjectChanged.delete(listener);},
// XD note 2026-09-27 §3.1 (PR 2): the header marks and denominators.
progress: readBodyProgress,
openLook:()=>openSurface('look'),
progressChanged:(listener)=>{bodyProgressListeners.add(listener);return ()=>bodyProgressListeners.delete(listener);}
})},{
      id: 'package',
      title: 'Package',
      // Every mode: what the character becomes in World is a property of the
      // character (ADR-033), not of the mode a person is in.
      modes: [...SHELL_MODES],
      registers: ['expert', 'creator'],
      paletteVerbs: ['open package', 'close package'],
      mount: createPackagePanel({
        // Lazy like every other panel dep: declared later in this function body.
        inputs: () => ({ doc: store.getDocument(), boneNames: packageBoneNames(subject.run.rig), library: libraryRead, takesInSession: [...heroTakes.keys()] }),
        ensureLibrary: () => {
          void ensureLibrary();
        },
        subscribeLibrary: (listener) => {
          libraryListeners.add(listener);
          return () => libraryListeners.delete(listener);
        },
        openBakeFolder: () => openBakeFolder(),
        setChoices: (choices) => {
          try {
            store.dispatch({ schemaVersion: '1.0.0', verb: 'package.set', params: { package: choices } });
          } catch (err) {
            noteStatus(`package: ${refusalLine(err)}`, err);
          }
        },
        preview: (clipId) => playLibraryClip(clipId),
        previewing: () => store.getShell().libraryClip,
        takeHero: () => takeHero(),
        takeHeadshot: () => takeHeadshot(),
        takeUrl: (name) => heroTakeUrl(name),
        showHeroGuide: (on) => showHeroGuide(on),
        package: () => packageDoor(),
        packageAll: () => packageAllDoor(),
        batchRunning: () => batchRunning,
        batchArmed: () => (batchArmed === null ? null : batchArmed.source.name),
      }),
    },{
      id: 'look',
      title: 'Look',
      // Every mode, the Character rationale: a material factor is a per-avatar
      // property, not a mode-scoped one (ADR-020 §11: it is the character's
      // TRUTH — export-real, unlike the display-only tenants next door).
      modes: [...SHELL_MODES],
      registers: ['expert', 'creator'],
      paletteVerbs: ['open look', 'close look'],
      mount: createMaterialsPanel({
        // Lazy on purpose — the same read-through-never-capture posture as
        // the Character panel above.
        materials: () => materialsUiState(),
        setMapUse: (material, channel, fromMap) => {
          materialPreview = null;
          store.dispatch({
            schemaVersion: '1.0.0',
            verb: 'material.setMapUse',
            params: { material, channel, fromMap },
          });
        },
        setFactor: (material, channel, value) => {
          materialPreview = null;
          store.dispatch({
            schemaVersion: '1.0.0',
            verb: 'material.setFactor',
            params: { material, channel, value },
          });
        },
        previewFactor: (material, channel, value) => {
          materialPreview = value === null ? null : { kind: 'scalar', material, channel, value };
          applyMaterialsFilter();
        },
        setColor: (material, channel, value) => {
          materialPreview = null;
          store.dispatch({
            schemaVersion: '1.0.0',
            verb: 'material.setColor',
            params: { material, channel, value },
          });
        },
        previewColor: (material, channel, value) => {
          materialPreview = value === null ? null : { kind: 'color', material, channel, value };
          applyMaterialsFilter();
        },
      }),
    }]);
  const availability = registry.availability();
  const WORKSPACES_KEY = 'animator.workspaces.v1';
  // Persisted layout is a CACHE of preference, parse-gated on every read; a
  // refusal restores defaults and NAMES what it dropped (§7) — the notice
  // rides the status line so the fallback is never silent.
  let initialWorkspaces = defaultWorkspaces(availability);
  let workspaceNotice: string | null = null;
  // Storage itself may be DENIED (sandboxed iframe, persistence policy) and
  // throws on ACCESS — an optional preference cache must degrade to defaults
  // with a loud notice, never take the boot down (Codex P2, #63). A deliberate
  // no-persistence environment and a broken one read the same here, and the
  // notice says which state we are in (Loud-Mothball).
  let storageOk = true;
  // One wording for the one environment fact, whether it is discovered on the
  // first read or on a write hours later.
  const STORAGE_LOST = 'workspace persistence unavailable — layouts will not survive reload';
  // The notice is LIVE, not a boot-time string. Reading storage can succeed
  // while WRITING later fails — an exhausted quota, a restricted-storage mode
  // — and that failure arrives long after the status line was composed. A
  // console warning is not a surface (Codex P2, #63): the UI would go on
  // looking like it saved the layout, and the loss would only appear on
  // reload. Anything that sets the notice re-renders the line through here.
  // The weight overlay's line lives in its OWN slot (Codex P2, #98 round
  // five): sharing `workspaceNotice` meant hiding the overlay cleared a
  // still-true application warning (STORAGE_LOST survived the storage, not
  // the W key). The status line composes both.
  let overlayNotice: string | null = null;
  // The wireframe's own slot (ADR-031 T0) — two overlays can be up at once,
  // and neither may clobber the other's voice.
  let wireNotice: string | null = null;
  let zoneNotice: string | null = null; // ADR-031 T1 — the zones' own voice
  // THE BODY VOICE (XD 2026-09-29 §3B; Allen, same day: "i tend to lean status"): the Body cards'
  // readings — a leaf's length, the analyzer's opinion, a tail's colliders — left the panel for
  // this line. Its own slot, so a hover never clobbers a zone's question or a storage warning.
  let bodyNotice: string | null = null;
  const renderStatus = (): void => {
    if (!statusEl) return;
    const extras = [workspaceNotice, overlayNotice, wireNotice, zoneNotice].filter((n): n is string => n !== null);
    const line = extras.length === 0 ? statusBase : `${statusBase} · ${extras.join(' · ')}`;
    // What the person is pointing at comes FIRST: the line is one line (ellipsis), so the
    // ambient part is what gets trimmed, never the reading they hovered for (2026-09-29).
    statusEl.textContent = bodyNotice === null ? line : `${bodyNotice} · ${line}`;
  };
  /** The overlay's own status voice — never touches application notices. */
  const noteOverlay = (notice: string | null, refusal = false): void => {
    overlayNotice = notice;
    if (notice !== null) console.warn(`[animator-v3] ${notice}`);
    if (refusal) revealStatus(); // a refusal on a hidden line is a silent one
    renderStatus();
  };
  /** The wireframe's status voice — its own slot, same fail-loud rule. */
  const noteWire = (notice: string | null, refusal = false): void => {
    wireNotice = notice;
    if (notice !== null) console.warn(`[animator-v3] ${notice}`);
    if (refusal) revealStatus();
    renderStatus();
  };
  /** The zones' status voice (ADR-031). A proposal reveals: it is a question put to a person. */
  const noteZones = (notice: string | null, reveal = false): void => {
    zoneNotice = notice;
    if (notice !== null) console.warn(`[animator-v3] ${notice}`);
    if (reveal) revealStatus();
    renderStatus();
    // A zone refusal lands on its flag (tailNotes) with no store change; the Body header reads it.
    bodyProgressListeners.forEach((listener) => listener());
  };
  /**
   * The Body cards' status voice (noteZones is its template). Hover and focus say a card's reading
   * here; an act says its result; `reveal` is for a REFUSAL only — a reading on a hidden line is
   * fine (the ` key shows it, and Expert shows it by default), a refusal on one is a silent one.
   * Only a revealed line reaches the console: a hover is not an event worth logging.
   */
  const noteBody = (line: string | null, reveal = false): void => {
    bodyNotice = line;
    if (reveal && line !== null) {
      console.warn(`[animator-v3] ${line}`);
      revealStatus();
    }
    renderStatus();
  };

  /**
   * A refusal on ONE status line, REASONS INCLUDED. The seam errors put their
   * header on the first line and every reason on the lines after
   * (TextureMasterError.reasons, GlbExportError.issues), and this line used to
   * keep only that header -- so a refusing export reached the field as
   * "texture-master refused:" with nothing after the colon, and the seam's own
   * fail-loud contract ("a caller surfaces them verbatim", ADR-020 section 7)
   * died at the last inch (Allen, 2026-09-01, on the deployed build). The
   * console keeps the whole error object either way; this is what a person reads.
   */
  const refusalLine = (err: unknown): string => {
    if (!(err instanceof Error)) return String(err);
    const head = err.message.split('\n')[0] ?? err.message;
    const listed = err as { readonly reasons?: readonly string[]; readonly issues?: readonly string[] };
    const reasons = listed.reasons ?? listed.issues;
    if (reasons === undefined || reasons.length === 0) return head;
    // Enough to act on, never a wall: the rest are one console line away.
    const shown = reasons.slice(0, 3).join(' - ');
    return reasons.length > 3 ? head + ' ' + shown + ' (+' + (reasons.length - 3) + ' more, see console)' : head + ' ' + shown;
  };

  const noteStatus = (notice: string | null, err?: unknown): void => {
    workspaceNotice = notice;
    if (notice === null) { renderStatus(); return; }
    if (err === undefined) console.warn(`[animator-v3] ${notice}`);
    else {
      console.warn(`[animator-v3] ${notice}`, err);
      // An error-bearing notice is a refusal speaking — it overrides the
      // hidden-by-default status line (fail-loud, LAWS).
      revealStatus();
    }
    renderStatus();
  };

  // ─── ADR-027 stage 1: the weight overlay's one control ─────────────────
  // W toggles it. With a bone selected (pose mode's selection) you see THAT
  // bone's heat; with none you see shared ownership — every bone a hue, the
  // thumb–palm web reading as the colour battle it is. Selection changes
  // repaint live, so walking the skeleton with the arrows sweeps the heat
  // bone to bone. Shift+W flips the D2 dial: heat floating over the texture
  // (0.85) vs Skinner's flat weight view (1.0). Refusals land on the status
  // line — a hidden refusal is a silent one.
  let overlayOn = false;
  let overlayShowing: string | null = null; // 'shared' | `bone:<name>` — repaint dedupe
  // The dedupe is keyed by SUBJECT too (Codex P2, #98): a load while shared
  // mode is showing swaps the scene — taking the old ghosts with it — and a
  // want-string that still reads 'shared' would return early, leaving the new
  // model bare while the control claims the overlay is on.
  let overlayShownScene: unknown = null;
  // The subject the subscriber last saw — release trigger for the OFF case.
  let overlaySubjectSeen: unknown = null;
  const repaintOverlay = (): void => {
    const liveScene = subject.run.scene;
    const swapped = overlaySubjectSeen !== null && overlaySubjectSeen !== liveScene;
    overlaySubjectSeen = liveScene;
    if (!overlayOn) {
      // Even HIDDEN, a replaced subject releases the old ghosts (Codex P2,
      // #98 round four): shown-once-then-hidden kept the detached model's
      // whole object graph reachable through the ghost array until W was
      // pressed again — a leak the GC cannot argue with.
      if (swapped && weightOverlay.probe().ghosts > 0) {
        weightOverlay.dispose();
        overlayShowing = null;
        overlayShownScene = null;
      }
      return;
    }
    const bone = store.getShell().selection[0] ?? null;
    const want = bone === null ? 'shared' : `bone:${bone}`;
    if (want === overlayShowing && subject.run.scene === overlayShownScene) return;
    // A driver's heat includes its DERIVED helpers (ADR-028 §6c): the crease
    // band's flesh was transferred to the `_corr` bone, which the pose
    // surface deliberately cannot select — without the union the elbow reads
    // as a hole in its own driver's heat (field find, 2026-08-21).
    const companions =
      bone === null ? [] : subject.run.hinges.filter((h) => h.profile.driver === bone).map((h) => h.helper);
    const refusal = bone === null ? weightOverlay.showShared() : weightOverlay.showBone(bone, companions);
    if (refusal !== null) {
      overlayShowing = null;
      overlayShownScene = null;
      // A refusal on a hidden-by-default line is a silent one (fail-loud;
      // Codex P2, #98) — noteOverlay reveals before it lands.
      noteOverlay(`weights: ${refusal}`, true);
      return;
    }
    overlayShowing = want;
    overlayShownScene = subject.run.scene;
    noteOverlay(
      bone === null
        ? 'weights: shared ownership — select a bone for its heat · W to hide'
        : `weights: ${bone}${companions.length > 0 ? ` (+ ${companions.join(', ')})` : ''} · W to hide`,
    );
  };
  /** The weight view on one bone — the soft-tissue card's "weights" (the W key's bone mode, reached from the card). */
  const showBoneWeights = (bone: string): void => {
    store.select([bone]);
    overlayOn = true;
    repaintOverlay();
  };
  window.addEventListener('keydown', (event) => {
    if (event.key.toLowerCase() !== 'w' || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.repeat) return; // same door as the wire's `4` (Codex P2, #135): a held key is one press
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.shiftKey) {
      if (!overlayOn) return; // the dial belongs to a visible overlay
      const flat = weightOverlay.probe().opacity >= 0.99;
      weightOverlay.setOpacity(flat ? 0.85 : 1.0);
      return;
    }
    overlayOn = !overlayOn;
    if (!overlayOn) {
      weightOverlay.hide();
      overlayShowing = null;
      noteOverlay(null); // clears only the overlay's slot — app notices stand
      return;
    }
    repaintOverlay();
  });
  // ADR-031 T0 — the wireframe, on Maya's wireframe key. `4` toggles; Shift+4
  // flips the x-ray between faint and hard. Keyed on `code` so the shifted
  // glyph (`$` on a US layout) still means the same key.
  let wireOn = false;
  let wireSubjectSeen: unknown = null;
  const refreshWire = (): void => {
    const liveScene = subject.run.scene;
    const swapped = wireSubjectSeen !== null && wireSubjectSeen !== liveScene;
    wireSubjectSeen = liveScene;
    if (!wireOn) {
      // A replaced subject releases the old ghosts even while hidden — the
      // weight overlay's leak (Codex P2, #98 round four), not repeated here.
      if (swapped && wireframeOverlay.probe().ghosts > 0) wireframeOverlay.dispose();
      return;
    }
    const refusal = wireframeOverlay.show(); // rebuilds only when the sources changed
    if (refusal !== null) {
      noteWire(`wireframe: ${refusal}`, true);
      return;
    }
    if (swapped || wireNotice === null) noteWire('wireframe · 4 to hide · Shift+4 faint/mid/hard');
  };
  window.addEventListener('keydown', (event) => {
    if (event.code !== 'Digit4' || event.altKey || event.ctrlKey || event.metaKey) return;
    // One physical press, one transition: a held key auto-repeats keydown and
    // would flip the wire on every repeat (Codex P2, #135).
    if (event.repeat) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.shiftKey) {
      if (!wireOn) return; // the dial belongs to a visible wire
      // Three stops — faint, mid, hard — because the field's first read was
      // "legible but heavy-handed" at 0.35 (Allen, 2026-09-02): the default
      // is now the faint stop, and the cage is two presses away.
      const now = wireframeOverlay.probe().opacity;
      wireframeOverlay.setOpacity(now < 0.3 ? 0.45 : now < 0.6 ? 0.8 : 0.2);
      return;
    }
    wireOn = !wireOn;
    if (!wireOn) {
      wireframeOverlay.hide();
      noteWire(null);
      return;
    }
    refreshWire();
  });

  // ───────── ADR-031 T1: zones — the tail proposed on load ──────────────────
  // On every new subject the unarmatured-flesh detector runs once; if the
  // Document declares no zones and the largest cluster is a TUBE, it becomes a
  // zone named "tail" with `measured-proposal` provenance, tinted on the
  // flesh, and its flyout opens with the affirmation questions (§9.3). A
  // Document that already speaks is shown, never re-proposed; a zone cut on a
  // different mesh refuses by name (D2) rather than lighting the wrong faces.
  const zoneTint = createZoneTint(() => subject.run.scene);
  const ZONE_MULT_DEFAULT = 2;
  const ZONE_HUES = {
    tail: [1, 0.72, 0.18],
    cloth: [0.35, 0.85, 1],
    softTissue: [1, 0.45, 0.62],
    hair: HAIR_HUE,
    none: [0.95, 0.95, 0.85],
  } as const satisfies Record<string, readonly [number, number, number]>;
  let zoneArraysFor: unknown = null;
  let zoneArrays: SubjectArrays | null = null;
  let tailArrays: SubjectArrays | null = null;
  let zoneFingerprint: MeshFingerprint | null = null;
  let zoneSubjectSeen: unknown = null;
  const zoneProposedFor = new WeakSet<object>();
  /** zone id → "R_ThighTwist01 53%, …" from the last measurement, for the flyout's evidence line. */
  const zoneOwnedBy = new Map<string, string>();
  let openZoneId: string | null = null;

  /**
   * `tail` is the same subject with the skin AS THE FILE PAINTED IT
   * (`tailDetectArrays`, adapter zones/file-paint): the run has re-owned the buttock and lower
   * back to the pelvis (ADR-029) by now, and the tail detector's owner cue
   * read that flesh as tail — the kobold's zone climbed 15 cm up the back
   * (2026-09-23). Same positions and faces, so the fingerprint is shared.
   */
  const zoneArraysNow = (): { arrays: SubjectArrays; tail: SubjectArrays; fingerprint: MeshFingerprint } | null => {
    const scene = subject.run.scene;
    if (zoneArraysFor !== scene) {
      zoneArraysFor = scene;
      try {
        zoneArrays = subjectArrays(scene);
        tailArrays = tailDetectArrays(scene);
        // A centred tail turned the rest flesh: zones stay valid against the mesh before the turn (run-document).
        zoneFingerprint = subject.run.zoneFingerprint ?? fingerprintMesh(zoneArrays.positions, zoneArrays.indices);
      } catch (err) {
        zoneArrays = null;
        tailArrays = null;
        zoneFingerprint = null;
        noteZones(`zones: ${refusalLine(err)}`, true);
      }
    }
    return zoneArrays === null || tailArrays === null || zoneFingerprint === null
      ? null
      : { arrays: zoneArrays, tail: tailArrays, fingerprint: zoneFingerprint };
  };
  /**
   * Every cluster the detector finds on this subject, seeded at the declared
   * multiplier and grown `rings` toward the body (the junction the seed
   * refuses — read #2), largest first. Measured once per mesh.
   */
  const ZONE_RINGS_DEFAULT = 2;
  const ownedByOf = (c: { dominantBones: readonly { bone: string; share: number }[] }): string =>
    c.dominantBones.map((d) => `${d.bone} ${(100 * d.share).toFixed(0)}%`).join(', ');
  /** What an offer needs from a measurement — an unarmatured cluster, or the tail detector's walked tube. */
  type OfferedRegion = {
    kind: 'tube' | 'sheet' | 'blob' | 'stub';
    faceIndices: Uint32Array;
    elongation: number;
    dominantBones: readonly { bone: string; share: number }[];
  };
  /**
   * A detected tail's detail — `+Nrings` so the flyout's wider / tighter reads
   * and steps it like any zone; `-Nrings` once tighter has shed rings from its
   * body side (a detected tail starts at 0, so tighter goes below it).
   */
  const TAIL_DETAIL = (rings: number, flat: NonNullable<ReturnType<typeof detectTail>['tail']>['flat'] = null, tips = false): string =>
    `tail@hips${rings < 0 ? '-' : '+'}${Math.abs(rings)}rings` +
    // EXTEND TIPS (a fan's other tails taken beyond the seam) is part of the measurement, replayed by every step.
    (tips ? '+tips' : '') +
    // A FLATTENED tail (a beaver's paddle) records its thin axis on the zone —
    // it bends across it, up and down (Allen, 2026-09-24; for the springs lane).
    (flat === null ? '' : ` flat ${flat.aspect.toFixed(1)}:1 thin=(${flat.thinAxis.x.toFixed(3)},${flat.thinAxis.y.toFixed(3)},${flat.thinAxis.z.toFixed(3)})`);
  /** How far tighter may go on a detected tail (the detector stops earlier, at half the walked tube). */
  const TAIL_RINGS_MIN = -12;
  const tailRegion = (t: NonNullable<ReturnType<typeof detectTail>['tail']>): OfferedRegion => ({
    kind: t.kind === 'stub' ? 'stub' : 'tube',
    faceIndices: t.faceIndices,
    // "N× long" on the card: length over the tube's diameter.
    elongation: t.slenderness / 2,
    dominantBones: t.dominantBones,
  });
  type Candidate = {
    key: number;
    cluster: OfferedRegion;
    ownedBy: string;
    fingerprint: MeshFingerprint;
    detail: string;
  };
  let candidatesCache: { for: unknown; list: Candidate[] } | null = null;
  /** The possible stub's offer key (tails are 0, clusters 1…N). */
  const STUB_KEY = -1;
  /** Subjects on which a person dismissed the possible stub — it stays dismissed for that subject. */
  const stubDismissed = new WeakSet<object>();
  /** The tail detector's report on this subject, measured with the candidates. */
  let tailReportCache: { for: unknown; report: ReturnType<typeof detectTail> } | null = null;
  /**
   * The detected tail this zone IS (half its faces or more), from this
   * subject's report — its fan count and how far off centre it sits.
   */
  const detectedTailOf = (zone: Zone): NonNullable<ReturnType<typeof detectTail>['tail']> | null => {
    candidatesNow();
    const report = tailReportCache?.for === subject.run.scene ? tailReportCache.report : null;
    const t = report === null ? null : zone.shape === 'stub' ? report.stub : report.tail;
    if (t === null || t === undefined || zone.faces.length === 0) return null;
    const mine = new Set(t.faceIndices);
    let shared = 0;
    for (const f of zone.faces) if (mine.has(f)) shared++;
    // Half of the smaller side: a zone EXTEND TIPS grew past the walked tail
    // (the fan's other tails) still IS that tail — and keeps its fan offer.
    return shared * 2 >= Math.min(zone.faces.length, t.faceIndices.length) ? t : null;
  };
  const candidatesNow = (): Candidate[] => {
    const scene = subject.run.scene;
    if (candidatesCache !== null && candidatesCache.for === scene) return candidatesCache.list;
    const now = zoneArraysNow();
    const list: Candidate[] = [];
    if (now !== null) {
      try {
        // THE TAIL is the tail detector's (Allen's priors, 2026-09-23): a tube
        // meeting the body behind the hips. Its verdict is said on every
        // subject — the tail, or the gate that refused the nearest candidate.
        const tails = detectTail(now.tail);
        tailReportCache = { for: scene, report: tails };
        console.info(`[animator-v3] zones: ${tails.summary}`);
        if (tails.tail !== null) {
          list.push({
            key: 0,
            cluster: tailRegion(tails.tail),
            ownedBy: ownedByOf(tails.tail),
            fingerprint: now.fingerprint,
            detail: TAIL_DETAIL(0, tails.tail.flat),
          });
        } else if (tails.stub !== null && !stubDismissed.has(scene)) {
          // The LOWER confidence tier: a possible stub tail, offered to pin or
          // dismiss — never grown on its own (Allen, 2026-09-24).
          list.push({
            key: STUB_KEY,
            cluster: tailRegion(tails.stub),
            ownedBy: ownedByOf(tails.stub),
            fingerprint: now.fingerprint,
            detail: TAIL_DETAIL(0, null),
          });
        }
        const tailFaces = new Set(tails.tail?.faceIndices ?? []);
        const report = detectUnarmatured(now.arrays, { thresholdMultiplier: ZONE_MULT_DEFAULT, growRings: ZONE_RINGS_DEFAULT });
        report.clusters.forEach((cluster, i) => {
          // A bounding-box "tube" is no longer a tail offer: the tail detector
          // walked every tube near the hips and said which is one (the chest
          // folds a tiger's jacket makes are not). A cluster that IS the
          // detected tail (half its faces) is that offer already.
          if (cluster.kind === 'tube') return;
          let shared = 0;
          for (const f of cluster.faceIndices) if (tailFaces.has(f)) shared++;
          if (shared * 2 >= cluster.faceIndices.length && cluster.faceIndices.length > 0) return;
          list.push({
            key: i + 1,
            cluster,
            ownedBy: ownedByOf(cluster),
            fingerprint: now.fingerprint,
            detail: `unarmatured@${ZONE_MULT_DEFAULT}×+${ZONE_RINGS_DEFAULT}rings`,
          });
        });
      } catch (err) {
        noteZones(`zones: ${refusalLine(err)}`, true);
      }
    }
    candidatesCache = { for: scene, list };
    return list;
  };
  /**
   * The re-measured cluster that IS this zone: the one sharing the most faces
   * with it, whatever its kind. The first cut took the globally largest tube,
   * which on a Document with two zones would have overwritten a cloth zone
   * with tail faces (Codex P2, #136).
   */
  /** Was this zone's last measurement the tail detector's? (Then its rings step around the walked tube.) */
  const zoneIsDetectedTail = (zone: Zone): boolean =>
    [...zone.sources].reverse().find((s) => s.kind === 'measured-proposal')?.detail.startsWith('tail@') === true;
  const remeasureZone = (zone: Zone, rings: number, tips = zoneTips(zone)): { cluster: OfferedRegion; ownedBy: string; detail: string } | null => {
    const now = zoneArraysNow();
    if (now === null) return null;
    if (zoneIsDetectedTail(zone)) {
      // A detected tail steps by rings around its OWN walked tube — toward the body only — and keeps its tips.
      try {
        const r = detectTail(now.tail, { growRings: rings, extendTips: tips });
        const t = zone.shape === 'stub' ? r.stub : r.tail;
        // The detail records the rings the detector TOOK (a shed stops before the tail is gone).
        return t === null ? null : { cluster: tailRegion(t), ownedBy: ownedByOf(t), detail: TAIL_DETAIL(t.rings, t.flat, tips) };
      } catch (err) {
        noteZones(`zones: ${refusalLine(err)}`, true);
        return null;
      }
    }
    try {
      const report = detectUnarmatured(now.arrays, { thresholdMultiplier: ZONE_MULT_DEFAULT, growRings: rings });
      const have = new Set(zone.faces);
      let best: (typeof report.clusters)[number] | null = null;
      let bestShared = 0;
      for (const c of report.clusters) {
        let shared = 0;
        for (const f of c.faceIndices) if (have.has(f)) shared++;
        if (shared > bestShared) {
          bestShared = shared;
          best = c;
        }
      }
      if (best === null) return null;
      return { cluster: best, ownedBy: ownedByOf(best), detail: `unarmatured@${ZONE_MULT_DEFAULT}×+${rings}rings` };
    } catch (err) {
      noteZones(`zones: ${refusalLine(err)}`, true);
      return null;
    }
  };
  /** Did the zone's last measurement take a fan's tips (`+tips`)? */
  const zoneTips = (zone: Zone): boolean =>
    [...zone.sources].reverse().find((s) => s.kind === 'measured-proposal')?.detail.includes('+tips') === true;
  /** `+2rings` / `-1rings` → the signed count (tighter on a detected tail goes below zero). */
  const RINGS = /([+-]\d+)rings/;
  /** How many rings the zone's last measurement grew by (negative: shed), or null when a person reshaped it. */
  const zoneRings = (zone: Zone): number | null => {
    const last = zone.sources[zone.sources.length - 1];
    if (last === undefined || last.kind !== 'measured-proposal') {
      const measured = [...zone.sources].reverse().find((s) => s.kind === 'measured-proposal');
      if (measured === undefined) return null;
      const m = RINGS.exec(measured.detail);
      return m === null ? 0 : Number(m[1]);
    }
    const m = RINGS.exec(last.detail);
    return m === null ? 0 : Number(m[1]);
  };
  /** T2: a line per zone on the bar — a refusal's sentence or "placing…"; cleared when the chain lands. */
  const tailNotes = new Map<string, string>();
  /** The zones whose latest tail note is a REFUSAL (the Body header's `check`), kept beside the note, never parsed from it. */
  const tailRefused = new Set<string>();
  /**
   * The session-only preview wag (T2b-min). No toggle (Allen's second field
   * read: "it should simply be pin like the others, then the wag should happen
   * on preview no matter the animation"): once a chain exists it wags on every
   * frame, over whatever clip plays — never Document state, never exported.
   */
  const tailWag = createTailWag();
  /**
   * ADR-038: the tail spring (the VRM reference solver over the chain), LAYERED
   * (R5, ruled 2026-09-24): the mood — the wag among them — is the spring's
   * target and physics plays around it; off, the mood plays kinematically.
   * Its settings are the Document's `edits.tailSpring` (R3), set by one verb.
   */
  // GROUND CONTACT: the viewport's floor (its shadow catcher, at the character's feet), read once per rendered frame.
  let floorY = 0;
  let floorFrame = -1;
  const tailSpring = createTailSpring({
    document: () => current(store.getHistory()).edits.tailSpring,
    commit: (settings) => store.dispatch({ schemaVersion: '1.0.0', verb: 'tailSpring.set', params: { settings } }),
    floorY: () => {
      if (floorFrame !== viewport.probe.frames) {
        floorFrame = viewport.probe.frames;
        floorY = viewport.lensEvidence().catcherY;
      }
      return floorY;
    },
  });
  /**
   * ADR-038 J2: the soft-tissue leaves' springs (one per grafted region), the
   * tail spring's sibling in the same render-preview slot. Off in pose mode.
   */
  const softTissue = createSoftTissueSprings();
  /** J4's surface jiggle: rides the bounce leaves' frame, after them (`?j4=off` disables). */
  const surfaceJiggle = createSurfaceJiggle();
  /** The soft-tissue springs' extras for the export: the bounce's `jiggle` block and, beside it, J4's `surface` block (§14.5). */
  const springExtrasForExport = (): Record<string, Record<string, unknown>> => {
    const out: Record<string, Record<string, unknown>> = { ...softTissue.extrasForExport(subject.run) };
    const controlsOf = (region: JiggleRegionId) => softTissue.views(subject.run).find((v) => v.region === region)?.controls ?? null;
    for (const [name, extra] of Object.entries(surfaceJiggle.extrasForExport(subject.run, controlsOf))) out[name] = { ...(out[name] ?? {}), ...extra };
    return out;
  };
  if (runOptions.surfaceJiggle === true) console.info('[animator-v3] J4 dials', JSON.stringify(surfaceJiggle.tune(j4Dials())));
  const softTissueSpringing = (): boolean => softTissue.playing(subject.run) && store.getShell().mode !== 'pose';
  /** The region detector's reading, per subject and zone state — measured once, not per render. */
  const softTissueMeasured = new WeakMap<object, { key: string; value: ReturnType<typeof measureSoftTissue> | string }>();
  const softTissueView = (): SoftTissuePanelView => {
    const run = subject.run;
    const doc = store.getDocument();
    const zones = (doc.zones ?? []).filter((z) => z.intent === 'softTissue').map((z) => ({ id: z.id, name: z.name }));
    const key = JSON.stringify([(doc.zones ?? []).map((z) => [z.id, z.intent ?? null, z.faces.length]), doc.target.softTissue ?? []]);
    let measured = softTissueMeasured.get(run.scene);
    if (measured === undefined || measured.key !== key) {
      let value: ReturnType<typeof measureSoftTissue> | string;
      try {
        value = measureSoftTissue(doc, run.scene, run.rig);
      } catch (err) {
        value = err instanceof Error ? err.message : String(err);
      }
      measured = { key, value };
      softTissueMeasured.set(run.scene, measured);
    }
    const reading = measured.value;
    const report = typeof reading === 'string' ? null : reading.report;
    const bust = nativeBustBones(run.rig);
    const springs = softTissue.views(run);
    const declared = doc.target.softTissue ?? [];
    const cards: SoftTissueCardView[] = SOFT_TISSUE_REGIONS.map((region): SoftTissueCardView => {
      const graft = run.softTissueGrafts.find((g) => g.region === region);
      const spring = springs.find((s) => s.region === region) ?? null;
      const zoneId = declared.find((d) => d.region === region)?.zoneId ?? null;
      const native = region === 'LeftBreast' ? bust.left : region === 'RightBreast' ? bust.right : [];
      if (graft !== undefined) {
        return {
          region, state: 'grown', tier: graft.tier, zones, zoneId, spring, leafBone: graft.bone,
          reading: `Leaf on ${graft.parent}, ${(graft.length * 100).toFixed(1)} cm root to apex, ${graft.verticesWeighted} of ${graft.vertices} vertices weighted${graft.zoneId !== null ? `, over zone ${graft.zoneId}` : ''}.`,
        };
      }
      if (spring !== null && spring.source === 'file') {
        return { region, state: 'file', tier: null, zones, zoneId, spring, leafBone: spring.bone, reading: `The file wears this leaf (${spring.bone}) and declares its spring.` };
      }
      if (native.length > 0) {
        return { region, state: 'native', tier: null, zones, zoneId, spring: null, leafBone: null, reading: `This body wears its own bust bones (${native.join(', ')}): its breast spring. No leaf is grafted over them.` };
      }
      const r = report?.regions.find((x) => x.id === region);
      if (r === undefined) {
        const why = typeof reading === 'string' ? reading : (report?.unmeasured.find((u) => u.id === region)?.why ?? 'not measured');
        return { region, state: 'unmeasured', tier: null, zones, zoneId, spring: null, leafBone: null, reading: `Not measured: ${why}` };
      }
      return { region, state: r.tier === 'none' ? 'quiet' : 'offered', tier: r.tier, zones, zoneId, spring: null, leafBone: null, reading: r.reading };
    });
    // The standing note ("Bounce is offered where…") is gone (XD 2026-09-29 §3B): help, not state —
    // its words ride the Add bounce tip (soft-tissue-panel.ts).
    return { cards };
  };
  let springLastFrame: number | null = null;
  const tailSpringing = (): boolean => tailSpring.playing(subject.run) && store.getShell().mode !== 'pose';
  /** Does the preview wag play right now? */
  const tailWagging = (): boolean => subject.run.tailGrafts.length > 0 && store.getShell().mode !== 'pose' && !tailSpring.playing(subject.run);
  /** This frame's wag over the live chain, or null — handed to the render-preview slot. */
  let wagFrameDt = 0;
  const applyTailWag = (): (() => void) | null => {
    // Pose mode is for placing bones by hand: a chain swinging under the gizmo
    // would fight the person holding it, so the preview rests there.
    if (!tailWagging()) return null;
    const graft = subject.run.tailGrafts[0];
    if (graft === undefined) return null;
    const found = graft.bones.map((n) => subject.run.scene.getObjectByName(n));
    const bones = found.filter((b): b is NonNullable<(typeof found)[number]> => b !== undefined);
    if (bones.length === 0) return null;
    // ADR-038 R5: the preview wag is the tail drive's mood played without physics.
    const drive = tailSpring.kinematicOffsets(subject.run, wagFrameDt);
    return tailWag.apply(bones, performance.now() / 1000, drive !== null && drive.bones.length === bones.length ? drive.offsets : undefined);
  };
  /** The tail commit door lives with the elbow projection (declared later); filled in there. */
  let commitTailRef: ((command: unknown) => Promise<void>) | null = null;
  const tailSummary = (run: Subject['run']): string | null => {
    const t = run.tailGrafts[0];
    if (t === undefined) return 'tail: the chain is withdrawn — the zone stays';
    return (
      `tail: "${t.zone}" — ${t.bones.length} bones (${t.count.provenance === 'default' ? 'from its length' : `its length says ${t.defaultCount}`}) · ` +
      `${(t.length * 100).toFixed(0)} cm · owns ${t.verticesOwned} vertices, ${t.verticesBlended} in the seam, ${t.verticesRetained} left to the body`
    );
  };
  /**
   * A detected tail's evidence on the bar: a FAN refuses one chain before it
   * is asked for (Allen's kitsune, 2026-09-24 — five tails, one zone, one
   * chain down one of them), and a single tail says how far off centre it
   * sits (the illustrator's sideways sweep; the re-centre is designed, not
   * built — docs/design/TAIL_RECENTER_DESIGN_NOTE.md).
   */
  const tailEvidence = (zone: Zone): Pick<ZoneFlyoutState, 'fanRefusal' | 'centring' | 'centreOffer'> => {
    const t = detectedTailOf(zone);
    // A fan Grow found (a wand-made zone has no detector reading) — valid while the faces are the ones measured.
    const declared = (current(store.getHistory()).target.tails ?? []).find((d) => d.zoneId === zone.id);
    // A wand-made zone has no detector reading: its sweep is measured on the chain it would grow
    // (Penny, 2026-09-26: "it didn't offer the centre and bone option"). Measuring may FIND a fan.
    const measured = t === null && zone.intent === 'tail' && zone.shape !== 'stub' ? measuredCentring(zone, false) : null;
    const offerOf = (o: { degrees: number; side: string } | null) =>
      o !== null && o.degrees >= CENTRE_OFFER_DEG && declared?.recentre === undefined ? o : null;
    const found = fansFoundAtGrow.get(zone.id);
    if (found !== undefined && sameFaces(found.faces, zone.faces) && (t === null || t.fan <= 1)) {
      return { fanRefusal: tailFanRefusal(found.tails), centring: null, centreOffer: offerOf(measuredCentring(zone, true)) };
    }
    if (t === null) return { fanRefusal: null, centring: null, centreOffer: offerOf(measured) };
    if (t.fan > 1) return { fanRefusal: tailFanRefusal(t.fan), centring: null, centreOffer: offerOf(measuredCentring(zone, true)) };
    const c = t.centring;
    return {
      // A single tail swept past the threshold, not yet centred (never a fan, never a stub).
      centreOffer:
        t.kind === 'tube' && c.angleDeg >= CENTRE_OFFER_DEG && declared?.recentre === undefined ? { degrees: c.angleDeg, side: c.side } : null,
      fanRefusal: null,
      centring:
        c.angleDeg < 5
          ? 'sits centred behind the hips'
          : `sits ${c.angleDeg.toFixed(0)}° off centre, swept to the character's ${c.side}`,
    };
  };
  /**
   * Fans found only when Grow refused (Penny, 2026-09-26: a wand-made zone, no
   * detector reading, so the bar offered Grow, Grow refused, and "Group as one
   * tail" never appeared). Keyed by zone, pinned to the faces measured — a ➕/➖
   * that changes them drops the finding and Grow is offered again.
   */
  const fansFoundAtGrow = new Map<string, { readonly faces: Zone['faces']; readonly tails: number }>();
  const sameFaces = (a: Zone['faces'], b: Zone['faces']): boolean => a === b || (a.length === b.length && a.every((f, i) => f === b[i]));
  /**
   * A zone's sweep measured on the chain it would grow (grouped for a fan), for zones
   * the detector never read. One placement per zone + faces + grouping, cached. A fan
   * met while measuring ungrouped is recorded as Grow would record it; any other
   * refusal is no offer (Grow will say it).
   */
  const centringCache = new Map<string, { readonly faces: Zone['faces']; readonly offer: { degrees: number; side: string } | null }>();
  const measuredCentring = (zone: Zone, grouped: boolean): { degrees: number; side: string } | null => {
    const key = `${zone.id}|${grouped}`;
    const hit = centringCache.get(key);
    if (hit !== undefined && sameFaces(hit.faces, zone.faces)) return hit.offer;
    let offer: { degrees: number; side: string } | null = null;
    const frame = tailReportCache?.report.frame;
    const now = zoneArraysNow();
    if (frame !== null && frame !== undefined && now !== null) {
      try {
        const placed = placeTail({
          mesh: now.arrays,
          faces: zone.faces,
          zoneName: zone.name,
          zoneKind: zone.shape ?? 'tail',
          ...tailShapeOptions(zone, now.arrays.positions),
          ...(grouped ? { fan: 'grouped' as const } : {}),
        });
        const j = placed.jointsWorld;
        const k = j[Math.max(1, Math.round((j.length - 1) / 3))]!;
        // Lateral = up x forward: the character's LEFT (the detector's centringOf).
        const lateral = (k.x - j[0]!.x) * frame.forward.z - (k.z - j[0]!.z) * frame.forward.x;
        offer = { degrees: Math.abs(chainCentringYaw(j, frame.forward)), side: lateral > 0 ? 'left' : 'right' };
      } catch (err) {
        if (err instanceof TailFanRefusal && !grouped) fansFoundAtGrow.set(zone.id, { faces: zone.faces, tails: err.tails });
      }
    }
    centringCache.set(key, { faces: zone.faces, offer });
    return offer;
  };
  /** Swept at least this far off the midline, a single tail is offered "centre" (the kobold's natural 15° is a sweep too). */
  const CENTRE_OFFER_DEG = 12;
  const zoneFlyoutState = (zone: Zone): ZoneFlyoutState => {
    const declared = (current(store.getHistory()).target.tails ?? []).find((t) => t.zoneId === zone.id);
    const placed = subject.run.tailGrafts.find((t) => t.zoneId === zone.id);
    return {
      name: zone.name,
      faces: zone.faces.length,
      shape: zone.shape ?? null,
      // a hair zone never reaches the flyout (the Hair card owns it — zonesForThisMesh); read as unanswered if it did
      intent: zone.intent === 'hair' ? null : (zone.intent ?? null),
      rings: zoneRings(zone),
      reachKind: zoneIsDetectedTail(zone) ? 'tail' : 'region',
      wand: wandMode,
      // Extend tips is offered on a FAN only: a single tail has nothing beyond its seam (the kobold: 165 vertices of flank).
      tips: zoneIsDetectedTail(zone) && ((detectedTailOf(zone)?.fan ?? 1) > 1 || zoneTips(zone)) ? zoneTips(zone) : null,
      ownedBy: zoneOwnedBy.get(zone.id) ?? '',
      tail:
        declared === undefined
          ? null
          : {
              bones: declared.count.value,
              provenance: declared.count.provenance,
              defaultCount: placed?.defaultCount ?? null,
              lengthCm: placed === undefined ? null : placed.length * 100,
              pivotPlaced: declared.pivot !== undefined,
            },
      tailNote: tailNotes.get(zone.id) ?? null,
      ...tailEvidence(zone),

    };
  };
  /**
   * T2 — grow, re-count or withdraw the chain (D4 at the object). "grow"
   * measures the default count here, on the live mesh, so the Document stores
   * the NUMBER the rule chose with `default` provenance (§8: a default is a
   * declaration); more / fewer store the person's number as `declared`. Every
   * path goes through the tail commit door, which places off stage first and
   * throws the refusal back here to be rendered on the bar.
   */
  /** The turn that centres this zone's tail: measured on its placed chain, declared once, replayed after. */
  const centringDegrees = (zone: Zone, arrays: Parameters<typeof placeTail>[0]['mesh'], grouped = false): number => {
    const frame = tailReportCache?.report.frame;
    if (frame === null || frame === undefined) throw new Error("the rig's facing is not measured on this subject — nothing to centre against");
    // A grouped fan is measured grouped: ungrouped it refuses as the fan it is.
    const placed = placeTail({ mesh: arrays, faces: zone.faces, zoneName: zone.name, zoneKind: zone.shape ?? 'tail', ...(grouped ? { fan: 'grouped' as const } : {}) });
    return chainCentringYaw(placed.jointsWorld, frame.forward);
  };
  const onTailAction = (action: 'grow' | 'grow-centred' | 'group' | 'group-centred' | 'centre' | 'more' | 'fewer' | 'reset-root' | 'withdraw', trusted: boolean): void => {
    if (!trusted || openZoneId === null || commitTailRef === null) return;
    const zone = zoneById(openZoneId);
    if (zone === undefined) return;
    // A root placement still rebuilding owns the tail's declaration: a second edit started now would
    // race it from the same Document and one would be refused (Codex P2, #253). Reset joins its queue.
    if (action !== 'reset-root' && rootBusy(zone)) return;
    const declared = (current(store.getHistory()).target.tails ?? []).find((t) => t.zoneId === zone.id);
    const say = (note: string | null, refused = false): void => {
      if (note === null) tailNotes.delete(zone.id);
      else tailNotes.set(zone.id, note);
      if (refused) tailRefused.add(zone.id);
      else tailRefused.delete(zone.id);
      if (openZoneId === zone.id) zoneFlyout.update(zoneFlyoutState(zone));
      bodyProgressListeners.forEach((listener) => listener());
    };
    let command: unknown;
    try {
      if (action === 'withdraw') {
        command = { schemaVersion: '1.0.0', verb: 'tail.withdraw', params: { zoneId: zone.id } };
      } else if (action === 'reset-root' && declared !== undefined) {
        // PLACE THE ROOT: back to the measured root — through the handle's placement queue, so a reset
        // pressed while a drag is still rebuilding lands after it, never racing it (Codex P2, #253).
        placeTailRoot(null, zone.id);
        return;
      } else if (action === 'centre' && declared !== undefined) {
        const now = zoneArraysNow();
        if (now === null) throw new Error('this subject has no sole skinned mesh to measure');
        command = {
          schemaVersion: '1.0.0',
          verb: 'tail.declare',
          params: { ...declared, recentre: { degrees: centringDegrees(zone, now.arrays, declared.fan === 'grouped') }, confirmedBy: 'human:local' },
        };
      } else if (action === 'grow' || action === 'grow-centred' || action === 'group' || action === 'group-centred' || action === 'centre' || declared === undefined) {
        const grouped = action === 'group' || action === 'group-centred' || declared?.fan === 'grouped';
        const now = zoneArraysNow();
        if (now === null) throw new Error('this subject has no sole skinned mesh to measure');
        const measured = placeTail({
          mesh: now.arrays,
          faces: zone.faces,
          zoneName: zone.name,
          zoneKind: zone.shape ?? 'tail',
          ...(zone.shape === 'stub' ? { stub: true } : {}),
          ...tailShapeOptions(zone, now.arrays.positions),
          ...(grouped ? { fan: 'grouped' as const } : {}),
        });
        command = {
          schemaVersion: '1.0.0',
          verb: 'tail.declare',
          params: {
            zoneId: zone.id,
            count: { value: measured.stations.count.value, provenance: 'default' },
            ...(grouped ? { fan: 'grouped' } : {}),
            ...(action === 'grow-centred' || action === 'group-centred' || action === 'centre' ? { recentre: { degrees: centringDegrees(zone, now.arrays, grouped) } } : {}),
            confirmedBy: 'human:local',
          },
        };
      } else {
        const value = declared.count.value + (action === 'more' ? 1 : -1);
        command = {
          schemaVersion: '1.0.0',
          verb: 'tail.declare',
          params: { ...declared, count: { value, provenance: 'declared' }, confirmedBy: 'human:local' },
        };
      }
    } catch (err) {
      if (err instanceof TailFanRefusal) {
        // The bar turns into the fan row (the refusal + "Group as one tail"), not a dead-end note.
        fansFoundAtGrow.set(zone.id, { faces: zone.faces, tails: err.tails });
        say(null);
      } else {
        say(refusalLine(err), true);
      }
      noteZones(`tail: ${refusalLine(err)}`, true);
      return;
    }
    say(action === 'withdraw' ? 'withdrawing the chain…' : 'placing the chain…');
    commitTailRef(command).then(
      () => say(null),
      (err: unknown) => {
        say(refusalLine(err), true);
        noteZones(`tail: ${refusalLine(err)}`, true);
      },
    );
  };
  /** The detector's OFFERS not yet covered by a zone on this mesh — a candidate sharing half its faces with a zone is that zone. */
  const uncoveredCandidates = (): Candidate[] => {
    const { mine, hair: hairZones } = zonesForThisMesh();
    const covered = [...mine, ...hairZones].map((z) => new Set(z.faces));
    // A blob (the tiger's mane crown) is nothing to propose (ADR-031 §3.2) —
    // tubes and sheets are the offers; the wand is the door for the rest.
    return candidatesNow().filter((c) => {
      if (c.cluster.kind === 'blob') return false;
      for (const faces of covered) {
        let shared = 0;
        for (const f of c.cluster.faceIndices) if (faces.has(f)) shared++;
        if (shared * 2 >= c.cluster.faceIndices.length) return false;
      }
      return true;
    });
  };
  const zoneById = (id: string): Zone | undefined => (current(store.getHistory()).zones ?? []).find((z) => z.id === id);
  /**
   * The Document's zones split by WHICH MESH they were cut on (D2). A dropped
   * file spreads the previous Document into the new one, so the boot
   * character's zone rides along into the tiger's Document — it must neither
   * be shown there nor silence the tiger's own proposal (the field read,
   * 2026-09-02: "the flag options disappeared for the tail"). Stale zones are
   * kept — Document data is never dropped silently — and mentioned once.
   */
  const zonesForThisMesh = (): { mine: Zone[]; stale: Zone[]; hair: Zone[] } => {
    const zones = current(store.getHistory()).zones ?? [];
    const now = zoneArraysNow();
    if (now === null) return { mine: [], stale: [...zones], hair: [] };
    const mine: Zone[] = [];
    const stale: Zone[] = [];
    // ADR-039: the hair zone is the Hair card's (its own tint, its own verbs) — never a Tails & cloth card,
    // but it still COVERS its faces, so the detector does not re-offer a column of hair as a tail.
    const hair: Zone[] = [];
    for (const z of zones) (fingerprintMismatch(z.meshFingerprint, now.fingerprint) !== null ? stale : z.intent === 'hair' ? hair : mine).push(z);
    return { mine, stale, hair };
  };
  let staleNotedFor: unknown = null;
  /** The tail's root handle follows the open flag (assigned once the handle exists, below). */
  let syncTailRoot: () => void = () => undefined;
  const closeZoneFlyout = (): void => {
    disarmWand();
    openZoneId = null;
    zoneFlyout.close();
    syncTailRoot();
  };
  // ---- THE WAND (ADR-031 §3.2 T3; Allen, 2026-09-25) ----
  let wandMode: 'add' | 'remove' | null = null;
  /** The body's skeleton distance per vertex, per subject — grown tail bones left out (the cue is the BODY). */
  let wandArmature: { for: unknown; armature: Float32Array; radius: number } | null = null;
  const disarmWand = (): void => {
    if (wandMode === null) return;
    wandMode = null;
    viewport.setFacePicker(null);
  };
  subjectChanged.add(disarmWand);
  /** The wand's cue and brush for this subject, measured once per scene. */
  const wandCue = (now: NonNullable<ReturnType<typeof zoneArraysNow>>): NonNullable<typeof wandArmature> => {
    const scene = subject.run.scene;
    if (wandArmature?.for !== scene) {
      // grown tail bones and the hair's own leaf are not the body (the hair wand must not read its hair as on-bone)
      const body = now.arrays.bones.filter((b) => !/^Tail\d+/i.test(b.name) && !/^HairJiggle/.test(b.name));
      // Brush: 5% of the body's height, measured along the surface.
      wandArmature = { for: scene, armature: nearestSegmentDistances(now.arrays.positions, body), radius: 0.05 * meshHeight(now.arrays.positions) };
    }
    return wandArmature;
  };
  // ---- A NEW ZONE BY WAND (Allen, 2026-09-25: the bear girl's bob tail) ----
  let newZoneArmed = false;
  const disarmNewZone = (): void => {
    if (!newZoneArmed) return;
    newZoneArmed = false;
    viewport.setFacePicker(null);
  };
  subjectChanged.add(disarmNewZone);
  const armNewZone = (): void => {
    if (newZoneArmed) {
      disarmNewZone();
      noteZones('new zone: wand put away');
      return;
    }
    closeZoneFlyout();
    try {
      viewport.setFacePicker((face) => newZoneAt(face));
      newZoneArmed = true;
      noteZones('new zone: click the model where the zone is (press ➕ new zone again to put the wand away)');
    } catch (err) {
      noteZones(`new zone: ${refusalLine(err)}`, true);
    }
  };
  const newZoneAt = (face: number): void => {
    disarmNewZone();
    const now = zoneArraysNow();
    if (now === null) return;
    const cue = wandCue(now);
    try {
      const r = wandStroke({ positions: now.arrays.positions, indices: now.arrays.indices, armature: cue.armature }, [], { mode: 'add', face, radius: cue.radius });
      // Its SIZE decides the offer's shape, as the detector's own length gate does:
      // a patch shorter than 15% of the height is a stub (one or two bones).
      const P = now.arrays.positions;
      const lo = [Infinity, Infinity, Infinity];
      const hi = [-Infinity, -Infinity, -Infinity];
      for (const f of r.faces) {
        for (let k = 0; k < 3; k++) {
          const v = now.arrays.indices[f * 3 + k]!;
          for (let c = 0; c < 3; c++) {
            lo[c] = Math.min(lo[c]!, P[v * 3 + c]!);
            hi[c] = Math.max(hi[c]!, P[v * 3 + c]!);
          }
        }
      }
      const extent = Math.max(hi[0]! - lo[0]!, hi[1]! - lo[1]!, hi[2]! - lo[2]!);
      const shape: 'stub' | 'tube' = extent < 0.15 * meshHeight(P) ? 'stub' : 'tube';
      const zones = current(store.getHistory()).zones ?? [];
      const id = `z${zones.reduce((n, z) => Math.max(n, Number(z.id.slice(1))), 0) + 1}`;
      const taken = new Set(zones.map((z) => z.name));
      let name = 'tail';
      for (let n = 2; taken.has(name); n++) name = `tail ${n}`;
      store.dispatch({
        schemaVersion: '1.0.0',
        verb: 'zone.create',
        params: {
          zone: {
            id,
            name,
            faces: r.faces,
            meshFingerprint: now.fingerprint,
            sources: [{ kind: 'human-edit', detail: `wand-new face=${face} r=${cue.radius.toFixed(3)}` }],
            shape,
          },
        },
      });
      noteZones(`zone: "${name}" made by the wand — ${r.faces.length} faces, ${(extent * 100).toFixed(1)} cm across, offered as a ${shape === 'stub' ? 'stub tail' : 'tail'} · answer on its flag, refine with ➕ / ➖`);
      openZone(id);
    } catch (err) {
      noteZones(`new zone: ${refusalLine(err)}`, true);
    }
  };
  /**
   * A STUB whose faces now reach the tail length gate becomes a tail — at EVERY door
   * that sets a zone's faces (a law needs every door). Penny, 2026-09-25: ➕ at the
   * hair's tip must not grow a one-bone stub; 2026-09-26: Allen grew the same stub
   * with WIDER (+3…+8 rings, 40 cm) and it stayed a stub — two bones, no fan read,
   * no centre offer — because only the ➕ door carried the rule.
   */
  const stubGrownToTail = (zone: Zone, faces: ArrayLike<number>, arrays: { positions: ArrayLike<number>; indices: ArrayLike<number> }): 'tube' | undefined => {
    if (zone.shape !== 'stub') return undefined;
    const P = arrays.positions;
    const lo = [Infinity, Infinity, Infinity];
    const hi = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < faces.length; i++) for (let k = 0; k < 3; k++) { const v = arrays.indices[faces[i]! * 3 + k]!; for (let c = 0; c < 3; c++) { lo[c] = Math.min(lo[c]!, P[v * 3 + c]!); hi[c] = Math.max(hi[c]!, P[v * 3 + c]!); } }
    return Math.max(hi[0]! - lo[0]!, hi[1]! - lo[1]!, hi[2]! - lo[2]!) >= 0.15 * meshHeight(P) ? 'tube' : undefined;
  };
  const wandStrokeAt = (face: number): void => {
    if (openZoneId === null || wandMode === null) return;
    const zone = zoneById(openZoneId);
    const now = zoneArraysNow();
    if (zone === undefined || now === null) return;
    if (rootBusy(zone)) return;
    wandCue(now);
    if (wandArmature === null) return;
    const mode = wandMode;
    try {
      const r = wandStroke(
        { positions: now.arrays.positions, indices: now.arrays.indices, armature: wandArmature.armature },
        zone.faces,
        { mode, face, radius: wandArmature.radius },
      );
      if (r.faces.length === zone.faces.length && r.changed === 0) {
        noteZones(`wand: nothing to ${mode === 'add' ? 'add' : 'remove'} there — that face is ${mode === 'add' ? 'already in' : 'not in'} "${zone.name}"`, true);
        return;
      }
      if (r.faces.length === 0) {
        noteZones(`wand: that would remove all of "${zone.name}" — use remove zone instead`, true);
        return;
      }
      const reshape = stubGrownToTail(zone, r.faces, now.arrays);
      store.dispatch({
        schemaVersion: '1.0.0',
        verb: 'zone.setFaces',
        params: {
          id: zone.id,
          faces: r.faces,
          source: { kind: 'human-edit', detail: `wand${mode === 'add' ? '+' : '-'} face=${face} r=${wandArmature.radius.toFixed(3)}` },
          ...(reshape !== undefined ? { shape: reshape } : {}),
        },
      });
      if (reshape !== undefined) noteZones(`zone: "${zone.name}" is long enough to be a tail now — grows as a chain, not a stub`);
      noteZones(
        `wand: "${zone.name}" ${mode === 'add' ? 'took' : 'gave back'} ${Math.abs(r.faces.length - zone.faces.length)} faces — ${r.faces.length} now` +
          (r.islandsDropped > 0 ? ` (and ${r.islandsDropped} body island${r.islandsDropped === 1 ? '' : 's'} it left)` : ''),
      );
    } catch (err) {
      noteZones(`wand: ${refusalLine(err)}`, true);
    }
  };
  const zoneFlyout = createZoneFlyout(document.body, {
    screenXY: () => {
      if (openZoneId === null) return null;
      const world = zoneTint.anchorWorld(openZoneId);
      return world === null ? null : viewport.projectPoint(world);
    },
    onIntent: (intent, trusted) => {
      // The pin gesture's rule (ADR-022 §9): only a person can declare. A forged
      // click reaches the Document as nothing.
      if (!trusted || openZoneId === null) return;
      const id = openZoneId;
      try {
        store.dispatch({
          schemaVersion: '1.0.0',
          verb: 'zone.confirm',
          params: { id, intent, confirmedBy: 'human:local' },
        });
      } catch (err) {
        // T2: a zone carrying a chain cannot become cloth underneath it — the
        // gate says so by name, and the bar is where the person is looking.
        tailNotes.set(id, refusalLine(err));
        tailRefused.add(id);
        noteZones(`zone: ${refusalLine(err)}`, true);
        const zone = zoneById(id);
        if (zone !== undefined) zoneFlyout.update(zoneFlyoutState(zone));
        return;
      }
      // ONE STEP (Allen's field read, 2026-09-22 — "I don't see any bones in
      // the tail"): the answer used to CLOSE the bar, so "grow" was a second
      // trip to a flag nobody knew to reopen. Now the bar stays with the NEXT
      // thing to do — "Grow tail bones" for a tail, the plain "not yet" for
      // cloth — and "done" closes it.
      noteZones(`zone: "${zoneById(id)?.name ?? id}" confirmed as ${intent === 'tail' ? 'a tail — grow its bones on the bar' : intent === 'softTissue' ? 'soft tissue — pick it on a Soft tissue card' : 'cloth'}`);
      const zone = zoneById(id);
      if (zone !== undefined && openZoneId === id) zoneFlyout.update(zoneFlyoutState(zone));
    },
    onWand: (mode, trusted) => {
      if (!trusted || openZoneId === null) return;
      if (wandMode === mode) {
        disarmWand();
      } else {
        try {
          disarmHairWand();
          viewport.setFacePicker((face) => wandStrokeAt(face));
          wandMode = mode;
          noteZones(`wand: ${mode === 'add' ? '➕ add' : '➖ remove'} — click the model (press it again, done or Esc to stop)`);
        } catch (err) {
          noteZones(`wand: ${refusalLine(err)}`, true);
        }
      }
      const zone = zoneById(openZoneId);
      if (zone !== undefined) zoneFlyout.update(zoneFlyoutState(zone));
    },
    onTips: () => {
      if (openZoneId === null) return;
      const zone = zoneById(openZoneId);
      if (zone === undefined || rootBusy(zone)) return;
      const take = !zoneTips(zone);
      const found = remeasureZone(zone, zoneRings(zone) ?? 0, take);
      if (found === null) {
        noteZones(`zones: extend tips found no tail overlapping "${zone.name}" — kept as it is`, true);
        return;
      }
      zoneOwnedBy.set(zone.id, found.ownedBy);
      const arraysNow = zoneArraysNow();
      const reshape = arraysNow === null ? undefined : stubGrownToTail(zone, found.cluster.faceIndices, arraysNow.arrays);
      store.dispatch({
        schemaVersion: '1.0.0',
        verb: 'zone.setFaces',
        params: {
          id: zone.id,
          faces: [...found.cluster.faceIndices],
          source: { kind: 'measured-proposal', detail: found.detail },
          ...(reshape !== undefined ? { shape: reshape } : {}),
        },
      });
      if (reshape !== undefined) noteZones(`zone: "${zone.name}" is long enough to be a tail now — grows as a chain, not a stub`);
      noteZones(
        take
          ? `zone: "${zone.name}" takes the fan's other tails — ${found.cluster.faceIndices.length} faces (was ${zone.faces.length}), beyond the seam only`
          : `zone: "${zone.name}" gives the fan's other tails back — ${found.cluster.faceIndices.length} faces`,
      );
    },
    onReach: (direction) => {
      if (openZoneId === null) return;
      const zone = zoneById(openZoneId);
      if (zone === undefined || rootBusy(zone)) return;
      // Wider = one more ring of faces toward the body; tighter = one fewer.
      // A DETECTED tail starts at 0 rings, and tighter sheds rings from its
      // body side below that (Allen's husky, 2026-09-24: "TIGHTER didn't pull
      // any faces" — the step was clamped at 0 and returned without a word).
      const isTail = zoneIsDetectedTail(zone);
      const from = zoneRings(zone) ?? (isTail ? 0 : ZONE_RINGS_DEFAULT);
      const next = Math.min(12, Math.max(isTail ? TAIL_RINGS_MIN : 0, from + direction));
      if (next === from) {
        noteZones(`zone: "${zone.name}" is already as ${direction < 0 ? 'tight' : 'wide'} as it goes (${from} rings)`, true);
        return;
      }
      const found = remeasureZone(zone, next);
      if (found === null) {
        noteZones(`zones: at ${next} rings no measured region overlaps "${zone.name}" — kept ${from}`, true);
        return;
      }
      if (found.cluster.faceIndices.length === zone.faces.length && found.detail === zone.sources[zone.sources.length - 1]?.detail) {
        // The detector refused to shed further: what is left is the tail itself.
        noteZones(`zone: "${zone.name}" is the tail alone at ${from} rings — tighter would cut into it`, true);
        return;
      }
      zoneOwnedBy.set(zone.id, found.ownedBy);
      const arraysNow = zoneArraysNow();
      const reshape = arraysNow === null ? undefined : stubGrownToTail(zone, found.cluster.faceIndices, arraysNow.arrays);
      store.dispatch({
        schemaVersion: '1.0.0',
        verb: 'zone.setFaces',
        params: {
          id: zone.id,
          faces: [...found.cluster.faceIndices],
          source: { kind: 'measured-proposal', detail: found.detail },
          ...(reshape !== undefined ? { shape: reshape } : {}),
        },
      });
      if (reshape !== undefined) noteZones(`zone: "${zone.name}" is long enough to be a tail now — grows as a chain, not a stub`);
      const took = Number(RINGS.exec(found.detail)?.[1] ?? next);
      noteZones(
        took < 0
          ? `zone: "${zone.name}" shed ${-took} ring${took === -1 ? '' : 's'} at the body — ${found.cluster.faceIndices.length} faces, owned today by ${found.ownedBy}`
          : `zone: "${zone.name}" grown ${took} ring${took === 1 ? '' : 's'} toward the body — ${found.cluster.faceIndices.length} faces, owned today by ${found.ownedBy}`,
      );
    },
    onTail: onTailAction,
    onDelete: () => {
      if (openZoneId === null) return;
      const id = openZoneId;
      try {
        store.dispatch({ schemaVersion: '1.0.0', verb: 'zone.delete', params: { id } });
      } catch (err) {
        // T2: a zone carrying a chain refuses removal until the chain is withdrawn.
        tailNotes.set(id, refusalLine(err));
        tailRefused.add(id);
        noteZones(`zone: ${refusalLine(err)}`, true);
        const zone = zoneById(id);
        if (zone !== undefined) zoneFlyout.update(zoneFlyoutState(zone));
        return;
      }
      closeZoneFlyout();
      noteZones(null);
    },
    onDismiss: closeZoneFlyout,
    // However the bar closes — done, Esc, an outside click, its flesh leaving
    // the screen — the hold lets go, and playback resumes where it stood.
    onClosed: () => {
      openZoneId = null;
      syncTailRoot();
      if (!zoneHold) return;
      zoneHold = false;
      applyMixerPause();
      if (viewport.probe.paused) reproject();
    },
  });
  const openZone = (id: string): void => {
    const zone = zoneById(id);
    if (zone === undefined) return;
    openZoneId = id;
    zoneHold = true;
    applyMixerPause();
    zoneFlyout.open(zoneFlyoutState(zone));
    syncTailRoot();
  };
  /**
   * The detector OFFERS on a new subject — measured once, said once, pinned
   * only by a person from the Character panel (read #2: an auto-opened flag
   * on the boot character read as noise, and "like hands do — pin tail").
   * Runs OUTSIDE the store's notification (a microtask).
   */
  const offerTail = (): void => {
    const scene = subject.run.scene as object;
    if (zoneProposedFor.has(scene)) return;
    zoneProposedFor.add(scene);
    if (zonesForThisMesh().mine.length > 0) return; // THIS mesh already speaks
    const offers = uncoveredCandidates();
    if (offers.length === 0) {
      console.info('[animator-v3] zones: nothing no bone reaches on this subject — nothing to offer');
      return;
    }
    const tubes = offers.filter((c) => c.cluster.kind === 'tube').length;
    const stubs = offers.filter((c) => c.cluster.kind === 'stub').length;
    const sheets = offers.filter((c) => c.cluster.kind === 'sheet').length;
    const said = [
      tubes > 0 ? `a tail at the hips` : null,
      stubs > 0 ? `a possible stub tail (pin it or dismiss it)` : null,
      sheets > 0 ? `${sheets} hanging sheet${sheets === 1 ? '' : 's'} no bone reaches` : null,
    ].filter((s): s is string => s !== null);
    noteZones(`zones: ${said.length > 0 ? said.join(' and ') : `${offers.length} region(s) no bone reaches`} · Zones ▸ pin`);
    // The Hands precedent: a panel with something to say comes to the front,
    // because a persisted layout is a cache of preference that predates the
    // panel (Allen, read #2: "no zones panel — the same problem with the other
    // panels"). Once introduced, closing it is a preference the cache keeps.
    // The front helpers are declared LATER in this function; the boot offer
    // runs in a microtask that can fire before they exist (the smoke's
    // "Cannot access 'layoutHasPanel' before initialization"), so this asks
    // through a hook that is filled in when they are — and flushed then.
    // Once per CHARACTER, not per scene: every rerun (a grown leaf, a tail) builds a new scene and
    // re-offers, and under the Body accordion (XD PR 3) re-fronting would fold the section the person
    // is working in.
    const who = `${store.getDocument().target.specimen.uri}|${store.getDocument().target.label}`;
    if (zonesFrontedFor === who) return;
    // The guided frame fronts nothing: the line above still went to the status line.
    if (guided()) return;
    zonesFrontedFor = who;
    if (frontZonesPanel !== null) frontZonesPanel();
    else zonesFrontPending = true;
  };
  let zonesFrontedFor: string | null = null;
  let frontZonesPanel: (() => void) | null = null;
  let zonesFrontPending = false;
  /** A person dismisses an offer (the possible stub): gone for this subject, said once. */
  const dismissCandidate = (key: number): void => {
    if (key !== STUB_KEY) return;
    stubDismissed.add(subject.run.scene as object);
    candidatesCache = null;
    noteZones('zones: the possible stub tail is dismissed on this character');
    refreshZones();
  };
  /** A person pins an offered candidate: the zone is created with measured provenance and its flag opens. */
  /**
   * The detector's TAIL offer on the subject, when the skeleton has no tail of its own (no tail-named chain,
   * nothing grown, no tail zone declared) — the guided intake's `tail-unrigged` (follow-ups, 2026-10-03).
   */
  const tailOfferNow = (): Candidate | null => {
    if (nativeTailChain(subject.run.scene) !== null || subject.run.tailGrafts.length > 0) return null;
    if (zonesForThisMesh().mine.some((z) => z.intent === 'tail')) return null;
    return uncoveredCandidates().find((c) => c.key === 0 && c.cluster.kind === 'tube') ?? null;
  };
  /**
   * C2, unrigged — "Give it bones": the person's answer IS the confirmation. The full editor's path, in
   * order: the offer pinned as a zone the person called a tail, the chain grown (ADR-031 T2, `tail.declare`
   * through `commitTail`, the count the default rule picks), then the spring on.
   */
  const growOfferedTail = async (): Promise<void> => {
    const found = tailOfferNow();
    if (found === null) throw new Error('the tail offer is no longer on this subject');
    const zones = current(store.getHistory()).zones ?? [];
    const id = `z${zones.reduce((n, z) => Math.max(n, Number(z.id.slice(1))), 0) + 1}`;
    const taken = new Set(zones.map((z) => z.name));
    let name = 'tail';
    for (let n = 2; taken.has(name); n++) name = `tail ${n}`;
    zoneOwnedBy.set(id, found.ownedBy);
    store.dispatch({
      schemaVersion: '1.0.0',
      verb: 'zone.create',
      params: {
        zone: {
          id,
          name,
          faces: [...found.cluster.faceIndices],
          meshFingerprint: found.fingerprint,
          sources: [{ kind: 'measured-proposal', detail: found.detail }],
          shape: found.cluster.kind,
          intent: 'tail',
          confirmedBy: 'human:local',
        },
      },
    });
    const zone = zoneById(id);
    const now = zoneArraysNow();
    if (zone === undefined || now === null) throw new Error('this subject has no sole skinned mesh to measure');
    const measured = placeTail({ mesh: now.arrays, faces: zone.faces, zoneName: zone.name, zoneKind: zone.shape ?? 'tail', ...tailShapeOptions(zone, now.arrays.positions) });
    await commitTail({ schemaVersion: '1.0.0', verb: 'tail.declare', params: { zoneId: id, count: { value: measured.stations.count.value, provenance: 'default' }, confirmedBy: 'human:local' } });
    tailSpring.setOn(subject.run, true, 'human:local');
    noteZones(`tail: grown — ${measured.stations.count.value} bones on "${name}", the spring on — the person sees it wag and says Done`);
  };
  /** C2b "Leave it" in the tail's editor: the chain withdrawn, its zone removed, the spring off — as before Give it bones. */
  const leaveGrownTail = async (): Promise<void> => {
    const graft = subject.run.tailGrafts[0];
    const zoneId = graft?.zoneId ?? null;
    if (openZoneId !== null) closeZoneFlyout();
    tailSpring.setOn(subject.run, false, 'human:local');
    if (zoneId === null) return;
    await commitTail({ schemaVersion: '1.0.0', verb: 'tail.withdraw', params: { zoneId } });
    store.dispatch({ schemaVersion: '1.0.0', verb: 'zone.delete', params: { id: zoneId } });
    noteZones('tail: left as it was — the bones Harry offered are taken back');
  };
  const pinCandidate = (key: number): void => {
    const found = candidatesNow().find((c) => c.key === key);
    if (found === undefined) {
      noteZones('zones: that offer is no longer on this subject', true);
      return;
    }
    const zones = current(store.getHistory()).zones ?? [];
    const id = `z${zones.reduce((n, z) => Math.max(n, Number(z.id.slice(1))), 0) + 1}`;
    // A name is an identity across the whole Document, stale zones included.
    const taken = new Set(zones.map((z) => z.name));
    const base = found.cluster.kind === 'tube' || found.cluster.kind === 'stub' ? 'tail' : found.cluster.kind === 'sheet' ? 'cloth' : 'region';
    let name = base;
    for (let n = 2; taken.has(name); n++) name = `${base} ${n}`;
    zoneOwnedBy.set(id, found.ownedBy);
    store.dispatch({
      schemaVersion: '1.0.0',
      verb: 'zone.create',
      params: {
        zone: {
          id,
          name,
          faces: [...found.cluster.faceIndices],
          meshFingerprint: found.fingerprint,
          sources: [{ kind: 'measured-proposal', detail: found.detail }],
          shape: found.cluster.kind,
        },
      },
    });
    noteZones(`zone: "${name}" pinned — ${found.cluster.faceIndices.length} faces, ${found.cluster.elongation.toFixed(1)}× long · answer on its flag`);
    openZone(id);
  };
  const zonesUiState = (): ZonesUiState => {
    const now = zoneArraysNow();
    if (now === null) return { kind: 'refused', reason: 'this subject has no sole skinned mesh to measure' };
    const { mine } = zonesForThisMesh();
    return {
      kind: 'ready',
      candidates: uncoveredCandidates().map((c) => ({
        key: c.key,
        shape: c.cluster.kind,
        faces: c.cluster.faceIndices.length,
        elongation: c.cluster.elongation,
        ownedBy: c.ownedBy,
      })),
      // `mine` holds no hair zone (zonesForThisMesh); the narrowing says so to the type
      zones: mine.map((z) => ({ id: z.id, name: z.name, faces: z.faces.length, shape: z.shape ?? null, intent: z.intent === 'hair' ? null : (z.intent ?? null) })),
    };
  };
  const refreshZones = (): void => {
    const liveScene = subject.run.scene;
    const arrived = zoneSubjectSeen !== liveScene;
    zoneSubjectSeen = liveScene;
    if (arrived) queueMicrotask(offerTail);
    const { mine, stale } = zonesForThisMesh();
    if (stale.length > 0 && staleNotedFor !== liveScene) {
      // D2: never relabel a regenerated character. Said once per subject, kept in the Document.
      staleNotedFor = liveScene;
      console.info(
        `[animator-v3] zones: ${stale.length} zone(s) cut on another mesh (${stale.map((z) => `"${z.name}"`).join(', ')}) — kept in the Document, not shown on this subject`,
      );
    }
    if (mine.length === 0 || (guided() && !editingTail)) {
      if (zoneTint.probe().on) zoneTint.hide();
      if (openZoneId !== null) closeZoneFlyout();
      return;
    }
    const refusal = zoneTint.show(mine.map((z) => ({ id: z.id, faces: z.faces, color: ZONE_HUES[z.intent ?? 'none'] })));
    if (refusal !== null) {
      noteZones(`zones: ${refusal}`, true);
      return;
    }
    if (openZoneId !== null) {
      const open = mine.find((z) => z.id === openZoneId);
      if (open === undefined) closeZoneFlyout();
      else zoneFlyout.update(zoneFlyoutState(open));
    }
  };
  // Z opens the flyout on the first zone, and steps through them on repeat;
  // on an open flyout it closes. Keyed on `code` like the wire's `4`.
  window.addEventListener('keydown', (event) => {
    if (event.code !== 'KeyZ' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.repeat) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    const { mine: zones } = zonesForThisMesh();
    if (zones.length === 0) {
      noteZones('zones: none on this subject', true);
      return;
    }
    if (openZoneId === null) {
      openZone(zones[0]!.id);
      return;
    }
    const at = zones.findIndex((z) => z.id === openZoneId);
    if (at < 0 || at === zones.length - 1) closeZoneFlyout();
    else openZone(zones[at + 1]!.id);
  });
  // (the store.subscribe that keeps bone mode following the selection lives
  // with the other subscribes, after the store exists)

  const readStored = (): string | null => {
    try {
      return localStorage.getItem(WORKSPACES_KEY);
    } catch {
      storageOk = false;
      return null;
    }
  };
  const storedWorkspaces = readStored();
  if (storedWorkspaces !== null) {
    try {
      initialWorkspaces = parseWorkspaces(JSON.parse(storedWorkspaces), availability);
    } catch (err) {
      const reason = err instanceof WorkspaceParseError ? err.message : String(err);
      noteStatus(`workspace restored to defaults — ${reason}`);
      try {
        localStorage.removeItem(WORKSPACES_KEY);
      } catch {
        storageOk = false;
      }
    }
  }
  if (!storageOk) noteStatus(STORAGE_LOST);

  const store = createStore(createHistory(seed), initialShell(), {
    poseTargets,
    workspaces: {
      initial: initialWorkspaces,
      // The write door gets the same law the reload door has (Codex P2, #63).
      availability,
      persist: (workspaces) => {
        try {
          localStorage.setItem(WORKSPACES_KEY, JSON.stringify(workspaces));
        } catch (err) {
          if (storageOk) {
            // Once, loudly — then quiet: the environment fact does not change.
            // Loudly means ON THE SURFACE, not only in the console: this is
            // the first moment the user could learn their layout is not being
            // kept, and the console is not where they are looking.
            storageOk = false;
            noteStatus(STORAGE_LOST, err);
          }
        }
      },
    },
  });
  // THE DOCUMENT, READABLE FROM THE CONSOLE (probe seam, 2026-08-16): the
  // Document is not persisted, so a field session's pins / wrist / knuckles
  // could not reach a headless probe. `copy(__ANIMATOR_DOCUMENT_JSON__())`
  // in the devtools console hands the exact Document to the corpus driver.
  // Read-only; the same shape `parseDocument` accepts.
  window.__ANIMATOR_DOCUMENT_JSON__ = () => JSON.stringify(store.getDocument(), null, 2);

  // ───────── ADR-039 T1: hair — detect, confirm the skull, gravity holds the sculpt ─────────
  // The detector reads the run's subject with the skin AS THE FILE PAINTED IT (`hairSubject`, the
  // door the hair door test walks) in the tail detector's measured body frame. The controller holds
  // the offer and a person's draft skull (session only); the Document holds only what a person
  // confirmed (`hair.confirm` / `hair.setGravity` / `hair.withdraw`). Declared before the workspace
  // shell mounts: a persisted layout may mount the Body panel on the spot.
  const hairSubjects = new WeakMap<object, HairSubject | string>();
  const hairSubjectNow = (): HairSubject | string => {
    const scene = subject.run.scene;
    let found = hairSubjects.get(scene);
    if (found === undefined) {
      try {
        found = hairSubject(scene);
      } catch (err) {
        found = refusalLine(err);
      }
      hairSubjects.set(scene, found);
    }
    return found;
  };
  /** The body frame the tail detector measured on this subject (forward from the feet, height). */
  const hairFrame = (): NonNullable<ReturnType<typeof detectTail>['frame']> | string => {
    candidatesNow();
    const report = tailReportCache?.for === subject.run.scene ? tailReportCache.report : null;
    if (report === null) return 'the body frame was not measured on this subject';
    return report.frame ?? `no body frame — ${report.frameRefusal ?? 'unmeasured'}`;
  };
  const hair = createHairController({
    subjectKey: () => subject.run.scene,
    document: () => store.getDocument(),
    dispatch: (command) => store.dispatch(command),
    measure: (skull) => {
      const body = hairSubjectNow();
      if (typeof body === 'string') return body;
      const frame = hairFrame();
      if (typeof frame === 'string') return frame;
      try {
        return readHair(body, frame, skull);
      } catch (err) {
        return refusalLine(err);
      }
    },
    fingerprint: () => zoneArraysNow()?.fingerprint ?? null,
    skin: () => {
      const body = hairSubjectNow();
      return typeof body === 'string' || body.arrays.skin === null ? null : { skin: body.arrays.skin, bones: body.arrays.bones, indices: body.arrays.indices };
    },
    // ADR-039 T2a: what the run's hair pass did with the confirmed hair (the card's one sentence).
    softened: () => subject.run.hair,
  });
  const hairListeners = new Set<() => void>();
  /** The Hair card's section is open: the skull and the tint are drawn. */
  let hairVisible = false;
  const hairCardState = (): ReturnType<typeof hair.view>['state'] => {
    try {
      return hair.view().state;
    } catch (err) {
      console.warn('[animator-v3] hair: the reading threw', err);
      return 'refused';
    }
  };
  // ---- THE HAIR WAND (Allen, 2026-10-01: "I found myself wanting to ➕ add area similar to tails") ----
  // The zone wand (ADR-031 T3) on the confirmed hair zone: same stroke, same cue, one zone.setFaces per click.
  // It STAYS ARMED until Done (Allen's second field read: "only one click add at a time and then it disables"
  // — every click re-ran the character, and the remount put the wand away). While it is armed the run is held
  // on the faces it had when the wand came out (`hairWandFaces`, read by the re-run key); the tint follows
  // each click at once, and Done re-softens ONCE on the region as drawn.
  let hairWandMode: 'add' | 'remove' | null = null;
  let hairWandFaces: readonly number[] | null = null;
  let recheckRun: () => void = () => undefined;
  const disarmHairWand = (): void => {
    if (hairWandMode === null) return;
    hairWandMode = null;
    hairWandFaces = null;
    viewport.setFacePicker(null);
    hairChanged();
    recheckRun(); // the held faces let go: re-soften if the region moved
  };
  subjectChanged.add(disarmHairWand);
  const hairZoneNow = () => {
    const doc = current(store.getHistory());
    const declared = doc.target.hair;
    return declared === undefined ? undefined : (doc.zones ?? []).find((z) => z.id === declared.zoneId);
  };
  const hairWandAt = (face: number): void => {
    const zone = hairZoneNow();
    const now = zoneArraysNow();
    const mode = hairWandMode;
    if (zone === undefined || now === null || mode === null) return;
    const cue = wandCue(now);
    try {
      const r = wandStroke({ positions: now.arrays.positions, indices: now.arrays.indices, armature: cue.armature }, zone.faces, { mode, face, radius: cue.radius, dropBodyIslands: false });
      if (r.faces.length === zone.faces.length && r.changed === 0) {
        noteBody(`hair wand: nothing to ${mode === 'add' ? 'add' : 'remove'} there — that face is ${mode === 'add' ? 'already hair' : 'not hair'}`, true);
        return;
      }
      if (r.faces.length === 0) {
        noteBody('hair wand: that would remove all of the hair — use Not hair instead', true);
        return;
      }
      store.dispatch({
        schemaVersion: '1.0.0',
        verb: 'zone.setFaces',
        params: { id: zone.id, faces: r.faces, source: { kind: 'human-edit', detail: `hair-wand${mode === 'add' ? '+' : '-'} face=${face} r=${cue.radius.toFixed(3)}` } },
      });
      noteBody(`hair wand: ${mode === 'add' ? 'took' : 'gave back'} ${Math.abs(r.faces.length - zone.faces.length)} faces — ${r.faces.length} now`);
    } catch (err) {
      noteBody(`hair wand: ${refusalLine(err)}`, true);
    }
  };
  const armHairWand = (mode: 'add' | 'remove' | null): void => {
    if (mode === null) {
      const moved = hairWandFaces !== null && hairZoneNow()?.faces !== hairWandFaces;
      disarmHairWand();
      noteBody(moved ? 'hair: softening the region as drawn…' : 'hair wand: put away');
      return;
    }
    if (hairWandMode === null) {
      disarmWand(); // one wand owns clicks at a time
      viewport.setFacePicker((face) => hairWandAt(face));
      hairWandFaces = hairZoneNow()?.faces ?? null;
    }
    hairWandMode = mode; // ➕ and ➖ switch in place; Done puts it away
    noteBody(`hair wand: ${mode === 'add' ? '➕ add' : '➖ remove'} — click the model as often as you like, Done when the hair is right`);
    hairChanged();
  };
  const hairTool = createHairTool({
    viewport,
    scene: () => subject.run.scene,
    hold: (on) => {
      hairHold = on;
      applyMixerPause();
    },
    adjust: (skull) => {
      hair.adjust(skull);
      hairChanged();
      noteBody(hair.view().line);
    },
  });
  // ---- THE TAIL'S ROOT HANDLE (Allen, 2026-10-05: "starts a bit too high and a bit too far behind") ----
  // Shown on Tail1 while its zone's flag is open; a release declares the pivot, replayed by the run.
  const tailRootHandle = createTailRootHandle({
    viewport,
    scene: () => subject.run.scene,
    hold: () => undefined, // the open flag already holds playback (zoneHold)
    place: (pivot) => placeTailRoot(pivot),
  });
  // ONE placement in flight; a held arrow's repeats COALESCE to the latest pivot, committed when the
  // one in flight settles — each commit runs off the Document as it then stands (Codex P2, #253).
  let rootCommitting = false;
  const TAIL_ROOT_BUSY = 'placing the root — try that again in a moment';
  /**
   * A root placement still rebuilding owns the Document: any edit that changes it now (a tail action, a
   * zone's reach or tips, a wand stroke) would race it, and one would be refused (Codex P2, #253). Says
   * so on the bar and returns true; the note clears when the placement lands.
   */
  const rootBusy = (zone: Zone): boolean => {
    if (!rootCommitting) return false;
    tailNotes.set(zone.id, TAIL_ROOT_BUSY);
    if (openZoneId === zone.id) zoneFlyout.update(zoneFlyoutState(zone));
    return true;
  };
  /** The latest placement waiting, keyed to the zone it was made on (Codex P2, #253: never another tail's). */
  /** `pivot: null` is a RESET (the measured root), queued like any placement. */
  let rootPending: { readonly zoneId: string; readonly pivot: { x: number; y: number; z: number } | null } | null = null;
  const placeTailRoot = (pivot: { x: number; y: number; z: number } | null, zoneId: string | null = openZoneId): void => {
    if (zoneId === null) return;
    if (rootCommitting) {
      rootPending = { zoneId, pivot };
      return;
    }
    const declared = (current(store.getHistory()).target.tails ?? []).find((t) => t.zoneId === zoneId);
    if (declared === undefined || commitTailRef === null) {
      tailRootHandle.settled();
      return;
    }
    rootCommitting = true;
    // Through the tail's commit door (export hold, projection wait, off-stage placement) — every
    // tail edit's door, never a raw dispatch (Codex P1, #253). Success is said only once it landed.
    noteZones('tail: placing the root…');
    const settle = (): void => {
      rootCommitting = false;
      // The busy note may sit on any zone a person tried meanwhile (Codex P2, #253): clear every one.
      for (const [id, note] of tailNotes) if (note === TAIL_ROOT_BUSY) tailNotes.delete(id);
      const open = openZoneId === null ? undefined : zoneById(openZoneId);
      if (open !== undefined) zoneFlyout.update(zoneFlyoutState(open));
      const next = rootPending;
      rootPending = null;
      if (next !== null) placeTailRoot(next.pivot, next.zoneId);
    };
    const params: Record<string, unknown> = { ...declared, confirmedBy: 'human:local' };
    if (pivot === null) delete params['pivot'];
    else params['pivot'] = pivot;
    commitTailRef({ schemaVersion: '1.0.0', verb: 'tail.declare', params }).then(
      () => {
        noteZones(pivot === null ? 'tail: root reset to the measured one' : 'tail: root placed — the chain re-seats on it');
        // The last placement landed: the draft lets go, so the handle follows the bone as it now
        // stands (after a reset, the measured root) — the remount's sync ran while this was in flight.
        if (rootPending === null) tailRootHandle.settled();
        settle();
      },
      (err: unknown) => {
        if (rootPending === null) tailRootHandle.settled(); // the draft lets go: the handle returns to the bone as it stands
        noteZones(`tail: ${refusalLine(err)}`, true);
        settle();
      },
    );
  };
  /**
   * The body's facing for the handle's side plane. A placement re-runs the character, and the tail
   * report is re-measured only after the remount — for that moment it was missing, the handle was
   * told "no tail", and it dropped its focus and its queued draft. Re-placing a tail never turns
   * the body, so the last facing measured holds through the remount.
   */
  let rootForward: { readonly x: number; readonly y: number; readonly z: number } | null = null;
  /**
   * The bones, while the root handle is out (Allen, 2026-10-05: "we should show the bones at that
   * point" — a pivot is placed against the chain it starts). VIEW-ONLY: the pose tool's octahedra on
   * the editor overlays, nothing picks them, and pose mode (whose tool already draws them) gets none.
   */
  let rootBones: BoneVisual | null = null;
  let rootBonesFor: Subject | null = null;
  let rootBonesOn = false;
  const syncRootBones = (on: boolean): void => {
    rootBonesOn = on;
    const want = on && store.getShell().mode !== 'pose';
    if (want && rootBones !== null && rootBonesFor === subject) return;
    rootBones?.dispose();
    rootBones = null;
    rootBonesFor = null;
    if (!want) return;
    rootBones = createBoneVisual(viewport.editingSurface().overlays, subject.run.scene, [...subject.poseTargets], boneGeometry, mappedRigBones(subject.run.rig));
    rootBonesFor = subject;
  };
  syncTailRoot = () => {
    const id = openZoneId;
    const graft = id === null ? undefined : subject.run.tailGrafts.find((t) => t.zoneId === id);
    const measured = tailReportCache?.report.frame?.forward;
    if (measured !== undefined) rootForward = measured;
    if (id === null) rootForward = null;
    const target = graft !== undefined && graft.bones[0] !== undefined && rootForward !== null ? { bone: graft.bones[0], forward: rootForward } : null;
    tailRootHandle.show(target);
    syncRootBones(target !== null);
  };
  // Into pose mode the pose tool draws the bones; out of it, the view-only set returns.
  let rootBonesMode = store.getShell().mode;
  store.subscribe(() => {
    const mode = store.getShell().mode;
    if (mode === rootBonesMode) return;
    rootBonesMode = mode;
    syncRootBones(rootBonesOn);
  });
  subjectChanged.add(() => {
    // A placement still queued keeps the draft the person sees; the last one to land lets it go.
    if (!rootCommitting && rootPending === null) tailRootHandle.settled();
    syncTailRoot();
  });
  const drawHair = (): void => {
    let display: ReturnType<typeof hair.display> = null;
    try {
      // An offer never acts on the eye in the guided frame, whatever the register (design §1 finding 2).
      display = hairVisible && !guided() ? hair.display() : null;
    } catch (err) {
      noteBody(`hair: ${refusalLine(err)}`, true);
    }
    // Allen's field read, 2026-10-01: once the hair is defined and weighted the overlay gets out of the way
    // of the animation — it shows again to Adjust the skull or to reshape the region with the wand.
    if (display !== null && display.confirmed && !display.editable && hairWandMode === null) display = null;
    const refusal = hairTool.show(display);
    if (refusal !== null) noteBody(`hair: ${refusal}`, true);
  };
  const hairChanged = (): void => {
    drawHair();
    hairListeners.forEach((listener) => listener());
    bodyProgressListeners.forEach((listener) => listener());
  };
  store.subscribe(drawHair);
  subjectChanged.add(drawHair);
  // THE HAIR'S MOTION IS THE DOCUMENT'S (ADR-039 §14; Allen, 2026-10-01: "when I add an animation it resets
  // the three"): every run the hair spring is grown with the prior's controls, so the Document's Bounce /
  // Sway / Jiggle are laid on each new run, and on each change (undo included). Only what differs is set.
  const syncHairMotion = (): void => {
    if (current(store.getHistory()).target.hair === undefined) return;
    const live = softTissue.views(subject.run).find((v) => v.region === 'Hair')?.controls;
    if (live === undefined) return;
    const want = hair.motion();
    if (live.bounce === want.bounce && live.sway === want.sway && live.jiggle === want.jiggle) return;
    softTissue.setControls(subject.run, 'Hair', want, 'human:local');
    hairListeners.forEach((listener) => listener());
  };
  store.subscribe(syncHairMotion);
  subjectChanged.add(syncHairMotion);
  window.__ANIMATOR_HAIR__ = {
    card: () => hair.probe(),
    tool: () => hairTool.probe(),
    adjustBy: (du, dv, dw, scale) => hairTool.adjustBy(du, dv, dw, scale),
  };
  // ---- the character display filter (ADR-017 arm-space + head-space) ----
  // Realized ENTIRELY at the viewport's one clip door: the offsets derive from
  // the live rig + the Document's `edits`, and the filter transforms the
  // DISPLAY clip only — kernel ClipData never passes through it, so both
  // export doors and the bake stay untouched by construction (ADR-017 §2).
  // `subject` is read through, never captured (#61's lesson): the one
  // subscriber covers a dispatch, undo/redo, AND a subject swap — store.load
  // notifies, and by then `subject` already points at the new run, so the
  // notify where present() briefly wore the previous filter closes before the
  // next RAF samples anything. Declared BEFORE the workspace shell mounts:
  // a persisted layout may mount the Character panel immediately, and its
  // render calls these state functions on the spot.
  const armSpaceUiState = (): ArmSpaceUiState => {
    try {
      armSpaceOffsets(subject.run.rig, 0); // capability probe — refuses on a rig that cannot carry it
      const explicit = current(store.getHistory()).edits.armSpace;
      // ADR-030 §13 (ruled): absent = what the FILE declares, so a reopened export wears what was set.
      return {
        kind: 'ready',
        degrees: explicit ?? subject.run.armSpaceFile ?? 0,
        file: subject.run.armSpaceFile,
        explicit: explicit !== undefined,
      };
    } catch (err) {
      if (err instanceof ArmSpaceRefusal) return { kind: 'refused', reason: err.reason };
      throw err;
    }
  };
  // ---- head seating (ADR-030): the ARMATURE wears it; the filter only previews ----
  // The run seated the Neck by `edits.headSpace ?? measured baseline` and
  // recorded what it did (run.headSeating). A later commit — dispatch, undo,
  // redo — reseats the LIVE scene in place by the difference, which is
  // provably what a re-run would produce: the seating is the run's LAST
  // surgery (run-document.ts), nothing downstream measures off Neck/Head, and
  // the seating probe pins the equality (ADR-030 §11 D1a's escape hatch,
  // earned rather than assumed). The mixer's cached originals are the OLD
  // rest, so the surgery is bracketed: present with no clip (stop restores
  // rests), reseat, re-mount the subject (fresh projector rests over the
  // moved Neck, fresh hinge drives), and let the presentation subscriber
  // re-present — the new mixer records the moved rest as its original. The
  // clip restarts on commit, the price the preview filter already pays per
  // drag step. Registered BEFORE the filter and the presentation subscribers
  // so both act on the reseated subject in the same notify.
  /** Degrees the LIVE scene wears beyond what the file declared, per run object — seeded from what the run applied. */
  const seatingWorn = new WeakMap<object, number>();
  const wornSeating = (run: Subject['run']): number => seatingWorn.get(run) ?? run.headSeating.degrees;
  /** The TOTAL the live scene wears from the authored rest: the file's declaration + the worn surgery (ADR-030 §14). */
  const totalSeating = (run: Subject['run']): number => (run.headSeating.file?.degrees ?? 0) + wornSeating(run);
  /** Set by a reseat; the presentation subscriber re-presents on it and clears it. */
  let stageInvalid = false;
  const commitHeadSeating = (): void => {
    const run = subject.run;
    if (run.headSeating.bone === null) return; // the rig cannot carry it — an explicit value already refused at the run
    const explicit = current(store.getHistory()).edits.headSpace;
    // The one rule the run applies (run-document): totals from the authored
    // rest, the surgery relative to the file's declaration.
    const decision = decideHeadSeating({
      baseline: run.headSeating.baseline,
      explicit,
      file: run.headSeating.file,
      source: run.headSeating.source,
    });
    const target = decision.degrees;
    const worn = wornSeating(run);
    const scene = run.scene;
    // The same target identity + skeleton selector the run passed: a target
    // with two disjoint skeletons is reseated on the one the Document chose.
    const targetDecl = current(store.getHistory()).target;
    const selection = {
      label: targetDecl.label,
      ...(targetDecl.selectSkeleton !== undefined ? { selectSkeleton: targetDecl.selectSkeleton } : {}),
    };
    // Rests first when the bind is about to move: stop the mixer (its
    // originals restored), clear the projection. A declaration-only commit
    // (a person confirming the measured value) moves nothing on stage.
    const move = target !== worn;
    if (move) {
      viewport.present(scene, null);
      subject.projector.project([]);
    }
    // One surgery for the difference, the marker kept current — the seam the
    // reopen test walks (run-document): rotation about a fixed axis is linear
    // in the angle and the Neck's origin does not move, so the composition
    // of the run's seating and this delta is exact.
    const applied = commitHeadSeatingInPlace(scene, run.seatedRig, decision, worn, selection);
    if (applied === null) return; // neither the bind nor the declaration changed
    const headSeating = { ...run.headSeating, degrees: target, total: decision.total, provenance: decision.provenance };
    if (applied.reseat === null) {
      // Only the declaration changed (Codex P2, #126): the file will say a
      // person decided; the stage is untouched, so the record follows without
      // a re-mount or a clip restart.
      const nextRun: Subject['run'] = { ...run, headSeating };
      seatingWorn.set(nextRun, target);
      subject = { ...subject, run: nextRun };
      console.info(
        `[animator-v3] head seating declared ${decision.total.toFixed(1)}° ${decision.provenance} on "${applied.bone}" (bind unchanged)`,
      );
      return;
    }
    // A LOADED pose on the Neck seeded the projector with its pre-pose rest;
    // that rest moved with the surgery.
    const poseRests = run.poseRests.map((p) =>
      p.bone === applied.bone ? { bone: p.bone, rotation: quatMultiply(applied.reseat!.localPreRotation, p.rotation) } : p,
    );
    const nextRun: Subject['run'] = { ...run, seatedRig: applied.seatedRig, poseRests, headSeating };
    seatingWorn.set(nextRun, target);
    subject = mountSubject(nextRun);
    // Every in-lineage remount re-aims the store's bone gate (Codex/Luna P1, #180).
    store.setPoseTargets(subject.poseTargets);
    stageInvalid = true;
    console.info(
      `[animator-v3] head seating ${totalSeating(run).toFixed(1)}° → ${decision.total.toFixed(1)}° total on "${applied.bone}" ` +
        `(${applied.reseat.descendants} bones followed, ${applied.reseat.skeletons} skin${applied.reseat.skeletons === 1 ? '' : 's'}; ` +
        `file declared ${run.headSeating.file === null ? 'nothing' : `${run.headSeating.file.degrees.toFixed(1)}° ${run.headSeating.file.provenance}`})`,
    );
  };
  const headSpaceUiState = (): HeadSpaceUiState => {
    const seating = subject.run.headSeating;
    if (seating.refused !== null) return { kind: 'refused', reason: seating.refused };
    const explicit = current(store.getHistory()).edits.headSpace;
    return {
      kind: 'ready',
      degrees: explicit ?? totalSeating(subject.run),
      baseline: seating.baseline,
      explicit: explicit !== undefined,
      file: seating.file,
    };
  };
  let characterShown: {
    readonly run: object;
    readonly armDegrees: number;
    /** The head PREVIEW delta over what the armature wears (0 when no drag is live). */
    readonly headDelta: number;
  } | null = null;
  // A drag-in-progress previews through the display filter — the bones move
  // live under the thumb (Allen, 2026-07-31) — while the command log still
  // receives exactly one set per gesture on release. Preview is transient UI
  // state, never Document state: an interrupted drag leaves nothing to undo.
  // For head-space the filter previews only the DIFFERENCE between the
  // dragged value and the seating the armature already wears (ADR-030 §5:
  // preview is the one deliberate display-side exception; the commit is the
  // reseat above, and the seating probe pins that the two agree).
  let armSpacePreview: number | null = null;
  let headSpacePreview: number | null = null;
  const applyCharacterFilter = (): void => {
    const run = subject.run;
    const edits = current(store.getHistory()).edits;
    const armDegrees = armSpacePreview ?? edits.armSpace ?? run.armSpaceFile ?? 0; // absent = the file's declaration (ADR-030 §13)
    // The slider speaks in TOTALS from the authored rest; the live scene wears
    // the file's declaration plus the worn surgery, so the preview is the gap.
    const headDelta =
      headSpacePreview === null || run.headSeating.bone === null ? 0 : headSpacePreview - totalSeating(run);
    if (
      characterShown !== null &&
      characterShown.run === run &&
      characterShown.armDegrees === armDegrees &&
      characterShown.headDelta === headDelta
    )
      return;
    let armRotations: readonly WorldAxisRotation[] = [];
    try {
      armRotations = armSpacePlan(run.rig, armDegrees);
    } catch (err) {
      if (!(err instanceof ArmSpaceRefusal)) throw err;
      // A loaded Document can carry arm-space onto a rig that cannot resolve
      // it (the command never ran here). Loud, never silent: playback
      // continues unfiltered and the status names why (ADR-017 §7).
      if (armDegrees !== 0) noteStatus(`arm-space ${armDegrees}° not applied — ${err.reason}`, err);
    }
    // The head preview: a unit plan on the SEATED rig, scaled by the delta —
    // the band gates values, and a delta between two in-band values may
    // exceed it; rotation about a fixed axis is linear in the angle. It
    // previews a BIND property, so it must not synthesize motion for a clip
    // that leaves the Neck undriven: the commit leaves that Neck at its rest,
    // where the flesh reads the authored head (Codex P1, #126).
    const headRotations: readonly WorldAxisRotation[] =
      headDelta === 0
        ? []
        : headSpacePlan(run.seatedRig, 1).map((r) => ({ ...r, radians: r.radians * headDelta, untracked: 'skip' as const }));
    // ONE filter at the one door, arm and head concatenated — the rotation
    // lists address disjoint bones. Applied through the PLAYED-frame door
    // (withWorldAxisPreRotation, the 2026-08-03 amendment): the bind-frame
    // transport inverted arm-space on VRoids and diluted it on Tripo — the
    // world axis is now conjugated per key through the parent frame the clip
    // actually plays. The rig handed to the door is the SEATED one, so an
    // untracked Neck synthesizes its track over the rest local the scene
    // actually holds. Inlined so the clip type infers from the viewport seam
    // — main stays engine-free.
    const rotations = [...armRotations, ...headRotations];
    if (rotations.length > 0) {
      viewport.setClipDisplayFilter((clip) => withWorldAxisPreRotation(clip, run.seatedRig, rotations));
    } else {
      viewport.setClipDisplayFilter(null);
    }
    characterShown = { run, armDegrees, headDelta };
  };
  store.subscribe(commitHeadSeating);
  store.subscribe(applyCharacterFilter);
  // ADR-027: bone-mode heat follows the pose selection live.
  store.subscribe(repaintOverlay);
  store.subscribe(refreshWire);
  store.subscribe(refreshZones);
  refreshZones(); // the first subject arrived before anyone subscribed
  applyCharacterFilter(); // establish the boot state (seed: measured baselines, no explicit edits)

  // ---- the materials filter (ADR-020 T2 — EXPORT-REAL, ratified §11) ----
  // Factors drive the LIVE materials (edit ?? shipped, the whole truth every
  // pass, so undo falls back to the file's own values), and because the model
  // export reads the live scene, the same values ride into the file — the
  // display and the export are ONE door here, by design, not by accident.
  type MaterialPreview =
    | { kind: 'scalar'; material: string; channel: MaterialChannel; value: number }
    | { kind: 'color'; material: string; channel: MaterialColorChannel; value: LinearRgb };
  let materialPreview: MaterialPreview | null = null;
  const materialsUiState = (): MaterialsUiState => {
    const targets = materialFactorTargets(subject.run.scene);
    if (targets.length === 0) {
      return { kind: 'refused', reason: 'this subject carries no standard PBR materials' };
    }
    const edits = current(store.getHistory()).edits.materials ?? [];
    return {
      kind: 'ready',
      materials: targets.map((t) => {
        const edit = edits.find((e) => e.material === t.name);
        const scalar = (editValue: number | undefined, shipped: number): { value: number; shipped: number; explicit: boolean } => ({
          value: editValue ?? shipped,
          shipped,
          explicit: editValue !== undefined,
        });
        const color = (editValue: LinearRgb | undefined, shipped: LinearRgb): { value: LinearRgb; shipped: LinearRgb; explicit: boolean } => ({
          value: editValue ?? shipped,
          shipped,
          explicit: editValue !== undefined,
        });
        return {
          name: t.name,
          metallic: scalar(edit?.metallicFactor, t.shippedMetallic),
          roughness: scalar(edit?.roughnessFactor, t.shippedRoughness),
          normalScale: scalar(edit?.normalScale, t.shippedNormalScale),
          occlusionStrength: scalar(edit?.occlusionStrength, t.shippedOcclusionStrength),
          baseColor: color(edit?.baseColorFactor, t.shippedBaseColor),
          emissive: color(edit?.emissiveFactor, t.shippedEmissive),
          // The band asks "is there a texture to bake >1 into?" — without one
          // a >1 factor would refuse at export, so the slider cannot say it.
          // MEASURED per material, not a constant: the satin class ships
          // roughness ~0.5 (x2 reaches matte) and the newer glossy class ~0.04
          // (x2 leaves a mirror). See roughnessCeilingFor.
          roughnessMax: t.roughnessCeiling,
          // Absent means ATTACHED — the file's own behaviour, never a stored
          // "true" pretending to be an edit.
          metallicFromMap: edit?.metallicFromMap ?? true,
          roughnessFromMap: edit?.roughnessFromMap ?? true,
          hasMetallicRoughnessMap: t.hasRoughnessTexture || t.hasMetallicTexture,
          measuredRoughness: t.measuredRoughness,
          // Texture attributes: without the map the panel renders the refusal.
          hasNormalTexture: t.hasNormalTexture,
          hasOcclusionTexture: t.hasOcclusionTexture,
        };
      }),
    };
  };
  let materialsMissingNoted = '';
  interface FactorValues {
    metallicFromMap?: boolean;
    roughnessFromMap?: boolean;
    metallic?: number;
    roughness?: number;
    normalScale?: number;
    occlusionStrength?: number;
    baseColor?: LinearRgb;
    emissive?: LinearRgb;
  }
  /** The Document's material edits as the viewport's apply wants them. */
  const materialFactorValues = (
    edits: NonNullable<ReturnType<typeof current>['edits']['materials']>,
  ): Map<string, FactorValues> => {
    const factorMap = new Map<string, FactorValues>();
    for (const e of edits) {
      const values: FactorValues = {};
      if (e.metallicFromMap !== undefined) values.metallicFromMap = e.metallicFromMap;
      if (e.roughnessFromMap !== undefined) values.roughnessFromMap = e.roughnessFromMap;
      if (e.metallicFactor !== undefined) values.metallic = e.metallicFactor;
      if (e.roughnessFactor !== undefined) values.roughness = e.roughnessFactor;
      if (e.normalScale !== undefined) values.normalScale = e.normalScale;
      if (e.occlusionStrength !== undefined) values.occlusionStrength = e.occlusionStrength;
      if (e.baseColorFactor !== undefined) values.baseColor = e.baseColorFactor;
      if (e.emissiveFactor !== undefined) values.emissive = e.emissiveFactor;
      factorMap.set(e.material, values);
    }
    return factorMap;
  };
  const applyMaterialsFilter = (): void => {
    const edits = current(store.getHistory()).edits.materials ?? [];
    const factorMap = materialFactorValues(edits);
    if (materialPreview !== null) {
      factorMap.set(materialPreview.material, {
        ...(factorMap.get(materialPreview.material) ?? {}),
        [materialPreview.channel]: materialPreview.value,
      });
    }
    const { missing } = applyMaterialFactors(subject.run.scene, factorMap);
    // A loaded Document can name materials this subject lacks — loud once per
    // distinct miss-set, never per notify (ADR-017 §7 posture, armSpace's law).
    const missKey = missing.join(', ');
    if (missKey !== materialsMissingNoted) {
      materialsMissingNoted = missKey;
      if (missKey !== '') {
        noteStatus(`material factor(s) not applied — no material named ${missKey} on this subject`);
      }
    }
  };
  store.subscribe(applyMaterialsFilter);
  applyMaterialsFilter(); // establish the boot state (seed: no factor edits)

  // The top bar is SHELL CHROME (ADR-015 §3) — mode FSM + undo/redo survive
  // every workspace; its document.body mount is the chrome tier's path.
  mountUndoChrome(document.getElementById('viewport-center')!, store);

  // The workspace engine over the index.html sockets — fail-loud on the
  // scaffold contract like the canvas above.
  const zoneEl = (id: string): HTMLElement => {
    const el = document.getElementById(id);
    if (!(el instanceof HTMLElement)) throw new Error(`${id} missing — index.html workspace scaffold broken`);
    return el;
  };
  // The Package panel's library deps are declared HERE, before the shell
  // mounts: a persisted layout with the Package tab active mounts and renders
  // it synchronously inside mountWorkspaceShell, and its render touches
  // ensureLibrary / libraryListeners / libraryRead at once (Codex P1, #152;
  // the smoke "a persisted workspace with the Package tab active boots"). The
  // phase-4 door it plays through (previewLibraryClip) is declared later and
  // reached only at play time.
  // ───────── ADR-033 phase 6: hero takes ──────────
  // The Document remembers takes by FILENAME (F6); the bytes live here for the
  // session and go into the package folder at phase 7. A reopened Document
  // names takes this map does not hold — the panel says so and the starred
  // one is a reason until retaken. The map only GROWS for the session: a
  // lifted take keeps its bytes so Undo restores a take, not a name with
  // "no PNG in this session" behind it (Codex P2, #154); and the next name
  // is taken past everything the session ever named, so a lifted number is
  // never reused (Codex P2, #154). Object URLs are made once per take.
  const heroTakes = new Map<string, Uint8Array>();
  const heroTakeUrls = new Map<string, string>();
  // The bytes belong to the DOCUMENT, not the tool: a load begins a lineage
  // (store.lineage), undo never crosses it, and a saved Document of another
  // character may name `take-01.png` too — so the store is emptied when the
  // lineage changes, never carried across (Codex P1, #154 round four).
  let heroTakesLineage = store.lineage();
  store.subscribe(() => {
    if (store.lineage() === heroTakesLineage) return;
    heroTakesLineage = store.lineage();
    for (const url of heroTakeUrls.values()) URL.revokeObjectURL(url);
    heroTakeUrls.clear();
    heroTakes.clear();
  });
  const heroTakeUrl = (name: string): string | null => {
    const bytes = heroTakes.get(name);
    if (bytes === undefined) return null;
    let url = heroTakeUrls.get(name);
    if (url === undefined) {
      url = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: 'image/png' }));
      heroTakeUrls.set(name, url);
    }
    return url;
  };
  let takingHero = false;
  // The batch door's state (ADR-035), declared HERE: the package panel's deps
  // read it at render, and a persisted workspace renders that panel at boot,
  // before main reaches the door (the Codex P1 #152 smoke guards exactly this).
  let batchRunning = false;
  let batchCancel = false;
  /** The source armed by the FIRST gesture (Codex P1, #162 r3: a second native picker needs its own user activation). */
  let batchArmed: { readonly source: BatchSource; readonly sheet: RosterSheet } | null = null;
  /**
   * One take, through one door: the frame is read from the GPU, refused if
   * blank, named, remembered, and starred when it is the first. `lens` is the
   * headshot's placement (ADR-035) or nothing (the live crop, phase 6);
   * `said` is what the status line adds after the take's own line.
   */
  const takeFrame = async (label: string, plan: () => { lens?: HeadshotLens; said?: string }): Promise<void> => {
    if (takingHero) return; // one take at a time — a second click mid-read is not a second frame
    takingHero = true;
    try {
      // The scene at the gesture: a character dropped while the GPU read was
      // awaited is another lineage, and its package block must not receive
      // this frame (Codex P2, #154 round two).
      const scene = subject.run.scene;
      const { lens, said } = plan();
      const pixels = await viewport.captureSquare(HERO_SIZE, lens);
      if (subject.run.scene !== scene) throw new Error('the character changed while the take was read — take it again');
      const take = heroTakeFromPixels(pixels);
      // The choices are re-read AFTER the wait: a field edited meanwhile would
      // otherwise be reverted by a whole-block write of the stale snapshot.
      const choices = store.getDocument().package ?? defaultPackageChoices();
      const name = nextTakeName([...heroTakes.keys(), ...choices.heroTakes]);
      heroTakes.set(name, take.png);
      store.dispatch({ schemaVersion: '1.0.0', verb: 'package.set', params: { package: withHeroTake(choices, name) } });
      noteStatus(said === undefined ? heroTakeLine(name, take.facts) : `${heroTakeLine(name, take.facts)} · ${said}`);
    } catch (err) {
      noteStatus(`${label}: ${refusalLine(err)}`, err);
    } finally {
      takingHero = false;
    }
  };
  const takeHero = (): Promise<void> => takeFrame('hero', () => ({}));
  let portraitStage: ReturnType<typeof createPortraitStage> | null = null;
  const takeHeadshot = async (): Promise<void> => {
    if(takingHero) return;
    takingHero=true;
    try {
      const scene=subject.run.scene;
      const doc=store.getDocument();
      const {derived}=await produceExportGlb([]);
      portraitStage ??= createPortraitStage();
      const id=doc.package?.id ?? '';
      const portrait=await portraitStage.capture(derived.glb,PORTRAIT_ADJUSTMENTS[id]);
      if(subject.run.scene!==scene || store.getDocument()!==doc) throw new Error('The character or its edits changed during the portrait; reshoot it.');
      const choices=doc.package ?? defaultPackageChoices();
      const name=nextTakeName([...heroTakes.keys(),...choices.heroTakes]);
      heroTakes.set(name,portrait.png);
      store.dispatch({schemaVersion:'1.0.0',verb:'package.set',params:{package:withHeroFavourite(withHeroTake(choices,name),name)}});
      noteStatus(`Portrait taken and selected: ${name} ? neutral pose, framed from ${portrait.headBone}.`);
    } catch(error) { noteStatus(`headshot: ${refusalLine(error)}`,error); }
    finally { takingHero=false; }
  };
  // The 1:1 crop guide: a centred square over the viewport, the crop 📷 takes.
  const heroGuide = document.createElement('div');
  heroGuide.className = 'hero-guide';
  heroGuide.hidden = true;
  heroGuide.setAttribute('aria-hidden', 'true');
  const viewportCenter = document.getElementById('viewport-center');
  if (!(viewportCenter instanceof HTMLElement)) throw new Error('viewport-center missing — the hero guide has nowhere to sit');
  viewportCenter.append(heroGuide);
  const fitHeroGuide = (): void => {
    const side = Math.min(viewportCenter.clientWidth, viewportCenter.clientHeight);
    heroGuide.style.width = `${side}px`;
    heroGuide.style.height = `${side}px`;
  };
  new ResizeObserver(fitHeroGuide).observe(viewportCenter);
  const showHeroGuide = (on: boolean): void => {
    if (on) fitHeroGuide();
    heroGuide.hidden = !on;
  };

  // ───────── ADR-033 phase 5: the library the Package panel reads ──────────
  // Read ONCE on the panel's first render (prod, same origin), re-read when a
  // bake folder is opened. Not on the store: the library is not the work and
  // not how you look at it — it is World's state of affairs, fetched. The
  // panel re-renders through `libraryListeners` when it changes.
  let libraryRead: LibraryRead | null = null;
  let libraryInFlight: Promise<void> | null = null;
  /** A forced re-read asked while one was in flight — honoured after it settles (Codex P2, #152). */
  let libraryForcePending = false;
  let bakeFolder: BakeFolder | undefined;
  const libraryListeners = new Set<() => void>();
  const notifyLibrary = (): void => {
    for (const l of libraryListeners) l();
  };
  const ensureLibrary = (force = false): Promise<void> => {
    if (libraryInFlight !== null) {
      if (force) libraryForcePending = true;
      return libraryInFlight;
    }
    if (libraryRead !== null && !force) return Promise.resolve();
    libraryInFlight = readLibrary({ fetchFn: fetch, base: WORLD_BASE, ...(bakeFolder !== undefined ? { bake: bakeFolder } : {}) })
      .then((read) => {
        libraryRead = read;
        const pool = read.bake?.manifest.family ?? read.prod.find((p) => p.manifest !== null)?.manifest?.family;
        noteStatus(
          pool === undefined
            ? `library: nothing readable — ${read.errors[0] ?? 'no family manifest'}`
            : `library: ${pool.id} ${pool.version} · ${pool.clips.length} clips${read.bake !== null ? ` from ${read.bake.name}` : ' from prod'}${read.roster !== null ? ` · roster ${read.roster.rows.length} rows` : ''}${read.errors.length > 0 ? ` · ${read.errors.length} read(s) failed` : ''}`,
        );
      })
      .catch((err: unknown) => {
        noteStatus(`library: ${refusalLine(err)}`, err);
      })
      .finally(() => {
        libraryInFlight = null;
        notifyLibrary();
        if (libraryForcePending) {
          libraryForcePending = false;
          void ensureLibrary(true);
        }
      });
    return libraryInFlight;
  };
  const openBakeFolder = async (): Promise<void> => {
    const picked = await pickBakeFolder();
    if (picked === null) {
      noteStatus('bake folder: nothing chosen');
      return;
    }
    const folder = bakeFolderFromFiles(picked.name, picked.files);
    if (folder === null) {
      noteStatus(`bake folder: "${picked.name}" has no manifest.json at its root — not a bake`);
      return;
    }
    bakeFolder = folder;
    await ensureLibrary(true);
  };
  /**
   * The panel's ▶: bytes (folder, then prod; memoized per library read) to
   * the phase-4 door, under a generation taken at the gesture so a slow
   * fetch can neither override a newer choice nor restart a stop
   * (library-player.ts).
   */
  const stageRig = (read: LibraryRead): { rig: LibraryForRig } | { rig: null; reason: string; packageOnly: boolean } => {
    // The SESSION's names, not the package's: this preview binds a family
    // clip by name onto the session skeleton, absolutely (World's rule). A
    // rig preset's frames are re-seated only in the PACKAGE
    // (canonicalizeForFamily), so previewing on its own names would show
    // the twist World will not — it keeps refusing until the preview speaks
    // the canon.
    // A view answers with a LibraryForRig whenever it has an identity — a
    // FAMILY is what makes it playable (detection.family null = no pool; the
    // field read 2026-09-25: every tailed preset was sent to play and refused).
    const view = packagePanelView({ doc: store.getDocument(), boneNames: subject.run.rig.bones.map((b) => b.name), library: read });
    if (view.rig !== null && view.rig.detection.family !== null) return { rig: view.rig };
    const packaged = packagePanelView({ doc: store.getDocument(), boneNames: packageBoneNames(subject.run.rig), library: read });
    const packageOnly = packaged.rig !== null && packaged.rig.detection.family !== null;
    return {
      rig: null,
      packageOnly,
      reason: packageOnly
        ? 'this body takes the family skeleton only in its package (renamed, re-seated) — played here on its own bones the clip would twist, so the stage cannot preview it yet'
        : (view.rig?.reasons[0] ?? view.library.reasons[0] ?? 'no pool for this rig'),
    };
  };
  const libraryPlayer = createLibraryPlayer({
    read: () => libraryRead,
    subject: () => subject.run.scene,
    rig: (read) => stageRig(read),
    resolve: ({ read, rig, clipId }) => resolveClipBytes({ read, rig, clipId, base: WORLD_BASE, fetchBytes: fetchBytesStrict(fetch) }),
    preview: (clipId, bytes) => previewLibraryClip(clipId, bytes),
    refused: (err) => noteStatus(`library: ${refusalLine(err)}`, err),
  });
  const playLibraryClip = async (clipId: string | null): Promise<void> => {
    await libraryPlayer.play(clipId);
  };

  const workspaceShell = mountWorkspaceShell(
    { left: zoneEl('zone-left'), right: zoneEl('zone-right'), bottom: zoneEl('zone-bottom') },
    store,
    registry,
  );
  // Pose mode stops the mixer (§5.3) — nothing may re-sample a bone the pose
  // layer owns. Registered before the projector so a projector refusal on a
  // genuinely broken Document cannot leave playback state stale.
  //
  // RESUMING is a mixer birth too (Codex P2, #61 r7). three re-captures each
  // binding's "original state" whenever an action goes from stopped to
  // playing — not only when the AnimationMixer object is new. Leaving pose
  // mode with a pose still on the bones would therefore record that pose as
  // the action's rest, exactly as a fresh mixer would, and a later stop
  // (re-entering pose mode, or switching clips) would stamp it back onto a
  // bone the projector no longer tracks. presentOnRests guarded only
  // viewport.present(); this is the other door into the same room, so it gets
  // the same laundering — and the reproject subscriber, registered after this
  // one, re-covers the pose on the same notify.
  store.subscribe(() => {
    // The projector clear is keyed to the MODE, not to the pause: leaving pose
    // mode is what makes a projection stale. A pin hold pauses without ending
    // the pose, and must not drop the pose the projector is holding.
    if (store.getShell().mode !== 'pose') subject.projector.project([]);
    applyMixerPause();
  });

  // The timeline projection (§13.4, §5.2 rule 4): the ACTIVE authored clip is
  // what the mixer samples, rebuilt whenever its FROZEN identity changes —
  // dispatch, undo, redo alike. Undo a key.set and the presented clip simply
  // no longer contains it; there is no second store left to re-assert it.
  // THREE presentation states, so the stage never lies (Codex P2, #61 r3 — a
  // keyless selection must not masquerade as the default clip):
  //   default  — activeClip null: the Document's default presentation plays.
  //   authored — the staged clip has keys: the mixer samples exactly those.
  //   silence  — the staged clip is keyless (or undone out of the Document):
  //              the subject rests, NO clip plays. That IS the clip's current
  //              truth — playing the unrelated default walk while the shell
  //              reports this clip active would be the silent substitution §8
  //              forbids. First key.set flips it to authored; redo restores an
  //              undone clip the same way.
  // Registered BEFORE the pose projector so a re-present (which restores
  // bound rests) is immediately re-covered by the pose layer on the same
  // notify (§5.2's precedence, kept in order).
  /**
   * The CURRENT subject's default presentation — its first retargeted clip,
   * converted once per run object. Never the boot-time `playable` (Codex P1,
   * #126 round two): after a drop or a graft the subject is a different run,
   * and presenting the boot clip on it would play the android's walk on the
   * dropped character. The head-seating commit re-presents through this
   * path, which made the latent case common.
   */
  const defaultPlayableCache = new WeakMap<object, ReturnType<typeof toThreeClip> | null>();
  const defaultPlayable = (): ReturnType<typeof toThreeClip> | null => {
    const run = subject.run;
    let clip = defaultPlayableCache.get(run);
    if (clip === undefined) {
      const [first] = run.clips;
      clip = first === undefined ? null : toThreeClip(first.result.clip);
      defaultPlayableCache.set(run, clip);
    }
    return clip;
  };
  type StageState =
    | { kind: 'default' }
    | { kind: 'authored'; clip: AuthoredClip; fps: number }
    | { kind: 'silence' }
    | { kind: 'library'; clipId: string; playable: ReturnType<typeof toThreeClip> };
  let stage: StageState = { kind: 'default' };
  /**
   * Library clips bound to THIS subject (ADR-033 phase 4), by clipId. Keyed
   * by the SCENE, not the run object: a head-seating reseat spreads a new
   * run over the same scene and node names (Codex P2, #151), while a graft
   * or a drop builds a new scene — so the binding survives exactly the
   * changes it is valid across, and the WeakMap forgets with the scene. A
   * previewed id with no binding here plays the default, never a stale one.
   * Each binding remembers the BYTES it was bound from: bytes handed in for
   * an id already bound are the caller saying "this artifact" — a re-opened
   * or re-built bake folder names new bytes under the same id — so a
   * different buffer rebinds and replaces, the same buffer is a hit (Codex
   * P2, #151 round two).
   */
  interface LibraryBinding {
    readonly bytes: ArrayBuffer;
    readonly clip: ReturnType<typeof toThreeClip>;
    /** What the bind measured — said again on a hit, so A → B → A reads A's line, not B's (Codex P2, #151 round four). */
    readonly info: { bound: number; total: number; renamed: number; unresolved: readonly string[]; hip: HipScaleDecision | null };
    /** The subject's rest hip length the hip keys were scaled against — a reseat that moves it rebinds from the held bytes. */
    readonly subjectHipRest: number | null;
  }
  /** |localBind.position| of the subject's hip bone (World reads the same from the bone's rest matrix); null when the rig has none. */
  const subjectHipRest = (): number | null => {
    const p = subject.run.rig.bones.find((b) => isHipBoneName(b.name))?.localBind.position;
    return p === undefined ? null : Math.hypot(p.x, p.y, p.z);
  };
  // The hip line says what World will do to the keys (its hipHeightCompensation
  // rule, mirrored in the binder): a scaled factor is a fact the author reads
  // on a short-legged body; an unmeasurable hip is a warning, not silence.
  const hipLine = (hip: HipScaleDecision | null): string =>
    hip === null || hip.reason === 'identity'
      ? ''
      : hip.reason === 'scaled'
        ? ` · hip keys ×${hip.factor.toFixed(3)} (rest ${hip.subjectRestLength?.toFixed(3)} vs the clip's ${hip.donorRestLength?.toFixed(3)})`
        : hip.reason === 'grounded-pose'
          ? ' · hip keys as authored (death)'
          : ' · hip height UNMEASURABLE — keys bound raw';
  const libraryLine = (clipId: string, b: LibraryBinding['info']): string =>
    `library: ${clipId} · ${b.bound}/${b.total} tracks bound${b.renamed > 0 ? ` (${b.renamed} onto grafted bones)` : ''}` +
    (b.unresolved.length > 0 ? ` · not on this character: ${b.unresolved.slice(0, 4).join(', ')}${b.unresolved.length > 4 ? ', …' : ''}` : '') +
    hipLine(b.hip);
  const libraryBindings = new WeakMap<object, Map<string, LibraryBinding>>();
  /** Bumped per preview request; a load that finishes for an older request writes nothing (Codex P2, #151). */
  let previewGeneration = 0;
  const resolvePresentation = (): void => {
    const shell = store.getShell();
    const name = shell.activeClip;
    const doc = store.getDocument();
    const authored = name === null ? undefined : doc.clips.find((c) => c.name === name);
    const library = shell.libraryClip === null ? undefined : libraryBindings.get(subject.run.scene)?.get(shell.libraryClip)?.clip;
    const next: StageState =
      shell.libraryClip !== null && library !== undefined
        ? { kind: 'library', clipId: shell.libraryClip, playable: library }
        : name === null
          ? { kind: 'default' }
          : authored !== undefined && hasPlayableKeys(authored)
            ? { kind: 'authored', clip: authored, fps: doc.fps.value }
            : { kind: 'silence' };
    // "Unchanged" is PLAYBACK equality, never reference identity (Codex P2,
    // #61 r4): every applyCommand reparses the Document, so an UNRELATED edit
    // — a pose, another clip's key — rebuilds this clip's object with
    // identical content. Re-presenting on that would stop the mixer and reset
    // playback to zero. playbackEquals compares what the mixer samples (the
    // reference fast-path inside it still catches undo/redo's retained
    // objects); fps is compared alongside because the presented clip is baked
    // at the Document's declared rate.
    const unchanged =
      next.kind === stage.kind &&
      (next.kind === 'library'
        ? stage.kind === 'library' && stage.playable === next.playable
        : next.kind !== 'authored' ||
          (stage.kind === 'authored' && stage.fps === next.fps && playbackEquals(stage.clip, next.clip)));
    if (unchanged && !stageInvalid) return; // same state, same playback — nothing moved
    stageInvalid = false; // a reseat asked for this: the moved rest becomes the new mixer's original
    stage = next;
    // Through the ONE door (§ the law of mixer birth, above): the scene holds
    // imported rests when the new mixer is born, so no pose can be captured as
    // its "original" and stamped back by a later stop. Measured, #61 r5:
    // presenting bare here settles Hip 0.349 off rest — a pose already undone.
    // The reproject subscriber, registered after this one, re-covers the pose
    // layer on this same notify.
    presentOnRests(
      next.kind === 'default'
        ? defaultPlayable()
        : next.kind === 'authored'
          ? toThreeClip(authoredClipData(next.clip, doc.fps.value))
          : next.kind === 'library'
            ? next.playable
            : null,
    );
  };
  store.subscribe(resolvePresentation);

  /**
   * The library preview door (ADR-033 phase 4): bytes → the clip → bound to
   * THIS subject → the shell says which id plays. The bytes come from the
   * caller (the panel resolves folder-vs-prod; the probe hands them in), so
   * this door never fetches. The hit rate is said on the status line — a
   * clip that misses bones on this character is a fact the author needs
   * before pinning it. Null stops the preview.
   */
  const previewLibraryClip = async (
    clipId: string | null,
    bytes?: ArrayBuffer,
    isCurrent: () => boolean = () => true,
  ): Promise<{ bound: number; total: number; unresolved: readonly string[] } | null> => {
    const generation = ++previewGeneration;
    if (clipId === null) {
      // The status line said which library clip was playing; a stop must say
      // the stage went back, not leave that claim standing (Codex P2, #151 round five).
      if (store.getShell().libraryClip !== null) noteStatus(`library: preview of ${store.getShell().libraryClip} stopped`);
      store.previewLibraryClip(null);
      return null;
    }
    const scene = subject.run.scene;
    let held = libraryBindings.get(scene)?.get(clipId);
    // A hit is the same id bound from the SAME bytes (or no bytes offered); other bytes rebind.
    // A reseat keeps the scene but may move the hip's bind (ADR-029/030); the
    // scaled keys were computed against the old rest, so the held bytes rebind.
    const hipRest = subjectHipRest();
    if (held === undefined || (bytes !== undefined && bytes !== held.bytes) || held.subjectHipRest !== hipRest) {
      const source = bytes ?? held?.bytes;
      if (source === undefined) throw new Error(`library clip "${clipId}" is not bound to this subject and no bytes were given`);
      const loaded = await loadLibraryClip(source);
      // Superseded while loading — a newer preview (or a stop) has spoken; write nothing.
      if (generation !== previewGeneration || !isCurrent()) return null;
      if (subject.run.scene !== scene) throw new Error(`the subject changed while "${clipId}" loaded — preview it again`);
      const b = bindLibraryClip(loaded.clip, scene, { donor: loaded.hipRest, rig: subject.run.rig });
      if (b.bound === 0) throw new Error('None of this animation’s tracks match the current character.');
      held = {
        bytes: source,
        clip: b.clip,
        info: { bound: b.bound, total: b.total, renamed: b.renamed, unresolved: b.unresolved, hip: b.hip },
        subjectHipRest: hipRest,
      };
      let forScene = libraryBindings.get(scene);
      if (forScene === undefined) {
        forScene = new Map();
        libraryBindings.set(scene, forScene);
      }
      forScene.set(clipId, held);
    }
    noteStatus(libraryLine(clipId, held.info));
    store.previewLibraryClip(clipId);
    return { bound: held.info.bound, total: held.info.total, unresolved: held.info.unresolved };
  };

  let posedBones: readonly string[] = [];
  const reproject = (): void => {
    posedBones = subject.projector.project(current(store.getHistory()).edits.poses);
  };
  store.subscribe(reproject);
  reproject(); // establish the baseline (seed poses: [])

  // ---- the EXPORT door (ADR-008 §2i egress; ratified 2026-07-27) ----
  // The exported file is the post-graft truth: geometry + augmented skeleton +
  // every clip the run carries. Node TRS is read at write time, so the scene
  // must hold RESTS when the exporter parses: mixer stopped (bound state
  // restored) and the pose projection cleared — the same discipline as the
  // present door, for the same reason. Both are re-covered afterwards; a
  // half-second freeze of playback is the honest price of a clean file.
  // `subject` is read through, never captured (the #61 lesson).
  const shellChromeBar = document.getElementById('shell-chrome');
  if (!(shellChromeBar instanceof HTMLElement)) {
    throw new Error('shell-chrome missing — mountUndoChrome must run before the export door mounts into it');
  }
  mountExportChrome(shellChromeBar, async () => {
    // ADR-040: inside a World check the export IS the intake export (body only,
    // World's skeleton, re-checked, left for World) — one door, not two.
    if (intake.active()) {
      await intake.exportForWorld();
      return;
    }
    // WHERE first, while the click's activation is fresh (save-glb header):
    // serialize + derive can take >10s on a mastered character, after which
    // the picker is refused and the file silently lands in Downloads. The
    // clip list is cheap data refs; the filename it suggests is the same one
    // the anchor fallback uses.
    // Every exported clip carries the helpers' baked motion (ADR-028's bake
    // door): crease-band flesh is weighted to the helpers, so a clip without
    // their tracks would re-import with the correction frozen at bind —
    // elbows worse than the disease (Codex P1, #103).
    const exportClips = () => [
      ...subject.run.clips.map((c) => c.result.clip),
      ...subject.run.authoredClips.map((a) => a.clip),
    ].map((c) => bakeHingeTracks(c, subject.run.hinges, subject.run.rig, { includeLeafTranslations: true }));
    const label = current(store.getHistory()).target.label;
    const filename = exportFilename(label, exportClips().map((c) => c.name));
    const target = await pickSaveTarget(filename);
    if (target.kind === 'cancelled') {
      noteStatus('export cancelled — nothing written');
      return;
    }
    try {
      await awaitElbowProjection();
      const { res, derived } = await produceExportGlb(exportClips());
      const landed = await writeSaveTarget(target, derived.glb, filename);
      // A handle that went bad mid-export lands in Downloads instead; say so
      // rather than report the chosen destination that never received it.
      const fellBack = target.kind === 'picker' && landed === 'anchor' ? ' (chosen location refused — downloaded instead)' : '';
      const grafted = res.renamed.length > 0 ? `, ${res.renamed.length} grafted bones under canonical names` : '';
      // Per ROLE, because the ceilings are (#133: albedo 2048, data maps
      // 1024) — the old "≤1024" read as a lie beside a 2048 albedo.
      const texNote = derived.report.changed
        ? `, ${derived.report.images.length} texture(s) derived (${derived.report.images
            .map((i) => `${i.roles.join('/')} ${i.to[0]}`)
            .join(', ')})` +
          (derived.report.bakedRoughness.length > 0
            ? ` (roughness ×${derived.report.bakedRoughness.map((b) => b.factor).join(', ×')} baked)`
            : '')
        : '';
      // A cap is never silent: what the door LEFT OUT of the file is part of
      // the answer, not a console footnote (the tiger export, 2026-09-02 —
      // fourteen discs shipped and nothing on this line said a word).
      const leftOut = res.excluded.length > 0 ? ` · left out of the file: ${res.excluded.join(', ')}` : '';
      // An export IS the occasional confirmation the hidden-by-default pill
      // exists for (Allen, 2026-07-28) — and the line now carries what the door
      // left out. Written onto a hidden pill it reached nobody (the tiger read,
      // 2026-09-02: "no message re export in the status line"). Reveal; the
      // backtick still puts it away.
      revealStatus();
      noteStatus(`exported ${filename} — ${res.animations.length} animation(s)${grafted}${texNote}${fellBack}${leftOut}`);
    } catch (err) {
      noteStatus(refusalLine(err), err);
    }
  });

  /**
   * The exported BODY, one way for every door that writes a file: the
   * export door (all clips) and the package door (no clips — World disposes
   * embedded animations, ADR-033). Playback held and the pose cleared so the
   * exporter parses RESTS; arm-space declared on the nodes; detached material
   * channels baked; textures derived to the delivery ceiling. Everything it
   * moves is put back in `finally`, refusal or not.
   */
  /**
   * ADR-043 M0: the mood faces the subject's SOURCE file declares, read from
   * the bytes the session holds for it (a reopened Document relinks the same
   * file). Null for a source with none, and for FBX bodies (no variants).
   * Refuses — never skips — a mood the carrier cannot keep whole.
   */
  const sourceMoods = async (): Promise<MoodSet | null> => {
    const t = store.getDocument().target;
    if (t.specimen.format !== 'glb') return null;
    // The run's own resolver: an upload's held bytes, or a bundled specimen's
    // fetch (the boot character is never an upload — Codex P1, #259).
    return readMoodVariants(await appIO.loadSpecimen(t.specimen));
  };
  const produceExportGlb = async (clips: NonNullable<NonNullable<Parameters<typeof exportGlb>[1]>['clips']>): Promise<{ res: Awaited<ReturnType<typeof exportGlb>>; derived: ReturnType<typeof deriveGlbTextures> }> => {
    await awaitElbowProjection();
    // ONE producer in flight, across both doors: the scene is borrowed (renames,
    // debris removed, rests held) for the whole serialize, and a second caller
    // entering mid-way would have the first's `finally` put the scene back
    // under it (Codex P1, #156). The second door refuses loudly, never queues.
    if (exportHold) throw new Error('an export is already in progress — wait for it to finish');
    exportHold = true;
    applyMixerPause();
    subject.projector.project([]);
    // Derived helpers must be at REST when the exporter parses TRS — the RAF
    // driver may not fire between this clear and the synchronous parse below.
    driveHinges();
    // ...and the CREASE INFLUENCES must be zero, which driveHinges cannot do
    // for us. Exporting with playback stopped on a bent elbow leaves a nonzero
    // influence on the mesh, and GLTFExporter serialises those as the mesh's
    // DEFAULT `weights` — so the file opens permanently creased in every
    // third-party reader, since the exported clips carry helper and leaf
    // tracks but deliberately no morph tracks (D5: World drives the ramp at
    // play time). The model's default state is its REST state (Codex P1, #113).
    // Restored below by the same reproject() the pose path uses.
    for (const drive of subject.hingeDrives) {
      for (const morph of drive.morphDrives) {
        for (const t of morph.targets) t.influences[t.index] = 0;
      }
    }
    try {
      // A DETACHED channel is carried into the file by BAKING its constant into
      // the metallicRoughness plane. glTF reads both channels from one texture
      // whenever it is present, so "metallic from the map, roughness flat" is
      // not expressible as a material property — the surface can do it (three
      // keeps two independent map slots) and the file cannot. The value baked
      // is the one the viewport is showing: the channel's own factor.
      const materialEdits = current(store.getHistory()).edits.materials ?? [];
      const shippedOf = new Map(materialFactorTargets(subject.run.scene).map((t) => [t.name, t]));
      const flatten = materialEdits.flatMap((edit) => {
        const target = shippedOf.get(edit.material);
        const out: { material: string; channel: 'roughness' | 'metallic'; value: number }[] = [];
        if (edit.roughnessFromMap === false) {
          out.push({ material: edit.material, channel: 'roughness', value: edit.roughnessFactor ?? target?.shippedRoughness ?? 1 });
        }
        if (edit.metallicFromMap === false) {
          out.push({ material: edit.material, channel: 'metallic', value: edit.metallicFactor ?? target?.shippedMetallic ?? 1 });
        }
        return out;
      });
      // THE MAPS MUST BE ATTACHED WHILE THE EXPORTER RUNS. Detaching sets
      // three's map slots to null, so with both channels off GLTFExporter emits
      // no metallicRoughnessTexture at all — and the derive then has no plane
      // to write the constant into and refuses (Codex P1, #115). The file
      // carries a detachment as a BAKED texture, so the texture has to exist:
      // attach for the write, bake the constants in, then put the viewport
      // back exactly as the person left it.
      // ADR-030 §13 (ruled): arm-space is a play-time abduction, so the file
      // DECLARES the effective value on each upper-arm node — World's reader
      // applies it, this app's reopen seeds from it, everyone else plays the
      // authored rest. Written at the door from the value the viewport shows;
      // withdrawn when neutral and nothing was declared before.
      {
        const docNow = current(store.getHistory());
        const effective = docNow.edits.armSpace ?? subject.run.armSpaceFile ?? 0;
        const sel = {
          label: docNow.target.label,
          ...(docNow.target.selectSkeleton !== undefined ? { selectSkeleton: docNow.target.selectSkeleton } : {}),
        };
        try {
          const bones = armSpacePlan(subject.run.rig, 1).map((r) => r.bone);
          if (effective === 0 && subject.run.armSpaceFile === null) {
            clearSceneArmSpace(subject.run.scene, bones, sel);
          } else {
            const plan = effective === 0 ? bones.map((bone) => ({ bone, axis: armSpacePlan(subject.run.rig, 1)[0]!.axis, radians: 0 })) : armSpacePlan(subject.run.rig, effective);
            writeSceneArmSpace(
              subject.run.scene,
              plan.map((r) => ({ bone: r.bone, marker: { character: effective, degrees: (r.radians * 180) / Math.PI, axis: r.axis } })),
              sel,
            );
          }
        } catch (err) {
          if (!(err instanceof ArmSpaceRefusal)) throw err;
          // A rig that cannot carry arm-space declares nothing — and had nothing set (the run refused the same way).
        }
      }
      // The moods are bound to the subject being serialized: sourceMoods reads
      // the Document's target SYNCHRONOUSLY here, in the same turn exportGlb
      // takes the scene, so a character opened mid-export can neither drop
      // these moods nor lend its own (Codex P1, #259).
      const moodsOfThisSubject = sourceMoods();
      // Awaited below; a refusal before then must not surface as an unhandled rejection.
      moodsOfThisSubject.catch(() => undefined);
      let res;
      if (flatten.length > 0) {
        const attached = new Map(
          [...materialFactorValues(materialEdits)].map(([name, values]) => [
            name,
            { ...values, metallicFromMap: true, roughnessFromMap: true },
          ]),
        );
        applyMaterialFactors(subject.run.scene, attached);
        try {
          res = await exportGlb(subject.run.scene, { clips, springs: [...tailSpring.springsForExport(subject.run), ...softTissue.springsForExport(subject.run)], springExtras: springExtrasForExport() });
        } finally {
          // ALWAYS, not on the success path (Codex P1, #115): exportGlb refuses
          // for its own reasons — an unresolved track target, a ghost riding a
          // bone — and a refusal used to leave the viewport obeying maps that
          // the checked-off switches and the Document both say to ignore, with
          // nothing to put it back until some unrelated edit re-applied.
          applyMaterialsFilter();
        }
      } else {
        res = await exportGlb(subject.run.scene, { clips, springs: [...tailSpring.springsForExport(subject.run), ...softTissue.springsForExport(subject.run)], springExtras: springExtrasForExport() });
      }
      // The ADR-020 §5 T2 cutover: for a mastered (8K-class) character this
      // derive IS the texture path — channel-aware down to the delivery
      // ceiling, >1 roughness factors baked so the file stays spec-clean.
      // A delivery-class file passes through byte-identical (changed:false);
      // a refusal (§7) throws into the catch below and reaches the surface.
      // ADR-043 M0: the source's mood faces (KHR_materials_variants) ride the
      // export. GLTFExporter writes only the scene's materials, so before this
      // every export silently kept the default face and dropped the rest. They
      // are re-applied BEFORE the derive, so mood images take the same
      // delivery ceiling as the base colour they replace.
      const moods = await moodsOfThisSubject;
      const carried = moods === null ? null : applyMoodVariants(res.glb, moods);
      // A mood clone shares its base's metallic-roughness image, so a channel
      // the person detached on the base is detached on its clones too, or the
      // derive refuses them as sharers that disagree (Codex P1, #259).
      const flattenAll = carried === null ? flatten : [...flatten, ...flatten.flatMap((f) => carried.clones.filter((c) => c.base === f.material).map((c) => ({ ...f, material: c.material })))];
      const derived = deriveGlbTextures(carried?.glb ?? res.glb, flattenAll.length > 0 ? { flatten: flattenAll } : {});
      return { res, derived };
    } finally {
      reproject();
      exportHold = false;
      applyMixerPause();
    }
  };

  // ---- the PACKAGE door (ADR-033 phase 7) ----
  // WHERE first, at the gesture (the #140 rule); then the body with NO clips
  // through the same producer as the export door, the starred take shrunk to
  // the record's card, `packageAvatar` (census from the FILE, the record
  // stamped in, sha256 of the body), and World's folder written: avatar.json
  // · <id>.glb · hero/<id>.png, other takes kept for the author. The status
  // line says what it wrote and what it left out. A derived id becomes the
  // Document's chosen id on the first package, so a re-package replaces its
  // own row (World's point 1).
  const HARRY_VERSION = `harryhausen ${__HARRY_VERSION__}`;
  const packageToPlan = async (): Promise<PackagePlan> => {
    await awaitElbowProjection();
    const doc = store.getDocument();
    const view = packagePanelView({ doc, boneNames: packageBoneNames(subject.run.rig), library: libraryRead, takesInSession: [...heroTakes.keys()] });
    if (!view.ready || view.rig?.detection.family === null || view.rig === null || view.identity.id === null) {
      throw new PackageRefusal(view.reasons.length > 0 ? view.reasons : ['the package is not ready']);
    }
    const choices = { ...view.choices, id: view.identity.id };
    if (choices.heroFavourite === undefined) throw new PackageRefusal(['no hero take starred']);
    const star = heroTakes.get(choices.heroFavourite);
    if (star === undefined) throw new PackageRefusal([`the starred take "${choices.heroFavourite}" has no PNG in this session — retake it`]);
    const { derived } = await produceExportGlb([]);
    // World plays the family's clips by NAME with absolute rotations: the
    // body leaves as the family's canonical skeleton (names, supplied unkeyed
    // bones, and — for a rig preset's other roll — the anchor's frames), the
    // mesh at rest, the tail and the spring unchanged.
    const canon = FAMILY_CANONS[view.rig.detection.family.id];
    const body = canon === undefined ? derived.glb : canonicalizeForFamily(derived.glb, ontologyOfRig(subject.run.rig), canon).glb;
    const packaged = await packageAvatar({
      body,
      doc,
      choices,
      family: view.rig.detection.family.id,
      set: PACKAGE_SET,
      harryVersion: HARRY_VERSION,
      thumbnail: heroThumbnailDataUri(star, 512),
      // The panel said these facets; the record must carry the same carry-over.
      replaces: view.identity.replaces,
    });
    return planPackageFolder({ record: packaged.record, glb: packaged.glb, takes: heroTakes, heroTakes: choices.heroTakes, heroFavourite: choices.heroFavourite });
  };
  /**
   * ADR-040: the intake export's body. WHERE first, at the gesture (the #140
   * rule); then the body with NO clips through the export door's producer
   * (World disposes embedded clips, and a re-seated frame would bend them), then
   * World's canonical skeleton — the done-for-you fixes (renames, supplied
   * unkeyed bones, re-seated frames) — exactly as the package door applies it.
   * The session re-checks the bytes this returns before it leaves anything for World.
   */
  /**
   * The Fix step's build (INTAKE_GUIDED_FLOW §3.4 D, §5 PR 2): the export door's body with NO clips
   * (World disposes embedded clips, and a re-seated frame would bend them), then World's canonical
   * skeleton — renames, supplied unkeyed bones, re-seated frames — exactly as the package door applies
   * it. No save dialog: the file save is *Save a copy* (ADR-040 §2 as amended). The session re-checks
   * the bytes this returns.
   */
  const buildForWorld = async (report: RigCheckReport | null): Promise<IntakeBuilt> => {
    const label = current(store.getHistory()).target.label;
    const filename = `${label.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9._-]+/g, '-')}-for-world.glb`;
    await awaitElbowProjection();
    const { res, derived } = await produceExportGlb([]);
    const fixes: string[] = [];
    const receipts: string[] = [];
    if (res.renamed.length > 0) {
      fixes.push(`${res.renamed.length} grafted bones`);
      receipts.push(`Added ${res.renamed.length} finger and tail bones.`);
    }
    let glb = derived.glb;
    const canon = FAMILY_CANONS['tripo-2026-handed'];
    // A VRoid body on World's VRoid library keeps its own skeleton; a stop has nothing to fix.
    if (canon !== undefined && report?.route !== 'vrm' && report?.verdict !== 'unsupported') {
      try {
        const out = canonicalizeForFamily(derived.glb, ontologyOfRig(subject.run.rig), canon);
        glb = out.glb;
        if (out.report.renamed.length > 0) {
          fixes.push(`renamed ${out.report.renamed.length} bones to World's skeleton`);
          receipts.push(`Renamed ${out.report.renamed.length} bones.`);
        }
        if (out.report.supplied.length > 0) {
          fixes.push(`supplied ${out.report.supplied.length} unanimated bones`);
          receipts.push(`Added ${out.report.supplied.length} bones World expects.`);
        }
        if (out.report.convention === 'reseated') {
          fixes.push(`re-seated the frames (median ${out.report.medianReseatDegrees.toFixed(1)}°)`);
          receipts.push('Straightened the arms and legs.');
        }
      } catch (err) {
        if (!(err instanceof FamilyCanonRefusal)) throw err;
        // The re-check speaks for the file; this says why the skeleton was left as it was.
        noteStatus(`World's skeleton could not be applied — ${refusalLine(err)}`, err);
      }
    }
    return { glb, filename, fixes, receipts };
  };
  /** Today's export for World: WHERE first, at the gesture (the #140 rule), then the same build. PR 3 retires the dialog from Send. */
  const intakeExportBody = async (report: RigCheckReport | null): Promise<IntakeExportBody | null> => {
    const label = current(store.getHistory()).target.label;
    const filename = `${label.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9._-]+/g, '-')}-for-world.glb`;
    const target = await pickSaveTarget(filename);
    if (target.kind === 'cancelled') return null;
    const built = await buildForWorld(report);
    return {
      glb: built.glb,
      filename: built.filename,
      fixes: built.fixes,
      write: async () => {
        const landed = await writeSaveTarget(target, built.glb, built.filename);
        return target.kind === 'picker' && landed === 'anchor' ? ' (chosen location refused — downloaded instead)' : '';
      },
    };
  };
  /**
   * E: World's walk on the built bytes, in the proof stage. The clip is the one World resolves for this
   * avatar's `walk` key (`libraryForRig(...).inherited`, the Package panel's reading), its bytes by the
   * library player's resolution, bound by World's rule on the built file's names.
   */
  let proofStage: ReturnType<typeof createProofStage> | null = null;
  window.__ANIMATOR_INTAKE__ = {
    proof: () => proofStage?.probe() ?? null,
    tail: () => {
      const v = tailSpring.view(subject.run);
      return { available: v.available, refusal: v.refusal, on: v.on };
    },
  };
  const showWorldWalk = async (glb: ArrayBuffer): Promise<{ ok: true } | { ok: false; reason: string }> => {
    try {
      await ensureLibrary();
      const read = libraryRead;
      if (read === null) throw new Error("World's animation library could not be read");
      const { rig } = await importGlb(glb.slice(0), deps, { label: 'built for World' });
      // The avatar's OWN entity path when it has a package identity (an avatar-specific set override then
      // resolves as World will; Codex P2, #242), else the facets' generic path.
      const doc = store.getDocument();
      const facets = doc.package?.facets ?? defaultPackageChoices().facets;
      const identityPath = packagePanelView({ doc, boneNames: packageBoneNames(subject.run.rig), library: read, takesInSession: [] }).identity.entityPath;
      const forRig = libraryForRig(read, rig.bones.map((b) => b.name), identityPath ?? entityPathFor(facets, 'intake'));
      const walk = forRig.inherited.find((b) => b.key === 'walk' && b.state === 'neutral') ?? forRig.inherited.find((b) => b.key === 'walk');
      const clipId = walk?.binding.clips[0];
      if (clipId === undefined) throw new Error(`World's library names no walk for this body${forRig.reasons[0] !== undefined ? ` — ${forRig.reasons[0]}` : ''}`);
      const { bytes } = await resolveClipBytes({ read, rig: forRig, clipId, base: WORLD_BASE, fetchBytes: fetchBytesStrict(fetch) });
      const host = document.getElementById('viewport-center');
      if (host === null) throw new Error('the stage is missing');
      proofStage ??= createProofStage(host);
      const played = await proofStage.play({
        glb,
        clip: bytes,
        rig,
        alphaRad: alphaForFacing(rig.facing),
        cardTop: () => {
          const card = document.querySelector('.intake-step-card')?.getBoundingClientRect();
          const box = host.getBoundingClientRect();
          return card === undefined ? box.height : card.top - box.top;
        },
      });
      noteStatus(`World's walk "${clipId}" on the built file: ${played.bound}/${played.total} tracks bound`);
      return { ok: true };
    } catch (err) {
      noteStatus(`World's walk: ${refusalLine(err)}`);
      return { ok: false, reason: refusalLine(err) };
    }
  };
  let packaging = false;
  const packageDoor = async (): Promise<void> => {
    if (packaging) return;
    packaging = true;
    try {
      const before = packagePanelView({ doc: store.getDocument(), boneNames: packageBoneNames(subject.run.rig), library: libraryRead, takesInSession: [...heroTakes.keys()] });
      if (!before.ready) throw new PackageRefusal(before.reasons);
      const dest = await pickPackageDestination(before.identity.id ?? 'avatar');
      if (dest.kind === 'cancelled') {
        noteStatus('package cancelled — nothing written');
        return;
      }
      const plan = await packageToPlan();
      const where = await writePackage(plan, dest);
      revealStatus();
      noteStatus(packageLine(plan, where));
      const choices = store.getDocument().package;
      if (choices !== undefined && choices.id === undefined) {
        store.dispatch({ schemaVersion: '1.0.0', verb: 'package.set', params: { package: withIdentity(choices, { id: plan.id }) } });
      }
    } catch (err) {
      noteStatus(`package: ${refusalLine(err)}`, err);
    } finally {
      packaging = false;
    }
  };

  // ---- the PACKAGE ALL door (ADR-035 §3.3) ----
  // Both pickers at the gesture (the #140 rule), the sheet parsed with every
  // issue listed before any body is touched, then the runner drives THIS
  // door's seams one body at a time. A second press while running cancels
  // between bodies. The report lands in the destination; the filled sheet
  // and the report go back beside the bodies when the source was writable.
  /**
   * Seat FRAME ZERO of the played clip and let one frame render it (ADR-035
   * §3.3 — what World seats for a still hero). A paused mixer shows the rest
   * pose and a frame count is not a time, so the clip is HELD at 0 s.
   */
  const settleFrames = (): Promise<void> =>
    new Promise((resolve, reject) => {
      try {
        viewport.holdAt(0);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      const start = viewport.probe.frames;
      const tick = (): void => {
        if (viewport.probe.frames >= start + 1) resolve();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  const batchDeps = (write: BatchDeps['write']): BatchDeps => ({
    sha256: (bytes) => sha256Hex(bytes),
    roster: () => libraryRead?.roster ?? null,
    open: async (file, bytes) => {
      // The SAME door a drop walks (ADR-032's lineage rule): a File built from the folder's bytes, the intent said.
      const seq = ++dropSeq;
      return importDropped(new File([bytes], file.name, { type: 'model/gltf-binary' }), 'character', seq);
    },
    setChoices: (choices) => store.dispatch({ schemaVersion: '1.0.0', verb: 'package.set', params: { package: choices } }),
    play: (clipId) => libraryPlayer.play(clipId),
    previewOnlyInPackage: () => {
      const read = libraryRead;
      if (read === null) return false;
      const stage = stageRig(read);
      return stage.rig === null && stage.packageOnly;
    },
    settle: settleFrames,
    takeHeadshot: async () => {
      const before = new Set(heroTakes.keys());
      await takeHeadshot();
      return [...heroTakes.keys()].find((n) => !before.has(n)) ?? null;
    },
    packageToPlan: () => packageToPlan(),
    write,
    cancelled: () => batchCancel,
  });
  const writeJsonTo = async (dir: { getFileHandle(name: string, o?: { create?: boolean }): Promise<{ createWritable(): Promise<{ write(b: Uint8Array): Promise<void>; close(): Promise<void> }> }> }, name: string, text: string): Promise<void> => {
    const w = await (await dir.getFileHandle(name, { create: true })).createWritable();
    await w.write(new TextEncoder().encode(text));
    await w.close();
  };
  /**
   * TWO gestures (Codex P1, #162 round three): Chromium grants a native
   * picker only transient user activation, spent by the first picker and the
   * read that follows it, so the destination is asked on a SECOND press. The
   * first press arms the source (folder + sheet, parsed with every issue
   * listed); the button then says so; the second press picks the destination
   * FIRST THING and runs. A press while running cancels between bodies.
   */
  const packageAllDoor = async (): Promise<void> => {
    if (batchRunning) {
      batchCancel = true;
      noteStatus('package all: stopping after the body in flight');
      return;
    }
    if (batchArmed === null) {
      // ── gesture 1: the source ──
      try {
        if (!batchPickerAvailable()) throw new Error('Package all needs a browser with a directory picker (Chrome or Edge): a batch writes folders and the filled sheet back');
        const source = await pickBatchSource();
        if (source === null) {
          noteStatus('package all: no source chosen — nothing done');
          return;
        }
        const sheetFile = source.files.find((f) => f.name.toLowerCase() === 'roster-sheet.json');
        if (sheetFile === undefined) throw new Error(`"${source.name}" has no roster-sheet.json beside the bodies — the batch does not invent one from filenames (ADR-035 §3.2)`);
        const sheet = parseRosterSheet(JSON.parse(await sheetFile.text()) as unknown, `${source.name}/roster-sheet.json`, { set: PACKAGE_SET });
        const glbs = source.files.filter((f) => /\.glb$/i.test(f.name)).map((f) => f.name);
        const rec = reconcileSheet(sheet, glbs);
        batchArmed = { source, sheet };
        noteStatus(
          `package all: "${source.name}" armed — ${rec.packageable.length} to package, ${rec.skipped.length} skipped${rec.bodiesWithoutRows.length > 0 ? `, ${rec.bodiesWithoutRows.length} bodies without a row` : ''}${rec.rowsWithoutBodies.length > 0 ? `, ${rec.rowsWithoutBodies.length} rows without a body` : ''} · press ⇪ again to choose the destination and run`,
        );
      } catch (err) {
        noteStatus(`package all: ${refusalLine(err)}`, err);
      } finally {
        notifyLibrary(); // the button reads the armed state
      }
      return;
    }
    // ── gesture 2: the destination, then the run ──
    const { source, sheet } = batchArmed;
    batchRunning = true;
    batchCancel = false;
    notifyLibrary();
    try {
      const dest = await pickPackageDestination('batch');
      if (dest.kind === 'cancelled') {
        noteStatus(`package all: no destination chosen — "${source.name}" stays armed; press ⇪ to choose one, or reload to disarm`);
        return;
      }
      if (dest.kind !== 'folder') throw new Error('package all needs a writable destination FOLDER — this browser offered only a zip or a download');
      batchArmed = null;
      await ensureLibrary();
      if (libraryRead === null) throw new Error('the library could not be read — no pool to assign from');
      revealStatus();
      const { report, sheet: filled } = await runBatch({
        sheet,
        source: source.name,
        files: source.files,
        deps: batchDeps((plan) => writePackage(plan, dest)),
        onRow: (row, done, total) => {
          noteStatus(`${batchLine(done, total, 0, false)} · ${row.ok ? `${row.name} → ${row.wrote}${row.heroAtRest ? ' (headshot at rest — its clips play only on the packaged skeleton)' : ''}` : `${row.file} refused: ${row.reasons[0]}`}${row.warnings.length > 0 ? ` · ⚠ ${row.warnings[0]}` : ''}`);
        },
      });
      // The FILLED SHEET first — it is the one file whose loss costs the ids
      // and clips the run derived — then the reports; every write is
      // attempted and a failure is listed, never one write skipping the rest
      // (Codex P2, #162 round four).
      const reportText = `${JSON.stringify(report, null, 2)}\n`;
      const writes: readonly { readonly what: string; readonly run: () => Promise<void> }[] = [
        { what: `${source.name}/roster-sheet.json`, run: () => writeJsonTo(source.dir, 'roster-sheet.json', serializeRosterSheet(filled)) },
        { what: `${source.name}/batch-report.json`, run: () => writeJsonTo(source.dir, 'batch-report.json', reportText) },
        { what: `${dest.dir.name}/batch-report.json`, run: () => writeJsonTo(dest.dir, 'batch-report.json', reportText) },
      ];
      const failed: string[] = [];
      for (const w of writes) {
        try {
          await w.run();
        } catch (err) {
          failed.push(`${w.what}: ${refusalLine(err)}`);
          console.error('[animator-v3] batch write failed', w.what, err);
        }
      }
      noteStatus(
        `${batchLine(report.rows.length, report.rows.length, report.refused, report.cancelled)} · ${report.packaged} packaged → ${dest.dir.name}/${failed.length === 0 ? ` · sheet written back to ${source.name}/roster-sheet.json` : ` · WRITE FAILED: ${failed.join(' · ')}`}${report.skipped.length > 0 ? ` · ${report.skipped.length} skipped` : ''}${report.bodiesWithoutRows.length > 0 ? ` · ${report.bodiesWithoutRows.length} bodies without a row` : ''}`,
      );
    } catch (err) {
      noteStatus(`package all: ${refusalLine(err)}`, err);
    } finally {
      batchRunning = false;
      batchCancel = false;
      notifyLibrary(); // the panel re-renders: the button reads ⇪ again
    }
  };

  // ---- the CLIPS door (delivery contract Artifact B; ADR-016) ----
  // Animation-only: the target rig's skeleton as plain nodes + every clip the
  // run carries, no mesh. Unlike the model door this needs NO rest ceremony —
  // the skeleton is built from the rig descriptor's localBind, which the live
  // scene's pose cannot touch. Retargeted clips carry their translation
  // (root-motion ruling: the fall lives in that channel); authored clips are
  // the user's own rotation-only material and export as they are.
  mountExportChrome(
    shellChromeBar,
    async () => {
      try {
        // Same bake-door law as the model export above (Codex P1, #103 round
        // three — THE THIRD missed door of this class in one arc): the
        // animation-only artifact carries the `_corr` skeleton nodes, so a
        // clip without their tracks plays the reweighted avatar with the
        // helper frozen at bind.
        const label = current(store.getHistory()).target.label;
        const filename = exportFilename(`${label}-clips`, [...subject.run.clips.map(c => c.result.clip.name), ...subject.run.authoredClips.map(a => a.clip.name)]);
        // WHERE first — same activation law as the model door (save-glb header).
        const target = await pickSaveTarget(filename);
        if (target.kind === 'cancelled') {
          noteStatus('clip export cancelled — nothing written');
          return;
        }
        await awaitElbowProjection();
        const exportingRun = subject.run;
        const clips = [
          ...exportingRun.clips.map(c => c.result.clip),
          ...exportingRun.authoredClips.map(a => a.clip),
        ].map(c => bakeHingeTracks(c, exportingRun.hinges, exportingRun.rig, { includeLeafTranslations: true }));
        const res = await exportAnimationOnly(exportingRun.rig, { clips });
        const landed = await writeSaveTarget(target, res.glb, filename);
        const fellBack = target.kind === 'picker' && landed === 'anchor' ? ' (chosen location refused — downloaded instead)' : '';
        const grafted = res.renamed.length > 0 ? `, ${res.renamed.length} grafted bones under canonical names` : '';
        noteStatus(`exported ${filename} — animation-only, ${res.animations.length} animation(s)${grafted}${fellBack}`);
      } catch (err) {
        noteStatus(refusalLine(err), err);
      }
    },
    { label: '⇩ Export Clips', ariaLabel: 'Export the animations only (no model) as a GLB clip file' },
  );

  // The pose gesture (§2.4): offered exactly while the FSM says pose mode —
  // in playback the tool does not exist, so the ambiguous drag of §5.3 is
  // unrepresentable rather than guarded. A pick flows into store.select
  // (Document ids); a released drag commits ONE pose.set through the ONE door.
  // The tool never writes the scene as truth — its preview is view state, and
  // the committed value arrives back via the projector like every other edit.
  // The beauty asset (seam #3): the carried v2 octahedral bone, extracted
  // fail-loud at boot — a missing mesh refuses NOW, at the loader, never as a
  // silently-degraded Blender-ish skeleton later (review §4.1 correction 3).
  const boneRes = await fetch(boneAssetUrl);
  if (!boneRes.ok) throw new Error(`octahedral bone asset fetch failed: HTTP ${boneRes.status}`);
  const boneGeometry = extractBoneGeometry((await loadGLB(await boneRes.arrayBuffer())).scene);

  /**
   * Built PER OFFERING from the CURRENT subject, never captured once.
   *
   * It was captured once, and the bug it caused is the reason this is a function:
   * boot mounts a 44-bone android, a hand graft remounts it at 64, and a config
   * frozen at boot kept drawing the 44. So the grafted fingers had bones, had
   * weights, deformed the mesh — and were INVISIBLE, which is indistinguishable
   * from not being there. Allen, looking at a hand he could not read: "without the
   * bones on the hands it's quite difficult to see."
   */
  const poseToolConfig = (): PoseToolConfig => ({
    boneNames: [...subject.poseTargets],
    mapped: mappedRigBones(subject.run.rig),
    geometry: boneGeometry,
    onSelect: (bone: string) => store.select([bone]),
    onCommit: (bone: string, rotation: { x: number; y: number; z: number; w: number }) =>
      store.dispatch({ schemaVersion: '1.0.0', verb: 'pose.set', params: { bone, rotation } }),
  });
  // Pick-walking (Allen, 2026-07-25 — the Maya idiom): arrows walk the
  // hierarchy from the current selection. Up=parent, Down=first child,
  // Left/Right=siblings (wrapping); hidden twists bridge exactly as the
  // skeleton draws them (one vocabulary — bone-vocabulary.ts). Deliberately
  // NOT Tab: that is browser focus traversal and the chrome lives on it —
  // hijacking it would be a keyboard No-Trap.
  //
  // Walks the CURRENT subject's rig, never a captured one — the same lesson
  // poseToolConfig (above) already carries: this walker was frozen over the
  // boot rig, so after a hand graft the grafted digits were unwalkable (down
  // from the hand: dead end; from a clicked finger joint: unknown bone) and
  // the field could not tell that from the feature not existing (Allen,
  // 2026-08-20: "difficult to select each joint without one" — over a hand
  // that HAD the hotkeys, aimed at the one skeleton they didn't know).
  let walkerCache: { rig: object; walker: PickWalker } | null = null;
  const currentWalker = (): PickWalker => {
    const rig = subject.run.rig;
    if (walkerCache === null || walkerCache.rig !== rig) {
      const walker = createPickWalker(
        rig.bones.map((b) => ({ name: b.name, parent: b.parent })),
        mappedRigBones(rig),
      );
      walkerCache = { rig, walker };
    }
    return walkerCache.walker;
  };
  const WALK_KEYS: Readonly<Record<string, WalkDirection>> = {
    ArrowUp: 'parent',
    ArrowDown: 'child',
    ArrowRight: 'nextSibling',
    ArrowLeft: 'prevSibling',
  };
  window.addEventListener('keydown', (event) => {
    const direction = WALK_KEYS[event.key];
    if (direction === undefined) return;
    // A modifier-bearing arrow is someone ELSE'S shortcut (Alt+Left = browser
    // back, Ctrl+arrows = OS/word navigation) — walking must never swallow
    // those (Codex P2, #59). Shift is reserved too: a future extend-selection.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    // Arrows inside a text field move the CARET — same guard the overlay key
    // wears; without it a selected bone hijacks typing in any panel input.
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (elbowHold || store.getShell().mode !== 'pose') return; // the tool's mode scopes its keys (§13.2)
    if (viewport.getPoseTool()?.probe.dragging) return; // never yank the gizmo mid-gesture
    const from = store.getShell().selection[0];
    if (from === undefined) return;
    const next = currentWalker().walk(from, direction);
    if (next === null) return; // a dead end is a fact, not a fault — selection stays
    event.preventDefault();
    store.select([next]);
  });

  let poseToolOffered = false;
  /** The subject the live tool was built over — a change means it must be rebuilt. */
  let poseToolSubject: Subject | null = null;
  const offerPoseTool = (): void => {
    const wantIt = store.getShell().mode === 'pose';
    // A remount is as much a reason to rebuild as a mode change. The tool holds
    // the subject's root and its bone list; after a graft both are stale, and a
    // stale tool draws the pre-graft skeleton over a post-graft character —
    // fingers that move the mesh and cannot be seen or picked.
    if (wantIt === poseToolOffered && (!wantIt || poseToolSubject === subject)) {
      // Same offering — but a store change may have moved the selection
      // (e.g. cleared); keep the tool's highlight resolving from the store.
      if (wantIt) viewport.getPoseTool()?.select(store.getShell().selection[0] ?? null);
      return;
    }
    poseToolOffered = wantIt;
    poseToolSubject = wantIt ? subject : null;
    viewport.setPoseTool(wantIt ? poseToolConfig() : null);
    if (wantIt) viewport.getPoseTool()?.select(store.getShell().selection[0] ?? null);
  };
  store.subscribe(offerPoseTool);
  offerPoseTool(); // resolve the initial offering (playback: withdrawn)

  const sampleBone = run.rig.bones[0]?.name ?? '';
  window.__ANIMATOR_SHELL__ = {
    snapshot: () => ({
      mode: store.getShell().mode,
      canUndo: store.canUndo().ok,
      canRedo: store.canRedo().ok,
      isDirty: store.isDirty(),
      lastAction: store.lastAction(),
      posedBones,
      selection: store.getShell().selection,
      activeClip: store.getShell().activeClip,
      libraryClip: store.getShell().libraryClip,
      sampleBone,
      tailWag: { on: tailWagging(), ...tailWag.probe() },
      tailSpring: tailSpring.probe(subject.run),
      softTissue: softTissue.probe(subject.run),
      surfaceJiggle: surfaceJiggle.probe(subject.run),
    }),
    dispatch: (command) => store.dispatch(command),
    undo: () => store.undo(),
    redo: () => store.redo(),
    selectClip: (name) => store.selectClip(name),
    previewLibraryClip: (clipId, bytes) => previewLibraryClip(clipId, bytes),
    takeHero: async () => {
      const before = new Set(heroTakes.keys());
      await takeHero();
      return [...heroTakes.keys()].find((n) => !before.has(n)) ?? null;
    },
    takeHeadshot: async () => {
      const before = new Set(heroTakes.keys());
      await takeHeadshot();
      return [...heroTakes.keys()].find((n) => !before.has(n)) ?? null;
    },
    heroTake: (name) => heroTakes.get(name) ?? null,
    runBatch: async (bodies, sheetRaw) => {
      // The smoke's door: the same runner, the same deps, an in-memory writer
      // — no pickers, nothing on disk. Returns the report, the filled sheet
      // and every composed plan's file list (bytes for the hero, for the eye).
      const sheet = parseRosterSheet(sheetRaw, 'smoke sheet', { set: PACKAGE_SET });
      await ensureLibrary();
      const packages: { id: string; files: { path: string; role: string; size: number }[]; hero: number[] }[] = [];
      const files = bodies.map((b) => ({ name: b.name, text: () => Promise.resolve(''), arrayBuffer: () => Promise.resolve(new Uint8Array(b.bytes).buffer as ArrayBuffer) }));
      const out = await runBatch({
        sheet,
        source: 'smoke',
        files,
        deps: batchDeps(async (plan) => {
          const hero = plan.files.find((f) => f.role === 'hero');
          packages.push({ id: plan.id, files: plan.files.map((f) => ({ path: f.path, role: f.role, size: f.bytes.length })), hero: hero === undefined ? [] : Array.from(hero.bytes) });
          return `memory/${plan.id}/`;
        }),
      });
      return { report: out.report, sheet: out.sheet, packages };
    },
    packageToPlan: async () => {
      const plan = await packageToPlan();
      const body = plan.files.find((f) => f.role === 'body')!;
      const digest = await crypto.subtle.digest('SHA-256', body.bytes.slice().buffer as ArrayBuffer);
      return {
        id: plan.id,
        files: plan.files.map((f) => ({ path: f.path, role: f.role, size: f.bytes.length })),
        missingTakes: plan.missingTakes,
        record: JSON.parse(new TextDecoder().decode(plan.files.find((f) => f.role === 'record')!.bytes)) as unknown,
        bodySha256: [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join(''),
      };
    },
    exportMoods: async () => {
      const { derived } = await produceExportGlb([]);
      const set = readMoodVariants(derived.glb);
      return set === null
        ? null
        : {
            variants: [...set.variants],
            bases: set.bases.map((b) => ({
              material: b.material,
              base: [...b.baseVariants],
              moods: b.moods.map((m) => ({ variants: [...m.variants], mime: m.mimeType, bytes: m.image.byteLength })),
            })),
          };
    },
    document: () => store.getDocument(),
    boneRotation: (bone) => {
      // The CURRENT subject's scene, not the boot run's. `run` is the scene the
      // app started with; every swap builds a new one, so this closure was
      // answering questions about a scene that had been replaced — grafted bones
      // read as absent when they were present, which is the captured-value
      // disease #61 already paid for once (mixer birth).
      const node = subject.run.scene.getObjectByName(bone);
      if (!node) return null;
      const { x, y, z, w } = node.quaternion;
      return { x, y, z, w };
    },
    mountedPanels: () => workspaceShell.mountedPanels(),
    closePanel: (id) => store.setLayout(closePanel(store.getLayout(), id as PanelId)),
    openPanel: (id, zone) => store.setLayout(openPanel(store.getLayout(), zone as ZoneName, { id: id as PanelId })),
    // Forwarded raw: an out-of-vocabulary mode THROWS from the FSM naming the
    // vocabulary — a silent skip here would be §8's forbidden shape, and the
    // probe is the agent register, which gets the same fail-loud everything
    // else gets (Codex P2, #54 round 4).
    setMode: (mode) => store.setMode(mode as ShellMode),
    setTailMood: (mood, muscle) => {
      if (!tailSpring.view(subject.run).available) return false;
      const parsed = tailMoodSchema.parse(mood);
      tailSpring.commit(subject.run, { mood: parsed, ...(muscle !== undefined ? { muscle } : {}) }, 'human:local');
      return true;
    },
    softTissueLeaves: () => subject.run.softTissueGrafts.map((g) => ({ region: g.region, bone: g.bone, head: g.head, tail: g.tail })),
    tuneSurfaceJiggle: (tuning) => surfaceJiggle.tune(tuning),
    viewFrom: (eye, target) => viewport.viewFrom(eye, target),
    setTailSpring: (on) => {
      if (!tailSpring.view(subject.run).available) return false;
      tailSpring.setOn(subject.run, on, 'human:local');
      return true;
    },
    poseTool: () => {
      const tool = viewport.getPoseTool();
      return tool === null ? null : { ...tool.probe };
    },
    // The pin gesture's own counters — armed at all, and what its clicks hit.
    // A gesture that silently does nothing is the failure mode this whole arc
    // keeps running into, so it gets a probe like the pose tool has.
    pinTool: () => {
      const tool = viewport.getPinTool();
      return tool === null ? null : { ...tool.probe };
    },
    handleXY: (bone) => viewport.getPoseTool()?.handleScreenXY(bone) ?? null,
    boneDrawStates: () => viewport.getPoseTool()?.drawStates() ?? null,
    tailCensus: () => {
      const doc = store.getDocument();
      const zone = (doc.zones ?? []).find((z) => z.id === doc.target.tails?.[0]?.zoneId);
      const arrays = zoneArraysNow()?.arrays;
      const verts = new Set<number>();
      if (zone !== undefined && arrays !== undefined) for (const f of zone.faces) for (let k = 0; k < 3; k++) verts.add(arrays.indices[f * 3 + k]!);
      return tailCensus(subject.run.scene, verts);
    },
    springCensus: () => {
      const bones = tailSpring.springBones(subject.run);
      if (bones.length === 0) return null;
      let stage = subject.run.scene;
      while (stage.parent !== null) stage = stage.parent;
      return springCensus(stage, bones, subject.run.tailGrafts[0]?.radii ?? []);
    },
    // Where the hand markers really landed — a trust surface gets a probe, so a
    // transform error is a failing assertion rather than something a person
    // squints at and half-believes.
    handMarkers: () => handMarkers.probe(),
    weightOverlay: () => weightOverlay.probe(),
    wireframe: () => wireframeOverlay.probe(),
    zones: () => {
      const t = zoneTint.probe();
      return { declared: (current(store.getHistory()).zones ?? []).length, tinted: t.zones, paintedFaces: t.paintedFaces, open: openZoneId };
    },
    // A gesture surface that cannot be aimed at cannot be tested through the
    // door people actually use — the pose tool's `handleScreenXY` precedent.
    pinXY: (handBone, id) => handMarkers.screenXY(handBone, id, { projectPoint: (p) => viewport.projectPoint(p) }),
  };

  // ─── P1, moves 1 and 2: get a file IN ──────────────────────────
  // Drop a rigged GLB and it becomes the SUBJECT. Drop an FBX and its
  // animation becomes what the subject WEARS. Either way the other half of the
  // Document is carried forward, so the two drops compose in any order and a
  // dropped character arrives already moving. v2 never built drag-drop at all.
  //
  // Both are the LOAD door, not the edit door (ADR-007 §4). `sources`,
  // `clipSelections` and `target` have no command verbs — there is no way to
  // express "import" as an edit — so an import begins a fresh lineage and undo
  // does not cross it. The subject (run + poseTargets + projector) is rebound
  // as ONE value through mountSubject, because a projector built over the old
  // scene and pointed at a new one is the mixer-birth disease wearing an
  // import for a costume (#61 r5-r7).
  let dropSeq = 0;
  // The graft serializer (Codex P1, #77): hand declarations append here so
  // each one composes `declareHand` over the PREVIOUS graft's completed
  // document. Referenced by the hands panel's `declare` at click time only —
  // declaration order against the registry above does not matter.
  let graftChain: Promise<void> = Promise.resolve();

  /** Run a freshly composed Document and put it on stage through the one door. */
  /**
   * Richest first. `minimal+hands` carries the ten digits; `minimal` does not.
   * Neither is right as a constant — see chooseProfile — so the run measures.
   */
  const PROFILE_PREFERENCE = ['minimal+hands', 'minimal'] as const;

  /**
   * Open the Hand Studio in EVERY mode that offers it, leaving the current mode
   * exactly as it was.
   *
   * Workspace layouts are PER MODE (ADR-015), which had two consequences and
   * both were bugs. Opening into one mode's layout meant the panel vanished the
   * moment you switched modes — mid-graft, with answers half given. And the
   * first attempt to fix that by forcing `pose` changed the app's documented
   * boot state, which the boot smoke caught immediately and rightly: a feature
   * does not get to move the whole application to where it is convenient.
   *
   * So the panel is placed in each mode's layout and the mode is left alone.
   * You can still walk into pose yourself — where the mixer stops (§5.3) and the
   * hand holds still, which is the right place to read digits from — and the
   * panel will be there when you arrive.
   */
  const openPackageSurface = createPackageDoor(registry.get('package').mount,store);
  const layoutHasPanel = (id: string): boolean => Object.values(store.getLayout().zones).some(groups=>groups.some(g=>g.panels.some(p=>p.id===id)));
  const openSurface = (panel: 'body'|'look'|'package', section?: BodySection): void => {
    if(panel==='package'){openPackageSurface();return;}
    if (!layoutHasPanel(panel)) store.setLayout(openPanel(store.getLayout(),'right',{id:panel}));
    for (const zone of ['right','left','bottom'] as const) {
      store.getLayout().zones[zone].forEach((group,gi)=>{
        if(group.panels.some(p=>p.id===panel)) {
          store.setLayout(setZoneCollapsed(store.getLayout(),zone,false));
          store.setLayout(activateTab(store.getLayout(),zone,gi,panel));
        }
      });
    }
    if(section) {
      const node=document.querySelector<HTMLDetailsElement>(`[data-panel="body"] [data-section="${section}"]`);
      if(node && !node.hidden) {node.open=true;node.scrollIntoView({block:'nearest'});}
      else noteStatus('This character has no tools available in that section.');
    }
  };
  const openHandsEverywhere = (): void => openSurface('body','joints');
  const layoutHasHands = (): boolean => layoutHasPanel('body');
  frontZonesPanel=()=>openSurface('body','parts');
  if(zonesFrontPending){zonesFrontPending=false;frontZonesPanel();}

  // The `⇪ Package` chrome entry (design note §4): opens the panel where it
  // is not, fronts its tab where it is. The door itself (phase 7) lives on
  // the panel, disabled by the readiness list until it can succeed.
  {
    const divider = document.createElement('span');
    divider.style.cssText = 'width: 1px; height: 18px; background: var(--accent-border);';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '⇪ Package';
    btn.setAttribute('aria-label', 'Open the Package panel — ship this character to poqpoq World');
    btn.style.cssText =
      'font: inherit; padding: 4px 10px; border-radius: 6px; cursor: pointer; ' +
      'background: var(--accent-soft); color: var(--accent-text); border: 1px solid var(--accent-border);';
    btn.addEventListener('click', () => openSurface('package'));
    const advanced=document.createElement('button');
    advanced.type='button'; advanced.textContent='Show advanced controls';
    advanced.style.cssText = btn.style.cssText;
    const reflectAdvanced=()=>advanced.setAttribute('aria-pressed',String(store.getLayout().register==='expert'));
    advanced.addEventListener('click',()=>{
      const layout=store.getLayout();
      const register=layout.register==='expert'?'creator':'expert';
      const clean = (groups: typeof layout.zones.left) => groups.map(g=>({...g,panels:g.panels.map(p=>({id:p.id}))}));
      store.setLayout({...layout,register,zones:{left:clean(layout.zones.left),right:clean(layout.zones.right),bottom:clean(layout.zones.bottom)}});
    });
    store.subscribe(reflectAdvanced);reflectAdvanced();
    // THE REGISTER SETS THE STATUS LINE'S DEFAULT (Allen, 2026-09-29, ruling the XD note's Q2):
    // Expert is the evidence-dense register, and the Body readings now live on this line — so
    // Expert shows it; Creator keeps it hidden (the 2026-07-28 ruling). Applied only when the
    // register CHANGES, so the ` toggle (and a refusal's reveal) holds until the next switch.
    let statusRegister: string | null = null;
    const reflectStatusDefault = () => {
      const register = store.getLayout().register;
      if (register === statusRegister) return;
      // At boot Creator changes nothing: the line is born hidden (index.html), and a refusal that
      // already revealed it (a workspace restored to defaults) must not be silenced by this.
      const booting = statusRegister === null;
      statusRegister = register;
      if (register === 'expert') revealStatus();
      else if (!booting) statusEl?.classList.add('status-hidden');
    };
    store.subscribe(reflectStatusDefault); reflectStatusDefault();
    shellChromeBar.append(advanced);
    shellChromeBar.append(divider, btn);
    // ADR-040: the opt-in door to the World check — the same guided pass a file from World gets.
    const worldCheck = document.createElement('button');
    worldCheck.type = 'button';
    worldCheck.textContent = '✓ World check';
    worldCheck.dataset['action'] = 'world-check';
    worldCheck.setAttribute('aria-label', "Check this character against World's avatar rules and fix it for upload");
    worldCheck.style.cssText = btn.style.cssText;
    // One button, two meanings by state (design §3.5): with a session open it returns to the guided steps.
    worldCheck.addEventListener('click', () => {
      intakeFrame.resume();
      if (!intake.active()) void intake.optIn();
    });
    worldCheckLabel = () => {
      worldCheck.textContent = intake.active() && !intakeFrame.guided() ? 'Back to guided steps' : '✓ World check';
    };
    intake.subscribe(worldCheckLabel);
    worldCheckLabel();
    shellChromeBar.append(worldCheck);
  }

  /**
   * Run a Document and put it on stage. RETURNS WHETHER IT COMMITTED (Codex P1,
   * #94): a slower earlier drop must not overwrite a faster later one, so this
   * has a stale-sequence exit that returns having changed nothing — and to a
   * caller that exit was indistinguishable from success. The graft path armed
   * its revert snapshot on that silence, recording the OTHER load's Document as
   * "what the graft produced"; the identity check then passed for a document
   * the graft never made. A function with two outcomes must report which one.
   */
  const swapTo = async (next: AnimatorDocument, seq: number): Promise<boolean> => {
    let ran = await runDocument(next, appIO, deps, runOptions);
    let doc = next;
    let handNote: string | null = null;
    let graftableHands = false;

    // ─── Choose the richest profile every rig can actually cover ───────────
    // MEASURED: the BBSA ontology, the mapper and the retarget all already
    // handle fingers — a Mixamo clip's 30 finger channels land on a VRM with 0
    // dropped. What stopped them was the profile: `minimal` asks for no fingers
    // so they drop as unmapped, and `full` demands Jaw/Eyes/Breasts that no
    // Mixamo skeleton carries so it refuses the source outright.
    //
    // The choice needs the RIGS, which only exist after an import — so the run
    // happens first at the Document's declared profile, and upgrades only when
    // a richer one is genuinely covered. That costs a second run exactly on the
    // rigs that can use it, and nothing on the rigs that cannot. The winner is
    // written INTO the Document, because §8: the value a run reproduces is the
    // value the author wrote, not one re-decided on the next load.
    //
    // The target rig is `ran.rig`, the FINAL one — grafts applied. Choosing
    // against `ran.targetImport.rig` would make every successfully grafted hand
    // look fingerless (that descriptor is pre-graft evidence by definition), so
    // the "a graft would earn them" path could never pay out (Codex P1, #74).
    // The FAMILY the rig's own bone names declare (detect-family): the same
    // "needs the rigs, which only exist after an import" shape as the profile
    // choice below, so the two share ONE upgrade and one extra run. A null
    // detection changes nothing — 'unknown' keeps the honest ignorance the
    // drop door declared, and no other family's authored frames are applied.
    const detectedFamily = detectRigFamily(ran.rig);
    const nextFamily = detectedFamily ?? doc.target.family;
    const rigs = [ran.rig, ...[...ran.sources.values()].map((src) => src.rig)];

    /**
     * Re-run the Document at a profile/family pair. Commits `doc` and `ran`
     * only once the run has actually SUCCEEDED — a Document naming a profile
     * no run ever reached is §8's lie. Returns false on refusal; the caller
     * keeps the run already on stage.
     */
    const reRun = async (profile: string, family: string): Promise<boolean> => {
      try {
        const next2 = parseDocument({
          ...doc,
          target: { ...doc.target, profile, family },
          clipSelections: doc.clipSelections.map((sel) => ({ ...sel, profile })),
        });
        ran = await runDocument(next2, appIO, deps, runOptions);
        doc = next2;
        return true;
      } catch (err) {
        handNote = `profile selection skipped — ${err instanceof Error ? err.message : String(err)}`;
        return false;
      }
    };

    let choice: ReturnType<typeof chooseProfile> | null = null;
    try {
      choice = chooseProfile(rigs, deps.ontology, PROFILE_PREFERENCE);
    } catch (err) {
      handNote = `profile selection skipped — ${err instanceof Error ? err.message : String(err)}`;
    }
    const wantProfile = choice?.profile ?? doc.target.profile;
    if (wantProfile !== doc.target.profile || nextFamily !== doc.target.family) {
      const upgraded = await reRun(wantProfile, nextFamily);
      // THE FAMILY IS NOT THE PROFILE'S PASSENGER. It only re-keys authored
      // joint frames; the profile decides which BONES must be covered, and a
      // source can refuse that (measured on Bobby 2026-07-30: Mixamo carries
      // finger bones but not their TIPS, so `minimal+hands` threw at
      // retarget:source). These shared one try/catch, so the refusal rolled
      // the detected family back with it and the VRM leg frames never
      // applied — the walk stayed broken while the log said only "profile
      // selection skipped". Retry at the profile that already ran: same
      // coverage, correct frames.
      if (!upgraded && nextFamily !== doc.target.family) {
        const familyOnly = await reRun(doc.target.profile, nextFamily);
        if (familyOnly) handNote = `${handNote ?? ''} · family "${nextFamily}" applied`.replace(/^ · /, '');
      } else if (upgraded && choice !== null) {
        handNote = `hands: ${choice.profile}`;
      }
    }
    // A rejection is EVIDENCE, not a verdict — it names the rig and the bones
    // that cost it the richer profile, so "no fingers" is never a bare fact.
    const lost = choice?.rejected[0];
    if (lost !== undefined) {
      handNote =
        `no finger motion — "${lost.rig}" is missing ${lost.missing.length} of the ` +
        `"${lost.profile}" bones (${lost.missing.slice(0, 3).join(', ')}…); a graft would earn them`;
      // The bones it wanted are FINGER bones — otherwise a graft is not the
      // cure and offering one would be noise.
      graftableHands = lost.missing.some((id) => /Hand(Thumb|Index|Middle|Ring|Pinky)[1-4]$/.test(id));
    }

    // Did the run lose finger motion for want of BONES, on a rig whose hands
    // could actually grow them? Both halves matter: a rig that already has
    // fingers needs no graft, and a rig with no mapped hand cannot take one.
    const graftWouldHelp =
      graftableHands &&
      handCandidates(ran.rig, deps.ontology).some((c) => c.existingChildren.length === 0);

    const nextRun = ran;
    // A slower earlier drop must not overwrite a faster later one.
    if (seq !== dropSeq) return false;
    const [nextClipRun] = nextRun.clips;
    placement.cancel(); // Withdraw the old rig's read-only skeleton before offering the new rig.
    const outgoing = subject;
    subject = mountSubject(nextRun);
    const nextPlayable = nextClipRun ? toThreeClip(nextClipRun.result.clip) : null;
    const info = presentOnRests(nextPlayable);
    store.load(createHistory(doc), subject.poseTargets);
    // Rebuild the skeleton over the NEW subject. Not left to the store
    // subscription: `load` may settle without a mode change, and the case that
    // matters most — a graft that just added twenty bones — is exactly the one
    // where nothing about the shell state changes.
    offerPoseTool();
    // The outgoing subject is off stage and its overlays have re-homed (the
    // pose tool above was the last shared-geometry tenant). Without this, every
    // swap strands the previous import's GPU allocations until the context
    // dies — measured as "ten consecutive clip drops and the app stops".
    if (outgoing.run.scene !== nextRun.scene) viewport.disposeDetached(outgoing.run.scene);
    // Dropped bytes whose uri no Document field names anymore are unreachable —
    // a Document can only resolve uris it declares, so holding the buffers
    // would grow the session by one full file per drop, forever.
    const liveUris = new Set<string>([doc.target.specimen.uri, ...doc.sources.map((s) => s.specimen.uri)]);
    for (const key of [...uploads.keys()]) {
      if (!liveUris.has(key)) uploads.delete(key);
    }
    // A COMMITTED buffer needs no predecessor (Codex P2, #140 round 10): the
    // link exists so a load that does not commit can put back what it
    // replaced; kept past the commit it would hold every earlier same-named
    // file alive for the session.
    for (const key of liveUris) {
      const held = uploads.get(key);
      if (held !== undefined) priorOf.delete(held);
    }
    statusBase =
      `"${doc.name}" wears "${nextPlayable?.name ?? '(nothing yet)'}" ` +
      `· ${nextRun.rig.bones.length} bones · ${info.boundTracks} tracks bound` +
      (info.unresolvedTracks.length > 0 ? `, ${info.unresolvedTracks.length} unresolved` : '');
    // What the loader READ but did not UNDERSTAND. A .vrm imports through the
    // glTF path perfectly — 113 bones, full coverage, fingers and all — while
    // its VRM extensions go unread, and "it worked" with no mention of that is
    // how a file silently loses the half of itself we have not built yet
    // (ADR-013 is the humanoid map's home).
    // ─── the refusal IS the door ───────────────────────────────────────────
    // "a graft would earn them" is a promise, and until now nothing kept it:
    // the Hands panel was in no default layout and the palette that would
    // consume its verbs has not landed, so the only opener was the agent probe.
    // A person could not reach it at all. So the moment the run REPORTS that
    // finger motion was lost for want of bones, and the rig actually has hands
    // that could grow them, the panel comes to the front. Narrow on purpose:
    // not on every load, only when a graft would genuinely change the outcome.
    if (graftWouldHelp && !layoutHasHands()) openHandsEverywhere();

    // ADR-043 M0: mood faces are carried now, so the extension is not "unread";
    // the moods are named instead. A Document reopen did not walk the import
    // door's refusal, so a set that cannot be carried is said HERE (the export
    // refuses it too).
    let moodNote: string | null = null;
    try {
      const moods = await sourceMoods();
      if (moods !== null) moodNote = `mood faces carried: ${moods.variants.join(', ')}`;
    } catch (err) {
      moodNote = `mood faces cannot be carried — ${refusalLine(err)}; export will refuse`;
    }
    // The mood read may FETCH (a bundled specimen): a newer load can win
    // meanwhile, and this one must then report itself overtaken, not opened,
    // nor speak over the newer load's line (Codex P2, #259; the #140 rule).
    if (seq !== dropSeq) return false;
    const unread = nextRun.targetImport.rigReport.unhandledExtensions.filter((e) => e !== KHR_MATERIALS_VARIANTS || moodNote === null);
    const notes = [
      // FACE FORWARD said out loud: a backward-authored body was turned (the backward bear, 2026-09-25).
      nextRun.faceForward?.turned === true ? `facing: ${nextRun.faceForward.reason}` : null,
      handNote,
      moodNote,
      unread.length > 0 ? `read as glTF; ${unread.join(', ')} not used yet` : null,
    ].filter((n): n is string => n !== null);
    noteStatus(notes.length > 0 ? notes.join(' · ') : null);
    return true;
  };

  // Joint edits retain the same bone identities and therefore the same history.
  // Rebuild off-stage first. Undo/redo replay through this same projection door;
  // exports wait for it, and stale rebuilds can never overwrite a later load.
  // ADR-031 T2: a TAIL is the second tenant of this door — the same kind of
  // armature change inside a lineage (tail.declare, the elbow.pin precedent).
  // Its key carries the declaration AND the tailed zone's faces, so a zone
  // grown wider re-places the chain, and undo/redo replay through here too.
  const tailPart = (doc: AnimatorDocument) =>
    (doc.target.tails ?? []).map((t) => [t, (doc.zones ?? []).find((z) => z.id === t.zoneId)?.faces ?? null]);
  // ADR-038 J2: a soft-tissue leaf is the third tenant — its declaration and, when it names one, its zone's faces.
  const softTissuePart = (doc: AnimatorDocument) =>
    (doc.target.softTissue ?? []).map((t) => [t, t.zoneId === undefined ? null : ((doc.zones ?? []).find((z) => z.id === t.zoneId)?.faces ?? null)]);
  // ADR-039 T2a: a confirmed hair re-runs the character (its skull, zone and treatment; gravity is not read by attached hair).
  const hairPart = (doc: AnimatorDocument) => {
    const h = doc.target.hair;
    // the hair wand holds the run on the faces it started from until Done (one re-soften, not one per click)
    return h === undefined ? null : [h.zoneId, h.skull, h.treatment, hairWandFaces ?? (doc.zones ?? []).find((z) => z.id === h.zoneId)?.faces ?? null];
  };
  const elbowKey = (doc: AnimatorDocument) => JSON.stringify([doc.target.recenters ?? [], tailPart(doc), softTissuePart(doc), hairPart(doc)]);
  const tailsOfKey = (key: string): string => JSON.stringify((JSON.parse(key) as unknown[])[1]);
  let elbowLineage = store.lineage();
  let projectedElbows = elbowKey(store.getDocument());
  let requestedElbows = projectedElbows;
  let elbowGeneration = 0;
  let preparedElbow: { key: string; run: Subject['run'] } | null = null;
  const mountElbowRun = (nextRun: Subject['run'], tailsChanged = false) => {
    const restoreView = viewport.editingSurface().preserveView();
    const outgoing = subject;
    const bindings = libraryBindings.get(outgoing.run.scene);
    if (bindings) libraryBindings.set(nextRun.scene, bindings);
    subject = mountSubject(nextRun);
    // THE BONE GATE FOLLOWS THE REMOUNT (Codex/Luna P1, #180): a grown tail's
    // `Tail*_graft` bones become editable, a withdrawn one's refuse — the set
    // captured at the last load guarded a rig no longer on stage.
    store.setPoseTargets(subject.poseTargets);
    commitHeadSeating(); applyCharacterFilter(); applyMaterialsFilter();
    stageInvalid = true; resolvePresentation(); applyMixerPause(); reproject();
    repaintOverlay(); refreshWire(); refreshZones(); offerPoseTool();
    if (elbowHold) { viewport.setJointEditing(poseToolConfig()); subject.projector.project([]); }
    restoreView();
    if (outgoing.run.scene !== nextRun.scene) viewport.disposeDetached(outgoing.run.scene);
    const reconstructed = nextRun.bindMarkers.filter(m => m.code === 'hinge-paint-reconstructed')
      .reduce((sum, m) => sum + Number(m.detail?.['reconstructed'] ?? 0), 0);
    if (tailsChanged) {
      noteZones(tailSummary(nextRun));
      return;
    }
    setElbowStatus('');
    // STATUS, not the card (§3B) — and the reconstructed count REVEALED: it is a warning.
    noteBody(reconstructed > 0
      ? `elbows: ${reconstructed} vertices needed estimated weights; their original weights could not be recovered. Undo is available.`
      : 'elbows: placement saved. Undo or Reset restores the previous or imported position.', reconstructed > 0);
  };
  const onRunKey = (): void => {
    const key = elbowKey(store.getDocument());
    if (elbowLineage !== store.lineage()) {
      elbowLineage = store.lineage(); projectedElbows = key; requestedElbows = key;
      elbowGeneration++; elbowProjectionError = null;
      return;
    }
    if (requestedElbows === key) return;
    requestedElbows = key;
    const generation = ++elbowGeneration;
    elbowProjectionError = null;
    const tailsChanged = tailsOfKey(key) !== tailsOfKey(projectedElbows);
    if (preparedElbow?.key === key) {
      const ready = preparedElbow; preparedElbow = null;
      mountElbowRun(ready.run, tailsChanged); projectedElbows = key;
      return;
    }
    if (tailsChanged) {
      noteZones('tail: re-placing the chain…');
      // While the chain is being rebuilt (undo/redo of a tail, a zone reshaped),
      // no tail bone is addressable: the old chain may be gone from the
      // Document and the new one is not on stage yet. The remount below
      // restores the gate to exactly the rig it mounts.
      store.setPoseTargets(new Set([...subject.poseTargets].filter((b) => !/^Tail\d+_graft$/.test(b))));
    } else { setElbowStatus(''); noteBody('elbows: rebuilding corrections…'); }
    const doc = store.getDocument();
    elbowProjection = runDocument(doc, appIO, deps, runOptions).then(nextRun => {
      if (generation !== elbowGeneration) { viewport.disposeDetached(nextRun.scene); return; }
      mountElbowRun(nextRun, tailsChanged); projectedElbows = key;
    }).catch((error: unknown) => {
      if (generation !== elbowGeneration) return;
      elbowProjectionError = error instanceof Error ? error : new Error(String(error));
      if (tailsChanged) noteZones(`tail: ${refusalLine(elbowProjectionError)} · undo the edit to recover`, true);
      else {
        // KEEP the failure on the card, short (fail-loud); the detail rides the status line, revealed.
        setElbowStatus('Rebuild failed — undo to recover');
        noteBody(`elbows: rebuild failed: ${refusalLine(elbowProjectionError)}. Undo the edit to recover.`, true);
      }
    });
  };
  store.subscribe(onRunKey);
  recheckRun = onRunKey;
  const commitElbow = async (command: unknown): Promise<boolean> => {
    if (exportHold) throw new Error('Wait for the export to finish before changing elbows.');
    await awaitElbowProjection();
    const before = store.getDocument();
    const next = applyCommand(before, command);
    if (elbowKey(next) === projectedElbows) return true;
    const nextRun = await runDocument(next, appIO, deps, runOptions);
    if (store.getDocument() !== before || exportHold) {
      viewport.disposeDetached(nextRun.scene);
      throw new Error('The character changed while preparing the elbow. Please pin it again.');
    }
    preparedElbow = { key: elbowKey(next), run: nextRun };
    try { store.dispatch(command); } finally {
      if (preparedElbow) { viewport.disposeDetached(preparedElbow.run.scene); preparedElbow = null; }
    }
    return true;
  };
  /**
   * The tail's commit door — commitElbow's shape: run the NEXT Document off
   * stage first, and dispatch only when it placed. A refusal (no junction, a
   * count outside the band, a zone cut on another mesh) is THROWN to the
   * flyout, which renders the sentence; the Document never holds a tail that
   * cannot be placed, and undo never has to recover from one.
   */
  const commitTail = async (command: unknown): Promise<void> => {
    if (exportHold) throw new Error('wait for the export to finish before changing the tail');
    await awaitElbowProjection();
    const before = store.getDocument();
    const next = applyCommand(before, command);
    if (elbowKey(next) === projectedElbows) {
      store.dispatch(command);
      return;
    }
    const nextRun = await runDocument(next, appIO, deps, runOptions);
    if (store.getDocument() !== before || exportHold) {
      viewport.disposeDetached(nextRun.scene);
      throw new Error('the character changed while the tail was being placed — try again');
    }
    preparedElbow = { key: elbowKey(next), run: nextRun };
    try { store.dispatch(command); } finally {
      if (preparedElbow) { viewport.disposeDetached(preparedElbow.run.scene); preparedElbow = null; }
    }
  };
  commitTailRef = commitTail;
  const placement = createElbowPlacementTool({
    viewport, run: () => subject.run, document: () => store.getDocument(), buttons: () => elbowButtons,
    apply: (doc) => {
      const changed = doc.target.recenters?.find(r => JSON.stringify(r) !== JSON.stringify(store.getDocument().target.recenters?.find(old => old.bone === r.bone)));
      if (!changed) return Promise.resolve(true);
      return commitElbow({ schemaVersion: '1.0.0', verb: 'elbow.pin', params: changed });
    },
    rest: () => { viewport.setJointEditing(poseToolConfig()); elbowHold = true; applyMixerPause(); subject.projector.project([]); },
    finish: () => { viewport.setJointEditing(null); elbowHold = false; applyMixerPause(); if (viewport.probe.paused) reproject(); },
    // Every placement status is a refusal (a rest that could not be taken, a pin that failed).
    status: (message) => noteBody(`elbows: ${message}`, true),
    ready: () => projectedElbows === requestedElbows && elbowProjectionError === null,
  });
  elbowPinStatus = placement.status;
  let elbowStatusSeen = '';
  // One render-preview slot, two tenants: the elbow placement's temporary
  // edits and the tail's preview wag. Both apply just before a frame renders
  // and restore just after (last on, first off), so no door that reads the
  // scene between frames — export, package, bake — ever sees either.
  viewport.setRenderPreview(() => {
    const restorePlacement = placement();
    // ADR-039: the drawn skull follows the head, and its handle follows the skull.
    hairTool.frame();
    tailRootHandle.frame();
    // The elbow pins move without a store change (a drag is a draft until +Pin);
    // redraw the Body header when their reading changes (§3.1 Elbows row).
    const elbowStatusNow = JSON.stringify(placement.status());
    if (elbowStatusNow !== elbowStatusSeen) {
      elbowStatusSeen = elbowStatusNow;
      bodyProgressListeners.forEach((listener) => listener());
    }
    // The spring's clock is the render's: frame dt measured here, fed through
    // the runtime's fixed 60 Hz accumulator (held pose — see tail-spring.ts).
    const now = performance.now() / 1000;
    const frameDt = springLastFrame === null ? 0 : now - springLastFrame;
    springLastFrame = now;
    wagFrameDt = frameDt;
    const restoreWag = tailSpringing()
      ? tailSpring.applyFrame(subject.run, frameDt, { clipSeconds: viewport.probe.clipTimeSec, paused: viewport.probe.paused })
      : applyTailWag();
    // The root handle's bones are drawn AFTER the tail's motion is applied, so they wag with the flesh (Codex P2, #253).
    rootBones?.update();
    const restoreJiggle = softTissueSpringing() ? softTissue.applyFrame(subject.run, frameDt) : null;
    // J4: after the leaves have advanced, their motion rings the surface modes.
    const restoreSurface =
      softTissueSpringing() && subject.run.surfaceJiggle.length > 0
        ? surfaceJiggle.applyFrame(subject.run, frameDt, (region) => softTissue.views(subject.run).find((v) => v.region === region)?.controls ?? null)
        : null;
    return () => {
      restoreSurface?.();
      restoreJiggle?.();
      restoreWag?.();
      restorePlacement();
    };
  });

  /**
   * A new SUBJECT. Composed from the LIVE Document, not the seed, so dropping a
   * model after a clip keeps the clip. What cannot survive a rig change is
   * dropped explicitly: authored keys and pose edits both name bones this new
   * skeleton may not have, and carrying them would be the phantom-bone class
   * entering through the load door.
   */
  const importModel = async (file: File, uri: string, seq: number): Promise<boolean> => {
    // ADR-032: ONE lineage rule, in the kernel — the carry table (what a new
    // target keeps of the room and resets of the body) lives in
    // `beginTargetLineage` and its table test, not in a spread here that has
    // to remember every slice (it forgot zones, recenters, chestRepair; it
    // once forgot the profile — the 2026-08-26 dancingSquirrel refusal).
    // ADR-004's signature detection has not landed, so family is an explicit
    // claim and this one is honestly ignorant (the rule's default). THE
    // PROFILE IS THIS RIG'S PROPERTY, NOT THE LAST ONE'S: start at the floor;
    // chooseProfile runs immediately after on the rig that actually loaded
    // and upgrades when the measurement earns it.
    // The commit result rides out (Codex P2, #140 round 3): a run a newer
    // gesture overtook never reached the stage, and a caller that remembers
    // files must not remember it as opened.
    // FACE FORWARD, measured ONCE on the new body and declared in its Document
    // (the backward bear, the bear girl, 2026-09-25); the run replays it.
    // An FBX body is converted to a GLB here first (fbx-character.ts) — the run
    // replays the same conversion from the same buffer, so facing is measured
    // on exactly the body the run will see.
    const format = /\.fbx$/i.test(file.name) ? 'fbx' : 'glb';
    // ADR-043 M0: a file whose mood faces Harry cannot carry whole is refused
    // HERE, at the door, with its reasons — not discovered at export.
    if (format === 'glb') readMoodVariants(uploads.get(uri) as ArrayBuffer);
    const fbxBody = format === 'fbx' ? await fbxCharacterToGlb(uploads.get(uri) as ArrayBuffer, file.name) : null;
    let facing: { turnDeg: number; reason: string } | undefined;
    try {
      const measured = faceForwardGlb(fbxBody?.glb ?? (uploads.get(uri) as ArrayBuffer));
      if (measured.turned) facing = { turnDeg: Number(measured.turnDeg.toFixed(2)), reason: measured.reason };
    } catch (err) {
      noteStatus(`facing: ${refusalLine(err)} — left as authored`);
    }
    const committed = await swapTo(
      beginTargetLineage(store.getDocument(), {
        name: file.name.replace(/\.[^.]+$/, ''),
        label: file.name,
        specimen: { uri, format },
        ...(facing !== undefined ? { facing } : {}),
        // The floor: PROFILE_PREFERENCE is richest-first, so its last entry.
        profile: PROFILE_PREFERENCE[PROFILE_PREFERENCE.length - 1] ?? 'minimal',
      }),
      seq,
    );
    // What the conversion changed and could not keep — a dropped texture is said out loud.
    if (committed && fbxBody !== null && seq === dropSeq) {
      console.info('[animator-v3] FBX character converted', fbxBody.report);
      noteStatus(`${file.name}: ${fbxCharacterReportLine(fbxBody.report)}`);
      if (fbxBody.report.texturesDropped.length > 0) revealStatus();
    }
    return committed;
  };

  /**
   * A new CLIP for the subject already on stage. The target, its authored clips
   * and its pose edits all survive — the rig did not change, so nothing that
   * names its bones is invalidated.
   */
  const importClip = async (file: File, uri: string, seq: number): Promise<boolean> => {
    const cur = store.getDocument();
    // Takes are enumerated and chosen BY NAME (§8) before the Document is
    // written — never clips[0], which on a Mixamo export is routinely the
    // vestigial empty take. Load-stage only; the full import runs inside
    // runDocument once the selection is written down.
    const bytes = uploads.get(uri) as ArrayBuffer;
    if (glbShape(bytes) !== null) {
      // Baked GLBs carry named animation nodes, not a source skin to retarget.
      // Use the same binding path as World's library previews.
      const id = `local:${file.name}`;
      const result = await previewLibraryClip(id, bytes, () => seq === dropSeq);
      if (result === null || seq !== dropSeq) return false;
      store.setMode('playback');
      releaseUpload(uri, bytes);
      revealStatus();
      noteStatus(`${file.name}: local animation preview · ${result.bound}/${result.total} tracks bound` +
        (result.unresolved.length ? ` · missing: ${result.unresolved.slice(0, 4).join(', ')}` : '') +
        ' · reopen the animation after loading a saved Document');
      return true;
    }
    const takes = loadFBX(bytes).takes;
    const animation = takes.find((t) => t.classification === 'animation');
    if (animation === undefined) {
      const inventory = takes.map((t) => `${t.name} (${t.classification})`).join(', ');
      throw new Error(
        `no animation take in this file — it carries: ${inventory || '(no takes at all)'}. ` +
          `A static-pose or empty take is not an animation, and picking one anyway is how a file ` +
          `"imports fine" and then does nothing.`,
      );
    }

    // A Mixamo export carries TWO skeletons — Alpha_Surface and Alpha_Joints —
    // and the conversion refuses to guess between them (ADR-008 §7). The seed
    // Document hard-codes the answer; a dropped file cannot.
    //
    // So MEASURE it rather than ask: import once per candidate and keep the one
    // the animation actually drives, counting the rotation tracks that survive
    // conversion. Choosing by name ("the one called Joints") would be a name
    // consulted where a measurement exists — the same mistake that hides the
    // Tripo neck behind a /Twist/ regex. The winner is written INTO the
    // Document's source, so the run reproduces without re-deciding (§8).
    let selectSkeleton: string | undefined;
    try {
      await importFbx(bytes, deps, { label: file.name });
    } catch (err) {
      if (!(err instanceof MultipleSkeletonsError)) throw err;
      const scored: Array<{ name: string; tracks: number }> = [];
      for (const candidate of err.candidates) {
        try {
          const probe = await importFbx(bytes, deps, { label: file.name, selectSkeleton: candidate });
          const forTake = probe.clips.find((c) => c.clip.name === animation.name) ?? probe.clips[0];
          scored.push({ name: candidate, tracks: forTake?.clip.tracks.length ?? 0 });
        } catch {
          scored.push({ name: candidate, tracks: 0 });   // a candidate that cannot convert scores nothing
        }
      }
      scored.sort((a, b) => b.tracks - a.tracks);
      const best = scored[0];
      if (best === undefined || best.tracks === 0) {
        throw new Error(
          `none of this file's skeletons carries the take "${animation.name}" — tried ` +
            `${scored.map((c) => `${c.name} (${c.tracks} tracks)`).join(', ')}.`,
        );
      }
      selectSkeleton = best.name;
      noteStatus(
        `${file.name}: chose skeleton "${best.name}" — it drives ${best.tracks} tracks ` +
          `(${scored.slice(1).map((c) => `${c.name}: ${c.tracks}`).join(', ')})`,
      );
    }

    // ADR-032: a new SOURCE changes nothing about the target — the room's one
    // source and its selection are replaced by the kernel's rule, the body is
    // untouched (`addSourceToLineage` and its table test).
    return swapTo(
      addSourceToLineage(cur, {
        label: file.name,
        specimen: { uri, format: 'fbx' },
        take: animation.name,
        ...(selectSkeleton !== undefined ? { selectSkeleton } : {}),
      }),
      seq,
    );
  };

  // ─── ADR-032 phase 2: the Document door, and the handles behind Recent ──
  // Open goes through the SAME atomic swapTo a drop walks; save writes the
  // Document as JSON through the export door's two-phase writer. `upload:`
  // specimens relink loudly (D6) — document-io.ts owns the rule.
  const fileHandles = createFileHandles();
  const documentIo = createDocumentIo({
    parse: parseDocument,
    resolvable: (uri) => uploads.has(uri) || SPECIMEN_URLS[uri] !== undefined,
    hold: holdUpload,
    release: releaseUpload,
    // A cancel is a gesture: the in-flight run it abandons must not commit
    // (Codex P1, #140 round 8) — the stale-load guard compares against this.
    invalidate: () => {
      dropSeq += 1;
    },
    // The door opened on its own — a remembered handle may be USED here, never
    // asked for (the permission dialog needs a click; the menu's relink has one).
    recall: async (uri) => {
      const handle = await fileHandles.recall(uri);
      return handle === null ? null : fileFromHandle(handle, { mayPrompt: false });
    },
    swapTo,
    // The latest gesture wins — a WAIT, like a run, is published only by a
    // load that is still current (Codex P1, #140 round 2).
    isCurrent: (seq) => seq === dropSeq,
    note: (line, reveal) => {
      noteStatus(line);
      if (reveal) revealStatus();
    },
  });

  /**
   * Open a Document file (drop or menu) under the sequence its gesture
   * reserved. A refusal is one status line, reasons included.
   */
  const openDocumentFile = async (file: File, seq: number): Promise<boolean> => {
    // A load already overtaken says nothing (Codex P2, #140 round 3): its
    // "reading…" would overwrite the line the current load is speaking on.
    if (seq === dropSeq) noteStatus(`reading ${file.name}…`);
    try {
      const outcome = await documentIo.openDocument(await file.text(), file.name, seq);
      if (outcome.kind === 'opened') {
        // An open is the occasional confirmation the hidden pill exists for —
        // and it says what the file still depends on, so a later reopen asking
        // for those files is not a surprise.
        const named = documentIo.uploadsNamed(outcome.doc).map(uploadName);
        revealStatus();
        noteStatus(`opened ${file.name}${named.length > 0 ? ` — names ${named.join(', ')}; a reopen asks for them again` : ''}`);
      }
      // 'waiting' spoke for itself inside openDocument; 'superseded' means a
      // later load is on stage and already speaking — and this one was not accepted.
      return outcome.kind !== 'superseded';
    } catch (err) {
      // A load already overtaken keeps its refusal to the console (Codex P2,
      // #140 round 4): the line belongs to the load the person moved on to.
      if (seq === dropSeq) noteStatus(`${file.name} could not be opened — ${refusalLine(err)}`, err);
      console.error('[animator-v3] Document refused', { file: file.name, bytes: file.size, error: err });
      return false;
    }
  };

  /** Save the live Document as `.harry.json` — the JSON as-is, no bytes embedded (D5, D6). */
  const saveDocument = async (): Promise<void> => {
    const doc = store.getDocument();
    const filename = documentIo.documentFilename(doc);
    // WHERE first, at click time (save-glb header). The serialize is cheap
    // here, but it is the same door and the same rule.
    const target = await pickSaveTarget(filename, DOCUMENT_SAVE);
    if (target.kind === 'cancelled') {
      noteStatus('save cancelled — nothing written');
      return;
    }
    try {
      const encoded = new TextEncoder().encode(documentIo.serialize(doc));
      const bytes = new ArrayBuffer(encoded.byteLength);
      new Uint8Array(bytes).set(encoded);
      const landed = await writeSaveTarget(target, bytes, filename, DOCUMENT_SAVE.mime);
      // Mark saved ONLY what was written (Codex P1, #140). An edit made while
      // the write was in flight is not in the file, and calling the History
      // saved would let it be lost on reload. Documents are frozen values, so
      // identity is the test — and the line says so when they differ.
      const unchanged = store.getDocument() === doc;
      if (unchanged) store.markSaved();
      const stale = unchanged ? '' : ' · edited while saving — those edits are NOT in the file; save again';
      const fellBack = target.kind === 'picker' && landed === 'anchor' ? ' (chosen location refused — downloaded instead)' : '';
      const named = documentIo.uploadsNamed(doc).map(uploadName);
      const relinks = named.length > 0 ? ` · names ${named.join(', ')} — not embedded; a reopen asks for them` : '';
      revealStatus();
      noteStatus(`saved ${filename}${fellBack}${stale}${relinks}`);
    } catch (err) {
      noteStatus(`${filename} could not be saved — ${refusalLine(err)}`, err);
    }
  };

  /**
   * What a person SAID the file is for, when they said it: a menu entry names
   * its purpose; a drop names nothing and the door reads the content.
   */
  type DropIntent = 'character' | 'animation' | 'document';

  /**
   * The one door for a File, under the sequence its GESTURE reserved. `seq`
   * comes from the caller — the drop event, or the menu the moment a choice
   * is final — never from in here, because two files opened close together
   * must land in the order they were asked for, not the order their reads
   * finished (Codex P1 ×2, #140; the #94 stale-load guard compares against
   * this number and means nothing otherwise). RETURNS whether the door
   * accepted the file: a refusal is spoken here and reported as false, so a
   * caller that remembers files remembers only accepted ones.
   */
  const importDropped = async (
    file: File,
    intent: DropIntent | undefined,
    seq: number,
    /** Set by a RELINK: the Document it is for, by name — refused at the door if that wait has ended. */
    relinkOf?: string,
  ): Promise<boolean> => {
    // A Document file walks its own reader (ADR-032 phase 2) — same gesture, one door.
    if (intent === 'document' || (intent === undefined && isDocumentFile(file.name))) {
      return openDocumentFile(file, seq);
    }
    const uri = `upload:${file.name}`;
    // A .vrm is a glTF BINARY container: same bytes, same loader, extra
    // extensions the loader reports as unread. Rejecting it on its file
    // extension alone turned a whole target family away at the door.
    // A load already overtaken says nothing (Codex P2, #140 round 3): a menu
    // pick reserves its order before a possibly slow read, and its "reading…"
    // must not overwrite the line the current load is speaking on.
    if (seq === dropSeq) noteStatus(`reading ${file.name}…`);
    // Hoisted so the refusal path can tell ITS bytes from a newer load's.
    let bytes: ArrayBuffer | null = null;
    try {
      bytes = await file.arrayBuffer();
      // ROUTE ON CONTENT here too: a Document that lost its `.json` (a picker's
      // temp copy, a renamed download) is still text that begins with `{`, and
      // sending it to the FBX loader answered "Unknown format" — true, useless
      // (measured by the smoke, 2026-09-03).
      if (intent === undefined && looksLikeDocument(bytes)) return openDocumentFile(file, seq);
      // ADR-032 D6: a Document waiting to be relinked claims the file it named —
      // through this door, so a drop and a menu pick satisfy it alike. A file
      // it did not name abandons the wait (loudly, inside offer) and is the
      // ordinary import below.
      // The entry's intent rides along (Codex P1, #140 round 2): a waited-for
      // character handed over under "Add animation…" is refused inside offer,
      // the wait intact. ('document' returned above, and the type knows it.)
      const offered = await documentIo.offer(file, bytes, seq, intent, relinkOf);
      if (offered !== 'not-wanted') return offered !== 'superseded';
      // ROUTE ON CONTENT, not on the extension. A `.glb` that carries
      // animations and NO skin is a clip source, not a broken model — and the
      // clearest example is this app's own animation-only artifact (ADR-016's
      // Artifact B), which deliberately writes no skin. Routing it by
      // extension sent it to the model importer, where convertRig refused with
      // "contains no skinned skeleton": true, unhelpful, and indistinguishable
      // from "the app cannot open its own export".
      const shape = /\.(glb|vrm)$/i.test(file.name) ? glbShape(bytes) : null;
      if (/\.(glb|vrm)$/i.test(file.name) && shape === null) {
        throw new Error(`${file.name} is not a readable binary glTF file`);
      }
      // An FBX is a body when it carries a skin and either the person said so
      // or it has no animation to give — a Mixamo clip downloaded "with skin"
      // stays a clip source on a drop, as it always was.
      const fbx = /\.fbx$/i.test(file.name) ? fbxShape(bytes) : null;
      const isModel =
        fbx !== null
          ? fbx.hasSkin && (intent === 'character' || fbx.animationTakes === 0)
          : shape === null
            ? /\.(glb|vrm)$/i.test(file.name)
            : shape.hasSkins || shape.animationCount === 0;
      // THE PERSON'S CHOICE GOVERNS (Codex P1, #140). A menu entry names what
      // the file is FOR, and a file that is not that is refused — never
      // re-routed: "Add animation…" handed a skinned GLB must not replace the
      // subject and reset a lineage nobody asked to begin.
      if (intent === 'character' && fbx !== null && !fbx.hasSkin) {
        throw new Error(`${file.name} carries no skinned mesh — it is an animation; use Add animation… to put it on the subject`);
      }
      if (intent === 'character' && !isModel) {
        throw new Error(`${file.name} is an animation source, not a character — use Add animation… to put it on the subject`);
      }
      if (intent === 'animation' && isModel) {
        throw new Error(`${file.name} is a skinned character, not an animation — use Open character… to make it the subject`);
      }
      if (shape !== null && !isModel && seq === dropSeq) {
        noteStatus(`${file.name} carries ${shape.animationCount} animation(s) and no skinned skeleton — reading it as a clip source`);
      }
      // Held only once the file is what the gesture said it is — a refused file
      // leaves nothing behind.
      holdUpload(uri, bytes);
      const committed = await (isModel ? importModel(file, uri, seq) : importClip(file, uri, seq));
      // A superseded ordinary import leaves nothing behind either (Codex P2,
      // #140 round 6): the newer gesture may never commit, and no prune would
      // then remove these bytes — identity, so a newer load's bytes under the
      // same name stay.
      if (!committed) releaseUpload(uri, bytes);
      return committed;
    } catch (err) {
      // A refusal is the §8 contract speaking and belongs on the surface. The
      // dropped bytes go too — keeping them would leave a Document resolvable
      // to a file we could not use — and whatever they REPLACED comes back: a
      // refused file under the live subject's own name must not strand the
      // subject on stage with nothing behind it (the smoke's first find on
      // #140; Codex round 7). By identity, so a newer load's bytes under the
      // same name stay.
      if (bytes !== null) releaseUpload(uri, bytes);
      // A load already overtaken keeps its refusal to the console (Codex P2,
      // #140 round 4): the line belongs to the load the person moved on to.
      if (seq === dropSeq) {
        revealStatus();
        noteStatus(`${file.name} could not be imported — ${err instanceof Error ? err.message : String(err)}`);
      }
      // WHAT the file is, beside WHY it refused. A refusal names its own rule,
      // but diagnosing one from a user's console needs the artifact's shape
      // too — and asking someone to re-export while we guess is a poor trade
      // for eight lines that make the next report decisive.
      let shape: unknown = '(not a GLB)';
      try {
        const probeBytes = bytes ?? (await file.arrayBuffer());
        shape = glbShape(probeBytes) ?? '(not a GLB)';
      } catch {
        /* the bytes are already gone; the error below is still the point */
      }
      console.error('[animator-v3] import refused', {
        file: file.name,
        bytes: file.size,
        shape,
        error: err instanceof Error ? { name: err.name, message: err.message } : String(err),
      });
      console.error('[animator-v3] import refused — stack', err);
    }
    return false;
  };

  // The menu — the drop door's SECOND GESTURE (ADR-032 §2.4). It offers five
  // entries and hands a chosen File to the same function a drop reaches; it
  // decides nothing. Mounted into the shell chrome bar the export button
  // already joined, so it can never sit over a workspace zone.
  mountFileMenu(shellChromeBar, {
    // The load order is reserved the moment a choice is FINAL — the dialog
    // confirmed, the handle granted — and before any read (Codex P1, #140).
    reserveLoad: () => ++dropSeq,
    isCurrent: (seq) => seq === dropSeq,
    opened: async (kind, file, handle, seq, relinkOf) => {
      const accepted = await importDropped(file, kind, seq, relinkOf);
      // A handle is kept BESIDE the name (D6) — Recent and relink try it first —
      // but only for a file the door ACCEPTED (Codex P2, #140 round 2): a
      // refused pick remembered under its wrong kind would refuse again from
      // Recent, every time.
      if (accepted && handle !== null) {
        const uri = kind === 'document' ? `document:${file.name}` : `upload:${file.name}`;
        await fileHandles.remember({ uri, name: file.name, kind, at: new Date().toISOString() }, handle);
      }
      return accepted;
    },
    saveDocument,
    grantedHandle: async (uri) => {
      const handle = await fileHandles.recall(uri);
      if (handle === null) return null;
      return (await grantHandle(handle, { mayPrompt: true })) ? handle : null; // a click is behind this one
    },
    recent: () => fileHandles.recent(8),
    state: () => ({
      // The boot character is a subject like any other (D1): there is always one.
      hasSubject: true,
      recent: fileHandles.available()
        ? { available: true }
        : { available: false, reason: 'this browser kept no file handles — nothing can be reopened by name' },
      waiting: documentIo.pending(),
    }),
    cancelWaiting: () => documentIo.cancelPending('relink abandoned — what is on stage stays'),
    note: (line, reveal) => {
      noteStatus(line);
      if (reveal) revealStatus();
    },
  });

  window.addEventListener('dragover', (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  });
  window.addEventListener('drop', (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!/\.(glb|vrm|fbx|json)$/i.test(file.name)) {
      // Named, not ignored: a silently-swallowed drop is indistinguishable from
      // a broken window (§8). The message says what this seam DOES read.
      noteStatus(`${file.name} is not a model, an animation or a Document — drop a .glb, .vrm, .fbx or .harry.json`);
      return;
    }
    // The drop IS the gesture: its sequence is reserved here, before the read.
    void importDropped(file, undefined, ++dropSeq);
  });

  // The base grows; the notice is appended by the ONE renderer, so a notice
  // arriving later in the session lands on this same line rather than being
  // lost to a string composed once at boot.
  statusBase =
    `${bootLine} · "${seed.name}" wears "${playable.name}" ` +
    `(${presented.boundTracks} tracks bound, ${presented.unresolvedTracks.length} unresolved)`;
  renderStatus();
  console.info(`[animator-v3] document ran — ${statusEl?.textContent ?? statusBase}`);
  // ADR-040: World's hand-off, offered once the stage is up. Nothing loads without the person's click.
  // Not awaited: the prompt waits on a click, and a hand-off failure is a status line, never the fatal screen.
  intake
    .boot()
    .catch((err: unknown) => noteStatus(`World's hand-off: ${refusalLine(err)} — drop your file here instead`, err))
    .finally(() => {
      // The hand-off decided without the frame taking over (nothing guided): the hold ends, the editor shows.
      if (!intakeFrame.guided()) endBootHold();
    });
}

main().catch(reportFatal);
